import React, { useState, useEffect, useCallback, useMemo, useRef, useContext, createContext } from 'react';
import { MissingEmbellishmentsBanner } from './MissingEmbellishments';
import { FbmCard, FbmRatePicker } from './FbmCard';
import { useParams, useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { Checkbox, Select, Badge, Modal, Text, BlockStack, Banner } from '@shopify/polaris';
import { useAuth, useAuthFetch, authenticatedFetch } from './AuthComponent';
import { isLabelFirstWarehouse } from './constants/labelFirstWarehouses';
import { resolveOrdersBackTo, ordersBackToLabel } from './ordersBackTo';
import { useStore, STORE_DISPLAY_NAMES } from './StoreContext';
import { OrderPrintCard, MergedNjPrint, isPrinted, getPerLocationPrintTags, buildPopupHTML } from './OrderPrintCard';
import { njMergeAllowed, njWarehouseLocsOnOrder, readMergeNjPref, writeMergeNjPref } from './constants/njWarehouses';
import NumberPreviewThumb, { buildFontFaceCss, getPreviewFor, getPrintedNumberAttr } from './NumberPreviewThumb';
import OrderTimeline, { PORTAL_COLORS, portalColor, eventLabel as portalEventLabel, buildSessions, consolidateAutoEvents, parseGroupKey, sessionDomain, formatGroupKey, isCascadePressAutoPick, isSessionBoundary, isBundleChildEvent, bundleChildSet } from './OrderTimeline';
import SmartProductImage from './SmartProductImage';
import { buildBundles, EMBELLISHMENT_TYPES } from './pressingHelpers';
import { getPreviewImageUrls, getPreviewFrontUrl, shopifyResize } from './previewImage';
import OrderDetailV2 from './order-detail-v2/OrderDetailV2';
import OrderDetailV3 from './order-detail-v3/OrderDetailV3';
import OrderNotificationsBanner from './OrderNotificationsBanner';
import OrderNotificationsControl from './OrderNotificationsControl';
import ReportIssueModal from './ReportIssueModal';
import CommentComposer from './comments/CommentComposer';
import CommentEntry from './comments/CommentEntry';
import { tokenFor } from './comments/mentions';
import RefundModal from './RefundModal';

import { hasRole, allRolesIn } from './constants/roles';
// Per-order SKU-notification map — {sku: message}. Provided by OrderDetailV1
// and consumed by BundleRows + LineItemRow to render the red "notification"
// text next to the SKU. Falls back to {} when not inside a provider.
const SkuNotificationsContext = createContext({});
function useSkuNotifications() { return useContext(SkuNotificationsContext); }
function SkuNotificationCell({ sku }) {
    const map = useSkuNotifications();
    const msg = map[sku];
    if (!msg) return null;
    return (
        <div style={{ marginTop: 4 }}>
            <Badge tone="critical">{msg}</Badge>
        </div>
    );
}

function SkuCellContent({ sku }) {
    return <>{sku || '—'}</>;
}

// Per-order player-number preview map — keyed by product GID → {fontFamily,
// fontUrl, color, ...}. Used by nested components (BundleRows, LineItemRow)
// to swap embellishment thumbs for rendered-number previews without having
// to thread the map through every intermediate render function.
const NumberPreviewsContext = createContext(null);

const VIRTUAL_TITLES = new Set(['free returns + package protection', 'processing fee']);
const isVirtual = (title) => VIRTUAL_TITLES.has((title || '').toLowerCase());

const MISSING_IMAGE_SVG = 'data:image/svg+xml;utf8,' + encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">' +
    '<rect width="200" height="200" fill="#d1d5db"/>' +
    '<text x="100" y="100" font-family="-apple-system,Segoe UI,sans-serif" font-size="22" font-weight="700" fill="#6b7280" text-anchor="middle" dominant-baseline="middle" transform="rotate(-45 100 100)">Missing Image</text>' +
    '</svg>'
);
const handleMissingImage = (e) => {
    if (e.currentTarget.src !== MISSING_IMAGE_SVG) {
        e.currentTarget.onerror = null;
        e.currentTarget.src = MISSING_IMAGE_SVG;
    }
};

// Use the actual price paid on the order, not the current product price
const lineItemPrice = (li) => {
    const discounted = li.discountedUnitPriceSet?.shopMoney?.amount;
    const original   = li.originalUnitPriceSet?.shopMoney?.amount;
    const fallback   = li.variant?.price;
    const amount = discounted ?? original ?? fallback;
    return amount != null ? `$${parseFloat(amount).toFixed(2)}` : '—';
};

const SHOPIFY_ADMIN_HANDLES = {
    soccerpost: 'soccerpostshop',
    surf:       'surf-soccerpost',
    rush:       'rush-soccerpost',
    citysports: 'citysportsnj',
    u90:        'u90soccer',
    b2b:        'soccerpostb2b',
    stefans:    'stefans-soccer',
};

// Build a Shopify admin deep-link for a line item's product (variant-scoped
// when the variant id is present). Returns null when we don't have enough
// info to construct a link (e.g. embellishments without a product ref).
function shopifyAdminUrlForLi(storeKey, li) {
    const handle = SHOPIFY_ADMIN_HANDLES[storeKey];
    if (!handle) return null;
    const productGid = li?.variant?.product?.id || null;
    const variantGid = li?.variant?.id || null;
    const productId = productGid ? productGid.split('/').pop() : null;
    const variantId = variantGid ? variantGid.split('/').pop() : null;
    if (!productId) return null;
    if (variantId) {
        return `https://admin.shopify.com/store/${handle}/products/${productId}/variants/${variantId}`;
    }
    return `https://admin.shopify.com/store/${handle}/products/${productId}`;
}

// Context so BundleRows / LineItemRow can build product links without
// prop-drilling storeKey down every table renderer.
const StoreKeyContext = createContext('soccerpost');

// Wraps `children` in an <a> to Shopify admin when the line item has a
// resolvable product/variant. Reads storeKey from StoreKeyContext so
// callers don't have to prop-drill it. No-op when:
//   • there's no link (embellishment, custom line item, etc.)
//   • the user's role is 'franchise' — franchises don't have Shopify
//     admin access on our stores; showing them the link would only
//     lead to a Shopify permission error.
function ProductLink({ li, children, style }) {
    const storeKey = useContext(StoreKeyContext);
    const { user } = useAuth();
    if (allRolesIn(user, ['franchise'])) return children;
    const href = shopifyAdminUrlForLi(storeKey, li);
    if (!href) return children;
    return (
        <a href={href} target="_blank" rel="noopener noreferrer"
           title="Open in Shopify admin"
           style={{ color: 'inherit', textDecoration: 'none', borderBottom: '1px dotted #6d7175', ...style }}>
            {children}
        </a>
    );
}

const CANCEL_REASON_LABELS = {
    CUSTOMER:  'Customer changed/canceled order',
    FRAUD:     'Fraudulent order',
    INVENTORY: 'Items unavailable',
    DECLINED:  'Payment declined',
    STAFF:     'Staff',
    OTHER:     'Other reason',
};

const DELIVERY_LABELS = {
    LOCAL_PICKUP: 'Pickup in store',
    LOCAL_DELIVERY: 'Local Delivery',
    NONE: 'Shipping not required',
    SHIPPING: 'Shipping',
    PICK_UP: 'Pickup in store',
    RETAIL: 'Pickup in store',
};

// Pickup-store color badge map, populated from sessionStorage (set by Orders.jsx
// on first visit — see ops_store_colors_v1 key). Shared short-name → color lookup
// so FO cards can show the same colored store badge as the Orders list.
function getCachedStoreColors() {
    try {
        const raw = sessionStorage.getItem('ops_store_colors_v1');
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
}
function stripSoccerPostPrefix(name) {
    if (!name) return name;
    const s = String(name);
    return s.toLowerCase().startsWith('soccer post ') ? s.slice('Soccer Post '.length) : s;
}
const FALLBACK_STORE_PALETTE = [
    ['#EF4444','#FFFFFF'],['#991B1B','#FFFFFF'],['#FCA5A5','#000000'],['#BE123C','#FFFFFF'],
    ['#FDA4AF','#000000'],['#F97316','#000000'],['#C2410C','#FFFFFF'],['#FDBA74','#000000'],
    ['#EA580C','#FFFFFF'],['#9A3412','#FFFFFF'],['#EAB308','#000000'],['#854D0E','#FFFFFF'],
    ['#CA8A04','#FFFFFF'],['#FDE047','#000000'],['#713F12','#FFFFFF'],['#22C55E','#000000'],
    ['#166534','#FFFFFF'],['#86EFAC','#000000'],['#10B981','#FFFFFF'],['#047857','#FFFFFF'],
    ['#065F46','#FFFFFF'],['#3B82F6','#FFFFFF'],['#1E3A8A','#FFFFFF'],['#93C5FD','#000000'],
    ['#0EA5E9','#000000'],['#0369A1','#FFFFFF'],['#0284C7','#FFFFFF'],['#A855F7','#FFFFFF'],
    ['#581C87','#FFFFFF'],['#D8B4FE','#000000'],['#8B5CF6','#FFFFFF'],['#4C1D95','#FFFFFF'],
    ['#6D28D9','#FFFFFF'],['#EC4899','#FFFFFF'],['#831843','#FFFFFF'],['#F9A8D4','#000000'],
    ['#D946EF','#FFFFFF'],['#701A75','#FFFFFF'],['#64748B','#FFFFFF'],['#334155','#FFFFFF'],
];
function storeColorFor(shortName) {
    if (!shortName) return null;
    const map = getCachedStoreColors();
    if (map && map[shortName]) return map[shortName];
    // djb2 fallback if session cache missing (first visit landed on OrderDetail before Orders)
    let h = 5381;
    for (let i = 0; i < shortName.length; i++) h = ((h << 5) + h + shortName.charCodeAt(i)) >>> 0;
    const [bg, txt] = FALLBACK_STORE_PALETTE[h % FALLBACK_STORE_PALETTE.length];
    return { background: bg, text: txt };
}
function StoreBadge({ name }) {
    const short = stripSoccerPostPrefix(name);
    if (!short) return null;
    const color = storeColorFor(short);
    return (
        <span style={{
            display: 'inline-block', padding: '2px 8px', borderRadius: 10,
            fontSize: 12, fontWeight: 600,
            backgroundColor: color.background, color: color.text,
            whiteSpace: 'nowrap',
        }}>{short}</span>
    );
}

// ── Small presentational helpers ─────────────────────────────────────────────

function Card({ title, children, noPad }) {
    return (
        <div className="card">
            {title && <div className="card__header">{title}</div>}
            <div className={noPad ? undefined : 'card__body'}>{children}</div>
        </div>
    );
}

function Tag({ label, text }) {
    const value = label ?? text;
    return <span className="tag tag--default">{value}</span>;
}

function foStatusClass(status) {
    return 'badge badge--fo-' + (status || 'incomplete').toLowerCase().replace(/_/g, '-');
}

function FulfillmentBadge({ status }) {
    return <span className={foStatusClass(status)}>{status || 'Incomplete'}</span>;
}

function JumbleBadge({ color }) {
    return (
        <span className={`jumble-badge jumble-${color}`}>
            ⚠ Jumbled Bundle
        </span>
    );
}

function RemovedBadge() {
    return (
        <span style={{
            display: 'inline-block',
            marginLeft: 6,
            padding: '1px 6px',
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: 0.4,
            color: '#991b1b',
            backgroundColor: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: 3,
            verticalAlign: 'middle',
            textTransform: 'uppercase',
        }} title="Removed via Shopify Order Editing or refunded with restock">
            Removed
        </span>
    );
}

const REMOVED_ROW_STYLE = { textDecoration: 'line-through', opacity: 0.55 };

function isPickupMethod(methodType) {
    const m = (methodType || '').toUpperCase();
    return m === 'LOCAL_PICKUP' || m === 'PICK_UP' || m === 'RETAIL';
}

// ── Custom attribute helpers ──────────────────────────────────────────────────

function getAttr(li, key) {
    const a = (li.customAttributes || []).find(x => x.key === key);
    return a?.value ?? null;
}

function getPlayerName(item) {
    const attrs = [...(item.lineItemGroup?.customAttributes || []), ...(item.customAttributes || [])];
    return attrs.find(a => {
        const k = a.key.toLowerCase();
        return k === 'player name' || k === 'player' || k.includes('name');
    })?.value || null;
}

function getPlayerNumber(item) {
    const attrs = [...(item.lineItemGroup?.customAttributes || []), ...(item.customAttributes || [])];
    return attrs.find(a => {
        const k = a.key.toLowerCase();
        return k === 'number' || k === '#';
    })?.value || null;
}

// Picking portal splits bundles by player name ("!Wiley Gohman") or number ("#5")
// so each physical unit gets its own scan row. When OrderDetail looks up portal
// state by Shopify SKU, we need to aggregate any split-key rows back to the base
// SKU so picked/pressed counts line up with the on-screen line item.
//
// 2026-08-02 Stage 2: when `li` is provided and portalState._byLineItem has
// data for it, RETURN that data directly instead of SKU aggregation. This
// stops phantom FOs from inheriting "picked" from a sibling FO's real picks
// on the same SKU. Falls back to SKU aggregation for LIs that lack per-LI
// events (pre-Stage-1 orders).
function psForSku(portalState, sku, li = null) {
    if (!portalState || !sku) return null;
    // Per-LI preferred path (Stage 2 attribution).
    if (li) {
        const liNum = String(li.id || '').split('/').pop();
        const perLi = liNum && portalState?._byLineItem?.[liNum];
        if (perLi) {
            const exact = portalState[sku];
            return {
                qty_total:   li.quantity || 1,
                qty_picked:  perLi.qty_picked || 0,
                // press + pack per-LI arrive in Stage 3 — until then fall
                // through to the exact-SKU row for these two verbs so we
                // don't regress existing display.
                qty_pressed: exact?.qty_pressed || 0,
                qty_packed:  exact?.qty_packed  || 0,
                pack_status:       exact?.pack_status,
                pack_instances:    exact?.pack_instances,
                pack_fail_reason:  exact?.pack_fail_reason,
                pack_fail_action:  exact?.pack_fail_action,
                title:             exact?.title,
            };
        }
    }
    // Legacy SKU-aggregation fallback (unchanged).
    let matches = [];
    if (portalState[sku]) matches.push(portalState[sku]);
    for (const [k, v] of Object.entries(portalState)) {
        if (!v || k === sku) continue;
        // Picking portal builds split keys as `<sku>#<number>`, `<sku>|<label>`,
        // or `<sku>!<name>`. We must check ALL three suffixes — leaving any out
        // makes that split form's qty_picked invisible at order-detail level
        // (and the line item shows "0 picked" even though the picker scanned it).
        if (k.startsWith(sku + '!') || k.startsWith(sku + '#') || k.startsWith(sku + '|')) matches.push(v);
    }
    if (matches.length === 0) return null;
    const agg = { qty_total: 0, qty_picked: 0, qty_pressed: 0, qty_packed: 0 };
    for (const m of matches) {
        agg.qty_total   += m.qty_total   || 0;
        agg.qty_picked  += m.qty_picked  || 0;
        agg.qty_pressed += m.qty_pressed || 0;
        agg.qty_packed  += m.qty_packed  || 0;
    }
    const exact = portalState[sku];
    if (exact) {
        agg.pack_status       = exact.pack_status;
        agg.pack_instances    = exact.pack_instances;
        agg.pack_fail_reason  = exact.pack_fail_reason;
        agg.pack_fail_action  = exact.pack_fail_action;
        agg.title             = exact.title;
    }
    return agg;
}

// Thin wrapper around the canonical `buildBundles` helper (shared with Pressing,
// Packing, and OrderPrintCard). Re-shapes the output into the { item, children }
// form that this component uses for rendering. Pass order tags so the helper
// can activate the "Broken Order" bundle-reconstruction pass.
function groupLineItems(lineItems, tags) {
    return buildBundles(lineItems, { tags }).map(b => ({
        item: b.parent,
        children: b.children,
    }));
}

const HIDDEN_ATTR_KEYS = new Set(['preview front', 'preview back', 'preview', 'just_preview', 'parent product']);

function LineItemAttrs({ li }) {
    const attrs = (li.customAttributes || []).filter(a =>
        !a.key.startsWith('_') && !HIDDEN_ATTR_KEYS.has(a.key.toLowerCase())
    );
    if (!attrs.length) return null;
    return (
        <div className="li-attr-list">
            {attrs.map(a => (
                <div key={a.key} className="li-attr-row">
                    <span className="li-attr-key">{a.key}:</span> {a.value}
                </div>
            ))}
        </div>
    );
}

// ── Bundle rows ───────────────────────────────────────────────────────────────
// Renders a bundle as multiple <tr> elements:
//   - Preview images in col 0, rowSpan=N (spans all sub-item rows)
//   - Each sub-item gets its own row: [img+name] | SKU | Variant
//   - Qty and Price on the first sub-item row only
//   - Optional player footer row spanning all columns

// Team Portal requirement badge. Renders next to a line-item title so
// pickers/packers can see whether the item is REQUIRED / DEFAULT /
// RECOMMENDED / DISABLED for this player's spec (per tp_requirements block).
// Silent (returns null) for non-TP orders, embellishment children, and
// standalone items outside the team's catalog — i.e. no yellow "NOT-IN-CATALOG"
// noise on custom add-ons (per user directive).
function TpReqBadge({ entry }) {
    if (!entry) return null;
    switch (entry.status) {
        case 'required':
            return <Badge tone="critical">Required</Badge>;
        case 'default':
            return <Badge tone="info">Default</Badge>;
        case 'recommended':
            return <Badge tone="success">Recommended</Badge>;
        case 'disabled':
            // In-catalog but disabled for this spec (rare — someone bought a
            // Regular product while their spec was set to Goalkeeper, etc).
            // Attention-yellow so it's noticed but not alarm-red.
            return <Badge tone="attention">Off-Spec</Badge>;
        default:
            // 'not_in_catalog' + anything unknown — skip. Custom items,
            // embellishments, and mystery LIs render bare.
            return null;
    }
}

function BundleRows({ item, children, priceOf, rowIndex = 0, splitBundleMap = {}, portalState = {}, selectMode = null, selectedKeys = null, onToggleKey = null, skuInstanceIndex = 1, skuInstanceOffset = null, childSkuInstanceIndices = [], childSkuInstanceOffsets = [], lineItemFlags = null, isBrokenOrder = false, tpOverridesByName = null, tpReqByLiId = null }) {
    // Pull the number-preview map from context — set up at the OrderDetail
    // root. Null-safe when the provider hasn't mounted yet.
    const npCtx = useContext(NumberPreviewsContext) || {};
    // Collect visible attrs from the parent item
    const playerAttrs = (item.customAttributes || []).filter(a => !a.key.startsWith('_') && !HIDDEN_ATTR_KEYS.has(a.key.toLowerCase()));
    const playerName = playerAttrs.find(a => {
        const k = a.key.toLowerCase();
        return k === 'player name' || k === 'player' || k.includes('name');
    })?.value;
    // Player number and pressed name may live on a child embellishment item (optional add-ons
    // linked via _soccer_parent_line_item_key rather than the parent's own attrs).
    const allBundleAttrs = [
        ...playerAttrs,
        ...children.flatMap(c => (c.customAttributes || []).filter(a => !a.key.startsWith('_') && !HIDDEN_ATTR_KEYS.has(a.key.toLowerCase()))),
    ];
    const playerNumber = allBundleAttrs.find(a => {
        const k = a.key.toLowerCase();
        return k === 'number' || k === '#';
    })?.value;
    const pressedName = allBundleAttrs.find(a => a.key.toLowerCase() === 'name')?.value;
    const otherAttrs = playerAttrs.filter(a => {
        const k = a.key.toLowerCase();
        return !k.includes('name') && k !== 'player' && k !== 'number' && k !== '#';
    });

    const allAttrsForPreview = [
        ...(item.lineItemGroup?.customAttributes || []),
        ...(item.customAttributes || []),
        // Standalone-tee case: the customized preview can live on the line
        // item itself (no bundle / no group) — include children just so the
        // helper covers PPLR markers attached to either the parent or a child.
        ...(children || []).flatMap(c => c?.customAttributes || []),
    ];
    const { front: previewFront, back: previewBack } = getPreviewImageUrls(allAttrsForPreview);
    const hasPreview   = previewFront || previewBack;

    const allItems = [item, ...children];
    const N = allItems.length;
    const hasFooter = !!(playerName || playerNumber || pressedName || otherAttrs.length > 0);

    const border = '1px solid #d1d5db';
    const divider = '1px solid #e5e7eb';
    const bg = ROW_COLORS[rowIndex % 2];

    const isPick  = selectMode === 'pick' || selectMode === 'unpick';
    const isPress = selectMode === 'press' || selectMode === 'unpress';
    const bundleGroupKey = item.sku || `__no_sku__${item.id}`;

    return (
        <React.Fragment>
            {allItems.map((li, i) => {
                const isFirst = i === 0;
                const isLast  = i === N - 1;
                const variant = li.variant?.title && li.variant.title !== 'Default Title' ? li.variant.title : '';
                const bottomBorder = isLast && !hasFooter ? border : divider;
                // Use this item's own SKU instance index: parent uses skuInstanceIndex, each child uses its own
                const liSkuInstanceIndex = i === 0 ? skuInstanceIndex : (childSkuInstanceIndices[i - 1] || 1);
                // Proper running-sum offset from renderGroupedItems (handles varying
                // qty per parent line). Used by the child badge below so a
                // shared-SKU child of a 5-qty parent reads its 0/5 slice
                // correctly even when later parents of the same SKU have qty 15/45.
                const liSkuInstanceOffset = i === 0 ? skuInstanceOffset : (childSkuInstanceOffsets[i - 1] ?? null);
                const liGroupKey = li.sku || `__no_sku__${li.id}`;
                const liRemoved = (li.quantity || 0) === 0;
                const liRemovedStyle = liRemoved ? REMOVED_ROW_STYLE : undefined;
                const rowBg = bg;
                const liNumPreview = getPreviewFor(li, npCtx);
                const liNum = getPrintedNumberAttr(li.customAttributes)
                    || getPrintedNumberAttr(item.customAttributes)
                    || liNumPreview?.defaultValue
                    || '';
                const liHasNumber = !!(liNumPreview && liNum);

                return (
                    <tr key={li.id || i}>
                        {/* Checkbox column — pick: one per sub-item; press: one per bundle (first row, rowSpan) */}
                        {selectMode && isPick && (
                            <td style={{ width: 28, textAlign: 'center', verticalAlign: 'middle', backgroundColor: rowBg, borderTop: isFirst ? border : divider, borderBottom: bottomBorder, padding: '0 4px' }}>
                                <input type="checkbox" className="fo-select-check" checked={selectedKeys?.has(li.id) ?? false} onChange={() => onToggleKey?.(li.id)} />
                            </td>
                        )}
                        {selectMode && isPress && isFirst && (
                            <td rowSpan={N} style={{ width: 28, textAlign: 'center', verticalAlign: 'middle', backgroundColor: bg, borderTop: border, borderBottom: !hasFooter ? border : 'none', padding: '0 4px' }}>
                                <input type="checkbox" className="fo-select-check" checked={selectedKeys?.has(bundleGroupKey) ?? false} onChange={() => onToggleKey?.(bundleGroupKey)} />
                            </td>
                        )}
                        {/* Preview images — first row only, rowSpan covers all sub-item rows */}
                        {isFirst && (
                            <td rowSpan={N} style={{
                                padding: 6,
                                verticalAlign: 'middle',
                                backgroundColor: bg,
                                borderTop: border,
                                borderLeft: border,
                                borderBottom: !hasFooter ? border : 'none',
                                width: 1,
                                position: 'relative',
                            }}>
                                {/* TP requirement badge overlay — top-left corner
                                    of the bundle preview cell, sitting on top of
                                    the product image so pickers see status at a
                                    glance without scanning the item title. */}
                                {tpReqByLiId?.[item.id] && (
                                    <div style={{
                                        position: 'absolute',
                                        top: 4,
                                        left: 4,
                                        zIndex: 2,
                                        pointerEvents: 'none',
                                    }}>
                                        <TpReqBadge entry={tpReqByLiId[item.id]} />
                                    </div>
                                )}
                                {(() => {
                                    const sku = item.sku || '';
                                    const groupKey = sku || `__no_sku__${item.id}`;
                                    const ps = psForSku(portalState, groupKey, item) || portalState[groupKey];
                                    const qtyPicked  = ps?.qty_picked  ?? null;
                                    const qtyTotal   = ps?.qty_total   ?? null;
                                    const qtyPressed = ps?.qty_pressed ?? null;
                                    // Per-bundle display: show counts for this specific instance only.
                                    const bundleQty = item.quantity || 1;
                                    const instanceOffset = skuInstanceOffset !== null ? skuInstanceOffset : (skuInstanceIndex - 1) * bundleQty;
                                    const instancePicked  = qtyPicked  !== null ? Math.min(Math.max(0, qtyPicked  - instanceOffset), bundleQty) : null;
                                    const instancePressed = qtyPressed !== null ? Math.min(Math.max(0, qtyPressed - instanceOffset), bundleQty) : null;
                                    const anyPickingDone = Object.values(portalState).some(s => (s.qty_picked || 0) > 0);
                                    const effectiveTotal = (!ps || (qtyTotal === 0)) ? bundleQty : bundleQty;
                                    // BE flag wins for parent bundle row (2026-05-23) — but
                                    // refine to partial when portalState shows only partial
                                    // coverage for this instance (mirrors LineItemRow fix).
                                    const beFlag = lineItemFlags?.[item.id];
                                    let showFull, showPartial, showUnpicked;
                                    if (beFlag) {
                                        if (beFlag.picked === true) {
                                            const refinePartial = ps && instancePicked !== null && effectiveTotal > 0
                                                && instancePicked > 0 && instancePicked < effectiveTotal;
                                            showFull = !refinePartial;
                                            showPartial = refinePartial;
                                            showUnpicked = false;
                                        } else {
                                            showFull = false;
                                            showPartial = false;
                                            showUnpicked = true;
                                        }
                                    } else {
                                        showFull    = anyPickingDone && instancePicked  !== null && effectiveTotal > 0 && instancePicked  >= effectiveTotal;
                                        showPartial = anyPickingDone && instancePicked  !== null && effectiveTotal > 0 && instancePicked  > 0 && instancePicked < effectiveTotal;
                                        showUnpicked = anyPickingDone && !showFull && !showPartial && (instancePicked === 0 || instancePicked === null);
                                    }
                                    const instanceTotal = effectiveTotal;
                                    // Press badge mirrors the picked badge below: show the
                                    // ratio in full + partial states (X/X Pressed), nothing
                                    // when nothing's been pressed yet for this bundle. Was
                                    // previously showing a bare "Pressed" with no ratio,
                                    // which the user can't act on at a glance.
                                    const anyPressingDone = Object.values(portalState).some(s => (s.qty_pressed || 0) > 0);
                                    let showPressFull, showPressPartial;
                                    if (beFlag) {
                                        showPressFull    = beFlag.pressed === true;
                                        showPressPartial = false;
                                    } else {
                                        showPressFull    = anyPressingDone && instancePressed !== null && instanceTotal > 0 && instancePressed >= instanceTotal;
                                        showPressPartial = anyPressingDone && instancePressed !== null && instanceTotal > 0 && instancePressed > 0 && instancePressed < instanceTotal;
                                    }
                                    return (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                            <div className="bundle-preview-wrap">
                                                {hasPreview ? (
                                                    <>
                                                        {previewFront && (
                                                            <div
                                                                className={`bundle-preview-img-box ${previewBack ? 'bundle-preview-img-box--front' : 'bundle-preview-img-box--front-only'}`}
                                                                style={{ borderRight: previewBack ? divider : 'none' }}
                                                            >
                                                                <img src={shopifyResize(previewFront, 600)} alt="Front" className="bundle-preview-img" onError={handleMissingImage} />
                                                                <div className="bundle-preview-label">Front</div>
                                                            </div>
                                                        )}
                                                        {previewBack && (
                                                            <div className={`bundle-preview-img-box ${previewFront ? 'bundle-preview-img-box--back' : 'bundle-preview-img-box--back-only'}`}>
                                                                <img src={shopifyResize(previewBack, 600)} alt="Back" className="bundle-preview-img" onError={handleMissingImage} />
                                                                <div className="bundle-preview-label">Back</div>
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <div className="bundle-no-preview">
                                                        {item.image?.url
                                                            ? <SmartProductImage src={item.image.url} alt="" transparent />
                                                            : <span className="icon-box-lg">📦</span>}
                                                    </div>
                                                )}
                                            </div>
                                            {showPressFull && (
                                                <div className="od-pick-badge od-pick-badge--pressed" style={{ position: 'static', transform: 'none' }}>{instancePressed}/{instanceTotal} Pressed</div>
                                            )}
                                            {showPressPartial && (
                                                <div className="od-pick-badge od-pick-badge--partial" style={{ position: 'static', transform: 'none' }}>{instancePressed}/{instanceTotal} Pressed</div>
                                            )}
                                            {(() => {
                                                const entry = (ps?.pack_instances || {})[String(skuInstanceIndex)];
                                                if (entry && entry.status === 'failed' && (entry.fail_action || '').toLowerCase() === 'rework') {
                                                    return (
                                                        <div className="od-pick-badge od-pick-badge--rework"
                                                             style={{ position: 'static', transform: 'none' }}
                                                             title={entry.fail_reason || 'Rework'}>
                                                            Rework{entry.fail_reason ? `: ${entry.fail_reason}` : ''}
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()}
                                        </div>
                                    );
                                })()}
                            </td>
                        )}

                        {/* Sub-item: thumbnail + name */}
                        <td className="td-bundle" style={{ backgroundColor: rowBg, borderTop: isFirst ? border : divider, borderBottom: bottomBorder }}>
                            <div className="bundle-item-row">
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                                    <div className="bundle-item-thumb">
                                        {(() => {
                                            // Swap to NumberPreviewThumb when this child product has a
                                            // soccer.player_number_preview metaobject AND we have a
                                            // Number to render.
                                            if (liHasNumber) {
                                                return <NumberPreviewThumb preview={liNumPreview} number={liNum} size={40} borderColor="#e5e7eb" />;
                                            }
                                            // Bundle CHILD thumbnail (Logo / Number / etc.) — always
                                            // show the child's own product image. Do NOT pull the
                                            // `Preview Front` custom attribute even when it's
                                            // present. Historical edited orders left preview attrs
                                            // only on the parent garment, so this fell through to
                                            // li.image?.url and looked fine. Order-edit tooling
                                            // added the last week of July 2026 propagates the
                                            // parent's preview URL onto every child LI too, which
                                            // made bundle children render the customized garment
                                            // preview instead of the actual logo/number. Skipping
                                            // the preview lookup fixes both eras uniformly.
                                            //
                                            // (Parent-hero preview at the top of the bundle group
                                            // still uses the merged customAttributes — see
                                            // `previewFront`/`previewBack` above at line ~408.)
                                            //
                                            const imgSrc = li.image?.url;
                                            return imgSrc
                                                ? <SmartProductImage src={imgSrc} alt="" />
                                                : <span className="icon-box-sm">📦</span>;
                                        })()}
                                    </div>
                                    {(() => {
                                        // Broken Order: same child LI is reused across N
                                        // parents (Pass 1e), so each parent's badge needs to
                                        // show 1/1 (or 0/1), not the SKU-wide total.
                                        const childBundleQty = isBrokenOrder ? 1 : (li.quantity || 1);
                                        // BE flag wins for child line item (2026-05-23)
                                        const childBeFlag = lineItemFlags?.[li.id];
                                        if (childBeFlag) {
                                            if (childBeFlag.picked) {
                                                const _q = childBundleQty;
                                                return <div className="od-pick-badge od-pick-badge--full" style={{ position: 'static', transform: 'none' }}>{_q}/{_q} Picked</div>;
                                            }
                                            return <div className="od-pick-badge od-pick-badge--unpicked" style={{ position: 'static', transform: 'none' }}>UNPICKED</div>;
                                        }
                                        const gk = li.sku || `__no_sku__${li.id}`;
                                        const psi = psForSku(portalState, gk, li) || portalState[gk];
                                        const qp = psi?.qty_picked ?? null;
                                        const anyPickingDone = Object.values(portalState).some(s => (s.qty_picked || 0) > 0);
                                        if (!anyPickingDone) return null;
                                        if (!psi) {
                                            return <div className="od-pick-badge od-pick-badge--unpicked" style={{ position: 'static', transform: 'none' }}>UNPICKED</div>;
                                        }
                                        // Use the renderGroupedItems-computed running-sum
                                        // offset when present (handles per-parent variable
                                        // qty). Fall back to the naive formula only if
                                        // somehow not passed in.
                                        const childOffset = liSkuInstanceOffset !== null
                                            ? liSkuInstanceOffset
                                            : (liSkuInstanceIndex - 1) * childBundleQty;
                                        // 2026-05-19: removed the parent-clamp on child pick
                                        // badges. The event log is authoritative — a logo
                                        // that was scanned shows "picked" even if its parent
                                        // garment hasn't been scanned yet. Keeps row badges
                                        // consistent with the hero/FO header counts.
                                        const ip = Math.min(Math.max(0, (qp ?? 0) - childOffset), childBundleQty);
                                        const it = childBundleQty;
                                        if (ip >= it) return <div className="od-pick-badge od-pick-badge--full"     style={{ position: 'static', transform: 'none' }}>{ip}/{it} Picked</div>;
                                        if (ip > 0)   return <div className="od-pick-badge od-pick-badge--partial"  style={{ position: 'static', transform: 'none' }}>{ip}/{it} Picked</div>;
                                        return <div className="od-pick-badge od-pick-badge--unpicked" style={{ position: 'static', transform: 'none' }}>UNPICKED</div>;
                                    })()}
                                </div>
                                <ProductLink li={li}>
                                    <span className="bundle-item-name" style={liRemovedStyle}>{li.title}</span>
                                </ProductLink>
                                {liHasNumber && (
                                    <span className="bundle-item-num-badge" title={`Number to pick: ${liNum}`}>#{liNum}</span>
                                )}
                                {liRemoved && <RemovedBadge />}
                                {(li.customAttributes || []).some(a => a.key === '_soccer_club_product_distribution' && a.value === 'optional') && (
                                    <Badge tone="attention">Optional</Badge>
                                )}
                                {(splitBundleMap[li.lineItemGroup?.id] ?? splitBundleMap[li.id]) != null && (
                                    <JumbleBadge color={splitBundleMap[li.lineItemGroup?.id] ?? splitBundleMap[li.id]} />
                                )}
                            </div>
                        </td>

                        {/* SKU */}
                        <td className="td-bundle--sku" style={{ backgroundColor: rowBg, borderTop: isFirst ? border : divider, borderBottom: bottomBorder, ...liRemovedStyle }}>
                            <SkuCellContent sku={li.sku} />
                            <SkuNotificationCell sku={li.sku} />
                        </td>

                        {/* Variant */}
                        <td className="td-bundle--muted" style={{ backgroundColor: rowBg, borderTop: isFirst ? border : divider, borderBottom: bottomBorder, ...liRemovedStyle }}>
                            {variant}
                        </td>

                        {/* Qty — first row, or own row if optional. Removed lines display
                            the original quantity (struck through) so it's clear what was
                            in the cart before the edit. */}
                        <td className="td-bundle--right" style={{ backgroundColor: rowBg, borderTop: isFirst ? border : divider, borderBottom: bottomBorder, ...liRemovedStyle }}>
                            {isFirst
                                ? (liRemoved ? (item.originalQuantity || 0) : item.quantity)
                                : ((li.customAttributes || []).some(a => a.key === '_soccer_club_product_distribution' && a.value === 'optional')
                                    ? (liRemoved ? (li.originalQuantity || 0) : li.quantity)
                                    : '')}
                        </td>

                        {/* Price — first row, or own row if optional */}
                        <td className="td-bundle--price" style={{ backgroundColor: rowBg, borderTop: isFirst ? border : divider, borderBottom: bottomBorder, borderRight: border, ...liRemovedStyle }}>
                            {isFirst ? priceOf(item) : (
                                (li.customAttributes || []).some(a => a.key === '_soccer_club_product_distribution' && a.value === 'optional')
                                    ? priceOf(li) : ''
                            )}
                        </td>
                    </tr>
                );
            })}

            {/* Player info footer */}
            {hasFooter && (
                <tr>
                    <td colSpan={selectMode ? 7 : 6} className="player-footer-td" style={{ backgroundColor: bg, borderLeft: border, borderRight: border, borderBottom: border }}>
                        <div className="player-footer-wrap">
                            {playerName && <span className="player-footer-name">{playerName}</span>}
                            {pressedName && <span className="player-footer-pressed-name">{pressedName}</span>}
                            {(() => {
                                const ov = playerName && tpOverridesByName && tpOverridesByName[playerName];
                                if (ov) {
                                    return (
                                        <>
                                            {playerNumber && (
                                                <span
                                                    className="player-footer-number"
                                                    style={{ textDecoration: 'line-through', opacity: 0.55 }}
                                                    title="Original number from order"
                                                >#{playerNumber}</span>
                                            )}
                                            <span
                                                className="player-footer-number"
                                                style={{ color: '#c1281c', fontWeight: 700 }}
                                                title={`Team Portal number override (updated ${ov.tp_updated_at || ''})`}
                                            >#{ov.override_number}</span>
                                        </>
                                    );
                                }
                                return playerNumber
                                    ? <span className="player-footer-number">#{playerNumber}</span>
                                    : null;
                            })()}
                            {otherAttrs.map(a => (
                                <span key={a.key} className="player-footer-attr">
                                    <span className="player-footer-attr-key">{a.key}:</span> {a.value}
                                </span>
                            ))}
                        </div>
                    </td>
                </tr>
            )}
        </React.Fragment>
    );
}

const ROW_COLORS = ['#f9fafb', '#eff6ff']; // alternating light grey / light blue

function LineItemRow({ li, isChild, rowIndex = 0, splitBundleMap = {}, portalState = {}, selectMode = null, selectedKeys = null, onToggleKey = null, skuInstanceIndex = 1, skuInstanceOffset = null, lineItemFlags = null, tpReqByLiId = null }) {
    const bgBase = ROW_COLORS[rowIndex % 2];
    const bg = bgBase;
    const imgSize = isChild ? 36 : 75;
    const removed = (li.quantity || 0) === 0;
    const removedStyle = removed ? REMOVED_ROW_STYLE : undefined;
    const groupKey = li.sku || `__no_sku__${li.id}`;
    const ps = psForSku(portalState, groupKey, li) || portalState[groupKey];
    const qtyPicked = ps?.qty_picked ?? null;
    const qtyTotal  = ps?.qty_total  ?? null;
    const bundleQty = li.quantity || 1;
    const instanceOffset = skuInstanceOffset !== null ? skuInstanceOffset : (skuInstanceIndex - 1) * bundleQty;
    const instancePicked = qtyPicked !== null ? Math.min(Math.max(0, qtyPicked - instanceOffset), bundleQty) : null;
    const instanceTotal  = qtyTotal  !== null ? bundleQty : null;
    const anyPickingDone = Object.values(portalState).some(s => (s.qty_picked || 0) > 0);
    const effectiveTotal = (!ps || qtyTotal === 0) ? bundleQty : instanceTotal;
    // 2026-05-23: BE-supplied per-line-item flag wins over portalState-derived
    // value. For multi-FO partial orders, portalState carries cascade-lifted
    // qty_picked from prior flows; the BE flag uses real events + shipped-
    // claim subtraction so each line item reflects its own FO's reality.
    const beFlag = lineItemFlags?.[li.id];
    let showFull, showPartial, showUnpicked;
    if (beFlag) {
        // BE flag is binary (picked / not). When it says picked but portalState
        // shows only partial coverage for this instance, downgrade to partial so
        // the badge matches reality (e.g. "1/2 Picked" not "2/2 Picked"). Falls
        // back to full when portalState has no per-instance data (shipped FO).
        if (beFlag.picked === true) {
            const refinePartial = ps && instancePicked !== null && effectiveTotal > 0
                && instancePicked > 0 && instancePicked < effectiveTotal;
            showFull = !refinePartial;
            showPartial = refinePartial;
            showUnpicked = false;
        } else {
            showFull = false;
            showPartial = false;
            showUnpicked = true;
        }
    } else {
        showFull    = anyPickingDone && ps && instancePicked !== null && effectiveTotal > 0 && instancePicked >= effectiveTotal;
        showPartial = anyPickingDone && ps && instancePicked !== null && effectiveTotal > 0 && instancePicked > 0 && instancePicked < effectiveTotal;
        showUnpicked = anyPickingDone && !showFull && !showPartial && (instancePicked === 0 || instancePicked === null);
    }
    return (
        <tr className="tr-inner">
            {(selectMode === 'pick' || selectMode === 'unpick') && (
                <td style={{ width: 28, textAlign: 'center', verticalAlign: 'middle', backgroundColor: bg, padding: '0 4px' }}>
                    <input type="checkbox" className="fo-select-check" checked={selectedKeys?.has(li.id) ?? false} onChange={() => onToggleKey?.(li.id)} />
                </td>
            )}
            {(selectMode === 'press' || selectMode === 'unpress') && (
                <td style={{ width: 28, backgroundColor: bg, padding: '0 4px' }} />
            )}
            <td className={isChild ? 'td-li--img--child' : 'td-li--img'} style={{ backgroundColor: bg, position: 'relative' }}>
                {/* TP requirement badge — top-left overlay on the product image.
                    Hidden for children (which are always embellishments here). */}
                {!isChild && tpReqByLiId?.[li.id] && (
                    <div style={{
                        position: 'absolute',
                        top: 4,
                        left: 4,
                        zIndex: 2,
                        pointerEvents: 'none',
                    }}>
                        <TpReqBadge entry={tpReqByLiId[li.id]} />
                    </div>
                )}
                <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                    {(() => {
                        const previewAttr = (li.customAttributes || []).find(a => a.key.toLowerCase() === 'preview' || a.key.toLowerCase() === 'preview front');
                        const previewUrl = previewAttr?.value;
                        const imgUrl = previewUrl || li.image?.url;
                        // An embellishment (name / number lettering) whose parent sits in another
                        // location group renders as a top-level row here; it still needs the
                        // light-product backdrop analysis or white lettering on a transparent PNG
                        // disappears against the white row (SP order 7223679221938, Tinton Falls).
                        const isEmbellishmentRow = isChild || (li.customAttributes || []).some(a => (a.key || '').toLowerCase() === 'parent product');
                        return imgUrl
                            ? <SmartProductImage src={imgUrl} transparent={!isEmbellishmentRow} style={{ width: imgSize, height: imgSize, objectFit: isEmbellishmentRow ? 'contain' : 'cover', borderRadius: 4, border: isChild ? '1px solid #e5e7eb' : 'none', opacity: isChild ? 0.75 : 1, display: 'block' }} />
                            : <div style={{ width: imgSize, height: imgSize, backgroundColor: '#f3f4f6', borderRadius: 4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: isChild ? 14 : 18 }}>📦</div>;
                    })()}
                    {/* When BE flag wins (showFull driven by beFlag.picked), reflect
                        the BE decision in the count too — otherwise we render the
                        contradictory "0/N Picked (green)" when the event log shows
                        nothing but the server says this LI is fulfilled. */}
                    {showFull    && <div className="od-pick-badge od-pick-badge--full">{beFlag ? bundleQty : instancePicked}/{beFlag ? bundleQty : effectiveTotal} Picked</div>}
                    {showPartial && <div className="od-pick-badge od-pick-badge--partial">{instancePicked}/{effectiveTotal} Picked</div>}
                    {showUnpicked && <div className="od-pick-badge od-pick-badge--unpicked">UNPICKED</div>}
                    </div>
                    {(() => {
                        const entry = (ps?.pack_instances || {})[String(skuInstanceIndex)];
                        if (entry && entry.status === 'failed' && (entry.fail_action || '').toLowerCase() === 'rework') {
                            return (
                                <div className="od-pick-badge od-pick-badge--rework"
                                     style={{ position: 'static', transform: 'none' }}
                                     title={entry.fail_reason || 'Rework'}>
                                    Rework{entry.fail_reason ? `: ${entry.fail_reason}` : ''}
                                </div>
                            );
                        }
                        return null;
                    })()}
                </div>
            </td>
            <td className={isChild ? 'td-li--name--child' : 'td-li--name'} style={{ backgroundColor: bg, color: isChild ? undefined : '#202223', fontWeight: isChild ? undefined : 500 }}>
                <div className="li-title-row">
                    <ProductLink li={li}>
                        <span style={removedStyle}>{li.title}</span>
                    </ProductLink>
                    {removed && <RemovedBadge />}
                    {(splitBundleMap[li.lineItemGroup?.id] ?? splitBundleMap[li.id]) != null && (
                        <JumbleBadge color={splitBundleMap[li.lineItemGroup?.id] ?? splitBundleMap[li.id]} />
                    )}
                </div>
                <LineItemAttrs li={li} />
            </td>
            <td className="td-li--sku" style={{ backgroundColor: bg, ...removedStyle }}>
                <SkuCellContent sku={li.sku} />
                <SkuNotificationCell sku={li.sku} />
            </td>
            <td className="td-li--variant" style={{ backgroundColor: bg, ...removedStyle }}>{li.variant?.title !== 'Default Title' ? li.variant?.title : ''}</td>
            <td className="td-li--qty" style={{ backgroundColor: bg, ...removedStyle }}>{removed ? (li.originalQuantity || 0) : li.quantity}</td>
            <td className="td-li--price" style={{ backgroundColor: bg, ...removedStyle }}>
                {lineItemPrice(li)}
            </td>
        </tr>
    );
}

const TABLE_HEADERS = ['', 'Item', 'SKU', 'Variant', 'Qty', 'Price'];

function TableHead({ selectMode = null }) {
    return (
        <thead>
            <tr className="items-table-head-row">
                {selectMode && <th className="items-table-th" style={{ width: 28 }} />}
                {TABLE_HEADERS.map(h => (
                    <th key={h} className={`items-table-th${h === 'Qty' || h === 'Price' ? ' items-table-th--right' : ''}`}>{h}</th>
                ))}
            </tr>
        </thead>
    );
}

function renderGroupedItems(items, splitBundleMap = {}, portalState = {}, selectMode = null, selectedKeys = null, onToggleKey = null, instanceIndexMap = null, lineItemFlags = null, isBrokenOrder = false, tpOverridesByName = null, tpReqByLiId = null) {
    // Track instance counts and cumulative offsets for ALL SKUs (parent + child) independently.
    // If instanceIndexMap is provided (pre-computed from original Shopify order), use it so that
    // badge assignments are stable across sort modes.
    const skuCounts = {};
    const skuCumQty = {};
    const countSku = (sku, id, qty) => {
        const key = sku || `__no_sku__${id}`;
        skuCounts[key] = (skuCounts[key] || 0) + 1;
        const offset = skuCumQty[key] || 0;
        skuCumQty[key] = offset + qty;
        return { idx: skuCounts[key], offset };
    };
    return items.map(({ item, children }, i) => {
        let parentSkuIdx, parentSkuOffset, childSkuIndices, childSkuOffsets;
        if (instanceIndexMap?.[item.id]) {
            ({ parentSkuIdx, parentSkuOffset, childSkuIndices, childSkuOffsets } = instanceIndexMap[item.id]);
        } else {
            const parent = countSku(item.sku, item.id, item.quantity || 1);
            parentSkuIdx = parent.idx;
            parentSkuOffset = parent.offset;
            // Broken Order: each parent claims 1 instance of each child (shared LI).
            const childResults = children.map(c => countSku(c.sku, c.id, isBrokenOrder ? 1 : (c.quantity || 1)));
            childSkuIndices = childResults.map(r => r.idx);
            childSkuOffsets = childResults.map(r => r.offset);
        }
        return children.length > 0
            ? <BundleRows key={item.id || i} item={item} children={children} priceOf={lineItemPrice} rowIndex={i} splitBundleMap={splitBundleMap} portalState={portalState} selectMode={selectMode} selectedKeys={selectedKeys} onToggleKey={onToggleKey} skuInstanceIndex={parentSkuIdx} skuInstanceOffset={parentSkuOffset} childSkuInstanceIndices={childSkuIndices} childSkuInstanceOffsets={childSkuOffsets} lineItemFlags={lineItemFlags} isBrokenOrder={isBrokenOrder} tpOverridesByName={tpOverridesByName} tpReqByLiId={tpReqByLiId} />
            : <LineItemRow key={item.id || i} li={item} isChild={false} rowIndex={i} splitBundleMap={splitBundleMap} portalState={portalState} selectMode={selectMode} selectedKeys={selectedKeys} onToggleKey={onToggleKey} skuInstanceIndex={parentSkuIdx} skuInstanceOffset={parentSkuOffset} lineItemFlags={lineItemFlags} tpReqByLiId={tpReqByLiId} />;
    });
}

function ItemsTable({ lineItems, sortMode = 'order', splitBundleMap = {}, portalState = {}, selectMode = null, selectedKeys = null, onToggleKey = null, instanceIndexMap: instanceIndexMapProp = null, orderTags = null, lineItemFlags = null, tpOverridesByName = null, tpReqByLiId = null }) {
    const grouped = groupLineItems(lineItems, orderTags);
    // Broken Order tag: Pass 1e in buildBundles reuses the SAME child line
    // item under multiple parents (e.g. one WASA4 line at qty=4 covers 4
    // separate hoodies). For badge math each parent needs only 1 unit of
    // the child, so count child SKU offsets in increments of 1 instead of
    // li.quantity. BundleRows then caps childBundleQty to 1 to match.
    const isBrokenOrder = ((orderTags || []).some(t => (t || '').toLowerCase().trim() === 'broken order'));

    // Pre-compute instance indices and cumulative offsets in original Shopify order so
    // pick/press badges are stable regardless of which sort mode is active.
    // Offsets track the cumulative qty of prior instances (not index * qty, which breaks
    // when different line items with the same SKU have different quantities).
    // If caller passes a pre-computed map (FO cards do this across the ORDER'S line
    // items, not just the FO-filtered subset), use that instead — a rework entry
    // keyed by the ORDER-level index '2' must still line up when we render the
    // single remaining sock inside an FO card.
    let instanceIndexMap;
    if (instanceIndexMapProp) {
        instanceIndexMap = instanceIndexMapProp;
    } else {
        instanceIndexMap = {};
        const skuCounts = {};
        const skuCumQty = {};
        const countSku = (sku, id, qty) => {
            const k = sku || `__no_sku__${id}`;
            skuCounts[k] = (skuCounts[k] || 0) + 1;
            const offset = skuCumQty[k] || 0;
            skuCumQty[k] = offset + qty;
            return { idx: skuCounts[k], offset };
        };
        for (const { item, children } of grouped) {
            const parent = countSku(item.sku, item.id, item.quantity || 1);
            // Broken Order: each parent claims 1 instance of each child (shared LI).
            const childResults = children.map(c => countSku(c.sku, c.id, isBrokenOrder ? 1 : (c.quantity || 1)));
            instanceIndexMap[item.id] = {
                parentSkuIdx:    parent.idx,
                parentSkuOffset: parent.offset,
                childSkuIndices: childResults.map(r => r.idx),
                childSkuOffsets: childResults.map(r => r.offset),
            };
        }
    }

    if (sortMode === 'order') {
        return (
            <table className="items-table">
                <TableHead selectMode={selectMode} />
                <tbody>{renderGroupedItems(grouped, splitBundleMap, portalState, selectMode, selectedKeys, onToggleKey, instanceIndexMap, lineItemFlags, isBrokenOrder, tpOverridesByName, tpReqByLiId)}</tbody>
            </table>
        );
    }

    if (sortMode === 'product') {
        // Sort by productType first (Jersey, Shorts, Socks, ...), then title within
        // a type — alphabetical-only sort wedges items apart when their full
        // product names differ ("Academy 25 Jersey" vs "Park VII Jersey" with
        // "Park III Short" alphabetizing between them).
        const getType = (it) => (
            it.productType
            || it.variant?.product?.productType
            || ''
        ).toLowerCase();
        const sorted = [...grouped].sort((a, b) => {
            const ta = getType(a.item), tb = getType(b.item);
            if (ta !== tb) return ta.localeCompare(tb);
            return (a.item.title || '').localeCompare(b.item.title || '');
        });
        return (
            <table className="items-table">
                <TableHead selectMode={selectMode} />
                <tbody>{renderGroupedItems(sorted, splitBundleMap, portalState, selectMode, selectedKeys, onToggleKey, instanceIndexMap, lineItemFlags, false, tpOverridesByName, tpReqByLiId)}</tbody>
            </table>
        );
    }

    // ── Player mode ──────────────────────────────────────────────────────────
    const playerMap = {};
    const noPlayer = [];
    grouped.forEach(entry => {
        const name = getPlayerName(entry.item);
        if (name) {
            const number = getPlayerNumber(entry.item)
                || entry.children.map(c => getPlayerNumber(c)).find(Boolean);
            if (!playerMap[name]) playerMap[name] = { items: [], number };
            // Player attributes are sprinkled inconsistently across line items —
            // a player's hoodie may carry name-only while their jersey carries
            // name+number. Take the number from whichever item happens to have
            // it instead of locking in only the first encounter.
            else if (!playerMap[name].number && number) playerMap[name].number = number;
            playerMap[name].items.push(entry);
        } else {
            noPlayer.push(entry);
        }
    });
    const playerEntries = Object.entries(playerMap).sort(([a], [b]) => a.localeCompare(b));

    return (
        <table className="items-table">
            <TableHead selectMode={selectMode} />
            <tbody>
                {playerEntries.map(([playerName, { items, number }]) => {
                    const ov = tpOverridesByName && tpOverridesByName[playerName];
                    return (
                    <React.Fragment key={playerName}>
                        <tr>
                            <td colSpan={selectMode ? 7 : 6} className="player-header">
                                👤 {playerName}
                                {ov ? (
                                    <>
                                        <span
                                            className="player-header-number"
                                            style={{ textDecoration: 'line-through', opacity: 0.55, marginLeft: 8 }}
                                            title="Original number from order"
                                        >#{number || ov.order_number}</span>
                                        <span
                                            className="player-header-number"
                                            style={{ color: '#c1281c', fontWeight: 700, marginLeft: 4 }}
                                            title={`Team Portal number override (updated ${ov.tp_updated_at || ''})`}
                                        >#{ov.override_number}</span>
                                        <span
                                            style={{ color: '#c1281c', fontWeight: 700, marginLeft: 8, fontSize: '0.75em', textTransform: 'uppercase' }}
                                        >⚠ TP Override</span>
                                    </>
                                ) : (number ? <span className="player-header-number">#{number}</span> : null)}
                            </td>
                        </tr>
                        {renderGroupedItems(items, splitBundleMap, portalState, selectMode, selectedKeys, onToggleKey, instanceIndexMap, lineItemFlags, false, tpOverridesByName, tpReqByLiId)}
                    </React.Fragment>
                    );
                })}
                {noPlayer.length > 0 && (
                    <React.Fragment>
                        {playerEntries.length > 0 && (
                            <tr>
                                <td colSpan={selectMode ? 7 : 6} className="player-other-header">
                                    Other Items
                                </td>
                            </tr>
                        )}
                        {renderGroupedItems(noPlayer, splitBundleMap, portalState, selectMode, selectedKeys, onToggleKey, instanceIndexMap, lineItemFlags, false, tpOverridesByName, tpReqByLiId)}
                    </React.Fragment>
                )}
            </tbody>
        </table>
    );
}

// ── Players / Members panel ───────────────────────────────────────────────────

function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''; }

const PRESENCE_COLORS = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#06b6d4','#84cc16'];
function presenceColor(userId) {
    let h = 0;
    for (let i = 0; i < userId.length; i++) h = (h * 31 + userId.charCodeAt(i)) >>> 0;
    return PRESENCE_COLORS[h % PRESENCE_COLORS.length];
}

// Turn the raw TP specialization enum into a display label.
// TP stores: regular / captain / goalkeeper / returning_player / returning_goalkeeper.
const SPEC_LABELS = {
    regular:               'Regular',
    captain:               'Captain',
    goalkeeper:            'Goalkeeper',
    returning_player:      'Returning Player',
    returning_goalkeeper:  'Returning Goalkeeper',
};

function PlayersPanel({ orderAttrs, tpOverrides = [], tpTeamOverrides = [], tpRequirementsPlayers = [] }) {
    const raw = (orderAttrs || []).find(a => a.key === '_soccer_order_info')?.value;
    if (!raw) return null;
    let players = [];
    try { players = JSON.parse(raw); } catch { return null; }
    if (!players.length) return null;

    // Team Portal number overrides — keyed by player id (rock-solid FK).
    const overrideById = {};
    for (const ov of (tpOverrides || [])) {
        if (ov && ov.player_id != null) overrideById[String(ov.player_id)] = ov;
    }
    // Team Portal TEAM overrides — same shape/key (clubs with Team Override on).
    const teamOvById = {};
    for (const ov of (tpTeamOverrides || [])) {
        if (ov && ov.player_id != null) teamOvById[String(ov.player_id)] = ov;
    }

    // Live TP roster + compliance data (spec, role, missing required items),
    // keyed by user_id. Live-fetched from TP RDS by the backend at order-detail
    // time, so a mid-season spec change (Regular → Captain) shows up on the
    // next open without a recache. Empty {} for non-TP orders.
    const tpByPid = {};
    for (const p of (tpRequirementsPlayers || [])) {
        if (p && p.user_id != null) tpByPid[String(p.user_id)] = p;
    }

    return (
        <Card title="Players / Members">
            {players.map((p, i) => {
                const ov = p.id != null ? overrideById[String(p.id)] : null;
                return (
                <div key={p.id || i} className="players-panel-item">
                    <div className="players-panel-name">{p.name}</div>
                    {p.club?.title && (
                        <div className="players-panel-club">
                            <span className="players-panel-label">Club:</span> {p.club.title}
                        </div>
                    )}
                    {(() => {
                        const tov = p.id != null ? teamOvById[String(p.id)] : null;
                        if (tov) {
                            return (
                                <div className="players-panel-team">
                                    <span className="players-panel-label">Team:</span>{' '}
                                    {(p.team?.title || tov.order_team) && (
                                        <span style={{ textDecoration: 'line-through', opacity: 0.55 }}
                                              title="Original team from order">
                                            {p.team?.title || tov.order_team}
                                        </span>
                                    )}{' '}
                                    <span style={{ color: '#c1281c', fontWeight: 700 }}
                                          title={`Updated in Team Portal ${tov.tp_updated_at || ''}`}>
                                        {tov.override_team}
                                    </span>
                                </div>
                            );
                        }
                        return p.team?.title ? (
                            <div className="players-panel-team">
                                <span className="players-panel-label">Team:</span> {p.team.title}
                            </div>
                        ) : null;
                    })()}
                    <div className="players-panel-badges">
                        {(() => {
                            // Broken-order team-bulk: render the full assigned_number list
                            // (e.g. "#100, 101, ..., 111"). Falls back to singular config.number.
                            const assigned = p.config?.assigned_number;
                            let label = null;
                            if (Array.isArray(assigned) && assigned.length > 0) {
                                const nums = assigned.map(n => String(n).trim()).filter(Boolean);
                                if (nums.length) {
                                    try { nums.sort((a, b) => parseInt(a, 10) - parseInt(b, 10)); } catch (_) {}
                                    label = nums.join(', ');
                                }
                            }
                            if (!label && p.config?.number) label = String(p.config.number);
                            // TP override wins over the order-embedded number.
                            if (ov) {
                                return (
                                    <>
                                        <span
                                            className="players-panel-badge-number"
                                            style={{ textDecoration: 'line-through', opacity: 0.55 }}
                                            title="Original number from order"
                                        >
                                            #{label ?? ov.order_number}
                                        </span>
                                        <span
                                            className="players-panel-badge-number"
                                            style={{ color: '#c1281c', borderColor: '#c1281c', fontWeight: 700 }}
                                            title={`Updated in Team Portal ${ov.tp_updated_at || ''}`}
                                        >
                                            #{ov.override_number}
                                        </span>
                                    </>
                                );
                            }
                            return label ? <span className="players-panel-badge-number">#{label}</span> : null;
                        })()}
                        {p.config?.gender && (
                            <span className="players-panel-badge-meta">{cap(p.config.gender)}</span>
                        )}
                        {(() => {
                            // Prefer LIVE spec + role from TP RDS (tpRequirementsPlayers)
                            // over whatever the order snapshotted at checkout — a coach
                            // can re-roster a player mid-season and we want to show the
                            // CURRENT designation, not a stale one baked into the order.
                            const tpp = p.id != null ? tpByPid[String(p.id)] : null;
                            const spec = tpp?.specialization;
                            const role = tpp?.role || p.role;
                            const specLabel = spec ? (SPEC_LABELS[spec] || cap(spec.replace(/_/g, ' '))) : null;
                            return (
                                <>
                                    {specLabel && <span className="players-panel-badge-meta">{specLabel}</span>}
                                    {role && <span className="players-panel-badge-meta">{cap(role)}</span>}
                                </>
                            );
                        })()}
                    </div>
                    {ov && (
                        <div
                            className="players-panel-override"
                            title={`Team Portal updated this number after the order was placed (${ov.tp_updated_at || ''})`}
                            style={{
                                marginTop: 6, color: '#c1281c', fontWeight: 700,
                                fontSize: '0.85em', textTransform: 'uppercase',
                                letterSpacing: '0.02em',
                            }}
                        >
                            ⚠ Team Portal Number Override
                        </div>
                    )}
                    {(p.id != null && teamOvById[String(p.id)]) && (
                        <div
                            className="players-panel-override"
                            title={`Team Portal moved this player to a different team after the order was placed (${teamOvById[String(p.id)].tp_updated_at || ''})`}
                            style={{
                                marginTop: 6, color: '#c1281c', fontWeight: 700,
                                fontSize: '0.85em', textTransform: 'uppercase',
                                letterSpacing: '0.02em',
                            }}
                        >
                            ⚠ Team Portal Team Override
                        </div>
                    )}
                </div>
                );
            })}
        </Card>
    );
}

// ── Per-FO card (handles its own fulfill state) ───────────────────────────────

// FulfillmentMenu — per-FO action dropdown rendered next to the kebab in the
// FO card header. State-aware: shows Print Shipping Label / Transfer To
// Pickup Location / Ready For Pickup / Mark As Picked Up depending on the
// FO's delivery method + transfer/RFP/picked-up flags. Disabled for voided
// or fully-fulfilled FOs. Hidden for `crm` role (read-only).
// Shopify's "Void label" reasons (ShippingLabelCancellationReason) — same list
// the Shopify admin dialog shows. OTHER requires a description.
const VOID_REASON_OPTIONS = [
    ['WRONG_MAIL_SERVICE', 'Wrong mail service'],
    ['WRONG_PACKAGE_SIZE', 'Wrong package size'],
    ['WRONG_WEIGHT', 'Wrong shipment weight'],
    ['WRONG_SHIP_DATE', "Can't mail by ship date"],
    ['ERROR_DOWNLOADING_LABEL', "Can't download label"],
    ['ERROR_PRINTING_LABEL', "Can't print label"],
    ['WRONG_LABEL_FORMAT', 'Wrong label format'],
    ['LABEL_TOO_EXPENSIVE', 'Label is too expensive'],
    ['TEST_PURCHASE', 'This was a test purchase'],
    ['OTHER', 'Other'],
];

function FulfillmentMenu({
    fo, fulfillment = null, orderId, isOpen, isShipping, hasLabel, pickup, atPickupLocation,
    pickupReadyAt, pickedUpAt, isCancelledLabel, isTrulyFulfilled,
    storeKey, storeFetch, onAfterAction, siblingFos = [],
    storePickupOverride = '', onToast = null, fbmMoved = false, triggerClass = '',
}) {
    const { user } = useAuth();
    const allowedRoles = ['admin', 'franchise', 'site_manager', 'user', 'store_manager'];  // store_manager added 2026-09-11 (Eric)
    const [menuOpen, setMenuOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [busyLabel, setBusyLabel] = useState('Working…');
    const [fbmRates, setFbmRates] = useState(null);   // FBM moved orders: {rates, label_gid, session_gid, shipping_date, beforeIds}
    const [busySec, setBusySec] = useState(0);
    const [error, setError] = useState(null);
    const wrapRef = useRef(null);
    // Change-location modal state (lazy-loaded location list; small list, one fetch per open)
    const [changeLocOpen, setChangeLocOpen]         = useState(false);
    const [changeLocList, setChangeLocList]         = useState([]);
    const [changeLocSearch, setChangeLocSearch]     = useState('');
    const [changeLocSelected, setChangeLocSelected] = useState(''); // shopify_location_id
    const [changeLocSubmitting, setChangeLocSubmitting] = useState(false);
    const [changeLocError, setChangeLocError]       = useState(null);
    // 2026-08-01: toggle for whether Change Location also moves physical
    // stock. Default OFF (routing-only). See modal copy for the exact
    // semantic of each mode.
    const [changeLocMoveInventory, setChangeLocMoveInventory] = useState(false);

    // Mark-as-picked-up confirm (2026-09-11). The customer email is opt-in
    // and resets to OFF every time the dialog opens — Shopify's "Picked up"
    // notification should never go out by accident.
    const [pickedUpOpen, setPickedUpOpen]           = useState(false);
    const [pickedUpNotify, setPickedUpNotify]       = useState(false);

    // Cancel-fulfillment modal (2026-09-08). Defaults per spec: void the
    // label when one exists; leave at the store when transferred; do NOT
    // move inventory under move-back.
    const [cancelOpen, setCancelOpen]               = useState(false);
    const [cancelVoidLabel, setCancelVoidLabel]     = useState(true);
    const [cancelReason, setCancelReason]           = useState('');
    const [cancelDesc, setCancelDesc]               = useState('');
    const [cancelRevert, setCancelRevert]           = useState(false);
    const [cancelMoveInv, setCancelMoveInv]         = useState(false);
    const [cancelSubmitting, setCancelSubmitting]   = useState(false);
    const [cancelError, setCancelError]             = useState(null);
    const [cancelLocNames, setCancelLocNames]       = useState({}); // shopify_location_id -> name
    // Success toast (2026-09-08 fix round 2): rendered at the ORDER level via
    // `onToast` (order-level odPrintToast), not local state — this menu
    // instance unmounts as soon as onAfterAction() reloads the order and
    // drops the now-cancelled fulfillment's card, so a local toast timer
    // would never survive to fire. See OrderDetail's odPrintToast.

    // Seconds counter for the "Fulfilling & purchasing label…" overlay —
    // UPS label purchase + Shopify fulfillment auto-complete can take 5-15s
    // and the spinner alone makes it feel hung. Mirrors QCActionBar's
    // 'buying' step timer. Resets when the busy flag drops.
    useEffect(() => {
        if (!busy) { setBusySec(0); return; }
        const startedAt = Date.now();
        const id = setInterval(() => {
            setBusySec(Math.floor((Date.now() - startedAt) / 1000));
        }, 250);
        return () => clearInterval(id);
    }, [busy]);

    useEffect(() => {
        if (!menuOpen) return;
        const onDown = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setMenuOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [menuOpen]);

    if (!hasRole(user, allowedRoles)) return null;

    // Voided (label cancelled) or fully-fulfilled FOs: render the menu but
    // mark every option disabled, so the operator sees what would have been
    // available without thinking the menu is missing.
    const allDisabled = !isOpen || isCancelledLabel;

    // Compute action visibility per state machine. At most one of these is
    // primary at a time; the others are hidden (not just disabled).
    // `hasLabel` hides "Fulfill & Print Label" once a label exists for this
    // FO — the trigger button then greys out automatically because no
    // action remains (see triggerDisabled below). User flow: click Fulfill
    // & Print Label → overlay shows during purchase → label popups opens →
    // order refresh sets hasLabel=true → trigger button greys.
    // 2026-09-03 (SP-337266:A): an order with a store_pickup_override ships to
    // the STORE in a bulk box, never by UPS to the customer — Shopify refuses
    // to label it. Match the Packing Portal (isPickup when an override is set):
    // no "Fulfill & Print Label"; the transfer / packing-slip actions apply.
    const showPrintShipping = isShipping && !hasLabel && !(storePickupOverride || '').trim();
    // 2026-09-05: mark a shipping FO fulfilled in Shopify with NO label — for
    // hand-offs / carriers outside OpsHub (franchise partial fulfillments).
    // Same backend call the Bulk Delivery slip uses, minus the tracking.
    // 2026-10-06 (Eric, SP-373959): an FO with NO delivery method ("Shipping
    // not required" — Teamatical Number/Name lines re-opened by Cancel
    // Fulfillment, order-edit add-ons) had no way to be closed from here.
    // Same backend call; the label just says what it does.
    const isNoShipping = (fo.deliveryMethod?.methodType || '').toUpperCase() === 'NONE';
    const showFulfillNoLabel = (isShipping || isNoShipping) && !hasLabel;
    const showTransfer      = pickup && !atPickupLocation;
    // 2026-09-16 (Eric, SP-336072): an override order's FO is SHIPPING-method;
    // it arrives at the override store in a bulk box and Shopify has no
    // ready-for-pickup button for it. Offer Mark Ready For Pickup on any open
    // override FO — the backend records it locally (no Shopify email) and
    // returns a contact-the-customer notice.
    const _isOverrideFo = !pickup && !!(storePickupOverride || '').trim();
    const showReadyForPickup= (pickup && (atPickupLocation || fo._pickupState?.transferred_at) && !pickupReadyAt && !pickedUpAt)
        || (_isOverrideFo && isOpen && !isCancelledLabel && !pickupReadyAt && !pickedUpAt);
    // 2026-09-11: "Mark as Picked Up" no longer waits for the ready mark —
    // any open pickup FO (or an override order's FO, which Shopify types as
    // SHIPPING) can be closed as picked up straight from the order page.
    const showMarkPickedUp  = (pickup || !!(storePickupOverride || '').trim())
        && isOpen && !isCancelledLabel && !pickedUpAt;
    // Change Location is available on any OPEN FO — mirrors Shopify Admin's
    // Change Location option on the fulfillment-order kebab.
    const showChangeLocation = isOpen && !isCancelledLabel && !pickedUpAt;
    // Transfer to Override Pickup — atomic action for the `:A` order case:
    // FO is stuck IN_PROGRESS (or OPEN but SHIPPING-methodType) and the
    // order carries a store_pickup_override. Unfulfills the FO if needed,
    // then moves inventory + FO to the override store in one call. Hidden
    // when no override is set OR when the FO is already at the override
    // location OR when the standard "Transfer To Pickup" button already
    // covers this case (pickup method + not at pickup).
    const overrideName = (storePickupOverride || '').trim();
    const currentLocName = ((fo.assignedLocation || {}).location || {}).name
        || (fo.assignedLocation || {}).name || '';
    // Moved up from ~1617 (was below the visibility block that now uses
    // currentLocNumeric for cancelWasTransferred) — plain consts derived
    // from `fo`, safe to compute this early.
    const currentLocGid   = fo?.assignedLocation?.location?.id || '';
    const currentLocNumeric = currentLocGid.split('/').pop() || '';
    const showOverrideTransfer = !!overrideName
        && overrideName !== currentLocName
        && isOpen && !isCancelledLabel && !pickedUpAt
        && !showTransfer;

    // Cancel Fulfillment — the ONE action that exists only once the FO is
    // fulfilled (inverse of every other item's guard). Roles = Change Location.
    const cancelHasLabel = !!hasLabel || (fulfillment?.trackingInfo || []).length > 0;
    const cancelTransferredFrom = fo?._pickupState?.transferred_from_location_id || null;
    const cancelWasTransferred = !!cancelTransferredFrom && String(cancelTransferredFrom) !== String(currentLocNumeric);
    const showCancelFulfillment = !!isTrulyFulfilled && fulfillment?.id && fulfillment?.status === 'SUCCESS';
    // Pickup undo is two steps. Ready For Pickup, not yet picked up: "Cancel
    // Pickup" just clears Ready For Pickup — the FO stays at the store.
    // Transferred to a store but not yet marked ready (or already un-readied
    // by Cancel Pickup): "Cancel Fulfillment" on the transferred FO moves it
    // back and asks about inventory (see cancelMode === 'transfer' below).
    const showCancelPickup = pickup && isOpen && !isCancelledLabel && !pickedUpAt && !!pickupReadyAt;
    const showCancelTransfer = isOpen && !isCancelledLabel && !pickedUpAt && !pickupReadyAt && !!cancelTransferredFrom;
    const cancelMode = showCancelFulfillment ? 'fulfilled' : showCancelPickup ? 'pickup' : showCancelTransfer ? 'transfer' : null;

    // Trigger-level disable: when no action is available (picked-up FOs,
    // voided labels, fully-fulfilled non-pickup FOs), grey the trigger
    // button itself so the operator can see at a glance there's nothing
    // to do here. The menu is still openable to surface "No actions
    // available" if they want to confirm.
    const triggerDisabled =
        busy
        || (pickedUpAt && !cancelMode)
        || (!showPrintShipping && !showTransfer && !showReadyForPickup && !showMarkPickedUp && !showChangeLocation && !showOverrideTransfer && !cancelMode);

    const closeMenu = () => setMenuOpen(false);

    const callAutoComplete = async () => {
        try {
            await storeFetch(`/api/orders/${orderId}/fulfillment/auto-complete-fo`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ fulfillment_order_id: fo.id }),
            });
        } catch (e) {
            // Non-fatal — the action itself succeeded; pct just won't update
            // until next recache. Surface as warning, not error.
            console.warn('auto-complete-fo failed:', e);
        }
    };

    const openBuyAndPrint = (d, rate) => {
        const qs = new URLSearchParams({
            fulfillment_order_id: fo.id,
            label_gid:            d.label_gid,
            session_gid:          d.session_gid,
            quoted_price:         String(rate ? rate.price : (d.quoted_price ?? '')),
            shipping_date:        String(d.shipping_date ?? ''),
            store:                storeKey,
        });
        // 2026-07-29: carrier / service fall through so the kiosk
        // print path knows to purchase USPS Ground Advantage on the
        // PO-Box fallback path (default UPS Ground when omitted).
        const cc = rate ? rate.carrier_code : d.carrier_code;
        const sc = rate ? rate.service_code : d.service_code;
        if (cc) qs.set('carrier_code', cc);
        if (sc) qs.set('service_code', sc);
        if (rate) qs.set('slip', '4x6');   // FBM: 4x6 packing slip + 4x6 label
        setBusyLabel('Purchasing label & opening print window…');
        // Open the Packing-Portal-style buy-and-print URL in a new tab.
        // Auth is via ops_auth cookie → recipient's session authorizes.
        window.open(`/api/orders/${orderId}/shipping-label/buy-and-print-html?${qs}`,
                    '_blank', 'noopener,noreferrer');
    };

    const finishAfterOpen = async (beforeIds) => {
        // Poll until the new label appears in the DB (popup completes
        // the purchase asynchronously) OR 60s timeout. Once detected,
        // refresh the parent order so storedLabel populates and the
        // Fulfillment trigger button greys out via hasLabel=true.
        setBusyLabel('Waiting for label…');
        const POLL_INTERVAL = 1500;
        const POLL_MAX_TRIES = 40; // 60s
        let labelDetected = false;
        for (let i = 0; i < POLL_MAX_TRIES; i++) {
            await new Promise(res => setTimeout(res, POLL_INTERVAL));
            try {
                const lr = await storeFetch(`/api/orders/${orderId}/shipping-labels`);
                if (!lr.ok) continue;
                const body = await lr.json().catch(() => ({}));
                const labels = Array.isArray(body) ? body : (body?.labels || []);
                const fresh = labels.some(l =>
                    l?.shopify_label_gid && !beforeIds.has(l.shopify_label_gid));
                if (fresh) {
                    labelDetected = true;
                    break;
                }
            } catch (_) { /* keep polling */ }
        }
        // H9: only mark the order fulfilled (picked/pressed) once the label
        // is confirmed purchased. Previously auto-complete fired right after
        // opening the purchase tab, so a failed purchase (bad address,
        // expired session) still left the order auto-completed with no label.
        if (labelDetected) {
            setBusyLabel('Fulfilling order…');
            await callAutoComplete();
            setBusyLabel('Refreshing order…');
        } else {
            setBusyLabel('Label not detected — not marking fulfilled; refreshing…');
        }
        onAfterAction?.();
    };

    const handlePrintShipping = async () => {
        closeMenu();
        // Two-step: rate first to get label_gid + session_gid + quote, then
        // open the buy-and-print-html endpoint with the rate context. After
        // the popup opens, poll /api/orders/{id}/shipping-labels until the
        // new label appears OR 60s timeout — only THEN refresh the parent
        // page so storedLabel is populated and the Fulfillment trigger
        // greys out without the operator having to reload.
        setBusy(true); setError(null);
        setBusyLabel('Getting shipping rate…');
        try {
            // Snapshot the pre-existing label GIDs so we can detect when a
            // new one shows up. Fetch failures are non-fatal: we just won't
            // have any pre-existing GIDs to compare against, and any returned
            // label will look "new" — which is fine.
            // NOTE: endpoint returns {labels: [...]}, not a bare array.
            const beforeIds = new Set();
            try {
                const preRes = await storeFetch(`/api/orders/${orderId}/shipping-labels`);
                if (preRes.ok) {
                    const pre = await preRes.json().catch(() => ({}));
                    const arr = Array.isArray(pre) ? pre : (pre?.labels || []);
                    arr.forEach(l => l?.shopify_label_gid && beforeIds.add(l.shopify_label_gid));
                }
            } catch (_) { /* non-fatal */ }

            const isFbm = fbmMoved;
            const r = await storeFetch(`/api/orders/${orderId}/shipping-label/rate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(isFbm ? { fulfillment_order_id: fo.id, all_rates: true } : { fulfillment_order_id: fo.id }),
            });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) {
                setError(d?.detail || `Failed to get shipping rate (HTTP ${r.status}).`);
                return;
            }
            if (isFbm) { setFbmRates({ ...d, beforeIds }); return; }
            openBuyAndPrint(d, null);
            await finishAfterOpen(beforeIds);
        } catch (e) {
            setError(e?.message || 'Network error while rating the shipping label.');
        } finally {
            setBusy(false);
        }
    };

    const handleTransferOverridePickup = async () => {
        closeMenu();
        if (!window.confirm(
            `Transfer this FO to override pickup (${overrideName})?\n\n`
            + `This will unfulfill the FO if it's stuck IN_PROGRESS, then move both `
            + `the FO and its inventory to ${overrideName}. Shipping happens outside Shopify.`
        )) return;
        setBusy(true); setError(null);
        try {
            const r = await storeFetch(`/api/orders/${orderId}/transfer-to-override-pickup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ fulfillment_order_id: fo.id }),
            });
            const d = await r.json();
            if (!r.ok) { setError(d.detail || 'Override transfer failed.'); return; }
            onAfterAction?.();
        } catch (e) {
            setError(e.message || 'Override transfer failed');
        } finally { setBusy(false); }
    };

    const handleTransferPickup = async () => {
        closeMenu();
        setBusy(true); setError(null);
        try {
            const r = await storeFetch(`/api/orders/${orderId}/transfer-to-pickup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ fulfillment_order_id: fo.id }),
            });
            const d = await r.json();
            if (!r.ok) { setError(d.detail || 'Transfer failed.'); return; }
            // Open the pickup packing slip in a new tab.
            const qs = new URLSearchParams({
                fulfillment_order_id: fo.id,
                skip_transfer: 'true',
                store: storeKey,
            });
            window.open(`/api/orders/${orderId}/packing-slip-print?${qs}`,
                        '_blank', 'noopener,noreferrer');
            await callAutoComplete();
            onAfterAction?.();
        } catch (e) {
            setError(e.message || 'Transfer failed');
        } finally { setBusy(false); }
    };

    const handleReadyForPickup = async () => {
        closeMenu();
        setBusy(true); setError(null);
        try {
            const r = await storeFetch(`/api/orders/${orderId}/mark-ready-for-pickup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ fulfillment_order_id: fo.id }),
            });
            const d = await r.json();
            if (!r.ok) { setError(d.detail || 'Mark RFP failed.'); return; }
            if (d?.local_only) {
                const c = d.contact_notice || {};
                const who = [c.customer_name, c.customer_email, c.customer_phone].filter(Boolean).join(' · ');
                onToast?.({ key: Date.now(), tone: 'warning',
                    msg: `Marked Ready For Pickup in OpsHub only — Shopify cannot email a ${c.reason === 'override_store' ? 'pickup order moved to another store' : 'shipping order'}. Contact the customer${who ? `: ${who}` : ''}.` });
            }
            onAfterAction?.();
        } catch (e) {
            setError(e.message || 'Mark RFP failed');
        } finally { setBusy(false); }
    };

    const handleFulfillNoLabel = async () => {
        closeMenu();
        if (!window.confirm(isNoShipping
            ? 'Mark these items as fulfilled in Shopify?\n\nNothing ships; the customer is not emailed.'
            : 'Mark this fulfillment as fulfilled in Shopify without a shipping label?\n\nNo tracking is recorded and the customer is not emailed.')) return;
        setBusy(true); setError(null);
        try {
            const r = await storeFetch(`/api/orders/${orderId}/fulfill`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ fulfillment_order_ids: [fo.id], notify_customer: false }),
            });
            let d = {};
            try { d = await r.json(); } catch (_) {}
            const errs = (d.userErrors || []).map(e => e.message).join('; ');
            if (!r.ok || errs) { setError((d.detail && (d.detail.message || d.detail)) || errs || 'Fulfill failed.'); return; }
            onAfterAction?.();
        } catch (e) {
            setError(e.message || 'Fulfill failed');
        } finally { setBusy(false); }
    };

    const handleMarkPickedUp = () => {
        closeMenu();
        setPickedUpNotify(false);
        setError(null);
        setPickedUpOpen(true);
    };

    const submitMarkPickedUp = async () => {
        const notify = !!pickedUpNotify;
        setPickedUpOpen(false);
        setBusy(true); setBusyLabel('Marking as picked up…'); setError(null);
        try {
            const r = await storeFetch(`/api/orders/${orderId}/mark-picked-up`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ fulfillment_order_id: fo.id, notify_customer: notify }),
            });
            const d = await r.json();
            if (!r.ok) { setError(d.detail || 'Mark picked-up failed.'); return; }
            onToast?.(notify ? 'Marked as picked up · customer notified' : 'Marked as picked up');
            onAfterAction?.();
        } catch (e) {
            setError(e.message || 'Mark picked-up failed');
        } finally { setBusy(false); setBusyLabel('Working…'); }
    };

    // Change Location — open modal, lazy-fetch locations on first open.
    // Filter: active + not-store-only (matches sidebar).

    // Suggested default destination: if the ORDER has another OPEN FO at a
    // "Teams *" location that isn't this FO's current location, pre-select
    // it. Rationale: multi-warehouse team orders usually consolidate to the
    // other team-loc. First match wins (stable across siblingFos ordering).
    const suggestedDestLocId = (() => {
        for (const s of (siblingFos || [])) {
            const status = (s.status || '').toUpperCase();
            if (status === 'CANCELLED' || status === 'CLOSED') continue;
            const name = s.locationName || '';
            const locId = s.locationId || '';
            if (!locId || !name) continue;
            if (locId === currentLocNumeric) continue;
            if (name.startsWith('Teams ')) return locId;
        }
        return '';
    })();

    const openChangeLoc = async () => {
        closeMenu();
        setChangeLocError(null);
        setChangeLocSearch('');
        setChangeLocOpen(true);
        // Always default to routing-only on open — operator must explicitly
        // opt in to moving physical stock.
        setChangeLocMoveInventory(false);
        // Reset selection to the sibling-Teams default (or empty if none).
        setChangeLocSelected(suggestedDestLocId || '');
        if (changeLocList.length === 0) {
            try {
                const r = await storeFetch('/api/locations');
                if (r.ok) {
                    const rows = await r.json();
                    const filtered = (Array.isArray(rows) ? rows : [])
                        .filter(l => l.active && String(l.shopify_location_id) !== '0'
                                     && String(l.shopify_location_id) !== currentLocNumeric)
                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                    setChangeLocList(filtered);
                }
            } catch (e) {
                setChangeLocError('Could not load locations.');
            }
        }
    };
    const submitChangeLoc = async () => {
        if (!changeLocSelected) { setChangeLocError('Pick a destination location.'); return; }
        setChangeLocSubmitting(true); setChangeLocError(null);
        setBusyLabel('Moving fulfillment order…');
        try {
            const r = await storeFetch(`/api/orders/${orderId}/change-fo-location`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    fulfillment_order_id: fo.id,
                    new_location_id: Number(changeLocSelected),
                    move_inventory: changeLocMoveInventory,
                }),
            });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) {
                setChangeLocError(d.detail || `Change location failed (${r.status}).`);
                return;
            }
            setChangeLocOpen(false);
            onAfterAction?.();
        } catch (e) {
            setChangeLocError(e.message || 'Change location failed.');
        } finally {
            setChangeLocSubmitting(false);
        }
    };

    const openCancelFulfillment = async () => {
        closeMenu();
        setCancelError(null);
        setCancelReason(''); setCancelDesc('');
        if (cancelMode === 'pickup') {
            setCancelVoidLabel(false);
            setCancelRevert(false);
            setCancelMoveInv(false);
        } else if (cancelMode === 'transfer') {
            setCancelVoidLabel(false);
            setCancelRevert(true);
            setCancelMoveInv(false);
        } else {
            setCancelVoidLabel(cancelHasLabel);
            setCancelRevert(false); setCancelMoveInv(false);
        }
        setCancelOpen(true);
        // Fetch location names whenever the map is empty — not just when
        // transferred — so the post-submit "reopened at …" toast summary
        // can name the location even for a plain (never-transferred) FO.
        if (Object.keys(cancelLocNames).length === 0) {
            try {
                const r = await storeFetch('/api/locations');
                if (r.ok) {
                    const rows = await r.json();
                    const m = {};
                    (Array.isArray(rows) ? rows : []).forEach(l => { m[String(l.shopify_location_id)] = l.name; });
                    setCancelLocNames(m);
                }
            } catch (e) { /* names are cosmetic; ids still work */ }
        }
    };
    const submitCancelFulfillment = async () => {
        if (cancelVoidLabel && !cancelReason) { setCancelError('Pick a reason for voiding the label.'); return; }
        if (cancelVoidLabel && cancelReason === 'OTHER' && !cancelDesc.trim()) { setCancelError('Add a description for "Other".'); return; }
        setCancelSubmitting(true); setCancelError(null);
        try {
            const r = await storeFetch(`/api/orders/${orderId}/cancel-fulfillment`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    fulfillment_order_id: fo.id,
                    fulfillment_id: cancelMode === 'fulfilled' ? fulfillment?.id : null,
                    void_label: cancelMode === 'fulfilled' && cancelHasLabel,
                    void_reason: cancelVoidLabel ? cancelReason : null,
                    void_description: cancelVoidLabel ? cancelDesc.trim() : null,
                    revert_to_source: cancelMode === 'pickup' ? false : cancelMode === 'transfer' ? true : (cancelWasTransferred && cancelRevert),
                    move_inventory: cancelMode === 'pickup' ? false : cancelMode === 'transfer' ? cancelMoveInv : (cancelWasTransferred && cancelRevert && cancelMoveInv),
                }),
            });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) { setCancelError(d.detail || `Cancel fulfillment failed (${r.status}).`); return; }
            const failed = (d.steps || []).filter(s => !s.ok);
            if (failed.length) {
                // onAfterAction() below reloads the order, which unmounts this
                // card (and this modal) — an inline cancelError here would
                // flash and vanish before anyone could read it. Route the
                // partial-failure message through the order-level toast
                // instead, same as the success path.
                const msg = `Fulfillment cancelled, but: ${failed.map(s => s.detail).join('; ')}`;
                setCancelOpen(false);
                onToast?.({ key: Date.now(), msg, tone: 'warning' });
            } else {
                const sourceName = cancelLocNames[String(cancelTransferredFrom)] || `location ${cancelTransferredFrom}`;
                let message;
                if (cancelMode === 'pickup') {
                    const storeName = currentLocName || 'the current location';
                    message = `Pickup cancelled · Ready for Pickup cleared · still at ${storeName}`;
                } else {
                    const parts = [];
                    if (d.label_voided) {
                        parts.push(`label voided${cancelReason ? ` (${VOID_REASON_OPTIONS.find(([v]) => v === cancelReason)?.[1] || cancelReason})` : ''}`);
                    }
                    if (d.reverted) {
                        parts.push(`moved back to ${sourceName}, inventory ${cancelMoveInv ? 'moved' : 'not moved'}`);
                    } else if (d.reopened_at_location_id) {
                        parts.push(`reopened at ${cancelLocNames[String(d.reopened_at_location_id)] || 'current location'}`);
                    }
                    message = `Fulfillment cancelled${parts.length ? ' · ' + parts.join(' · ') : ''}`;
                }
                setCancelOpen(false);
                // Fires at the ORDER level (see onToast prop) — this card
                // unmounts as soon as onAfterAction() reloads the order, so
                // the toast must live above this component to survive.
                onToast?.({ key: Date.now(), msg: message, tone: 'success' });
            }
            onAfterAction?.();
        } catch (e) {
            setCancelError(e.message || 'Cancel fulfillment failed.');
        } finally {
            setCancelSubmitting(false);
        }
    };
    const filteredChangeLocs = (() => {
        const q = (changeLocSearch || '').trim().toLowerCase();
        // 2026-09-18 (Eric): match at the start of a word, not anywhere in the
        // name — "Te" used to list Lancas-te-r and Roches-te-r ahead of the
        // Teams locations.
        // Multi-word queries ("Teams L") match from any word boundary in the name.
        const wordStart = (name) => {
            const n = (name || '').toLowerCase();
            if (n.startsWith(q)) return true;
            const re = /[\s()\-\/]+/g; let m;
            while ((m = re.exec(n)) !== null) { if (n.slice(m.index + m[0].length).startsWith(q)) return true; }
            return false;
        };
        const base = q
            ? changeLocList.filter(l => wordStart(l.name))
            : changeLocList;
        // Pin the suggested destination to the top so the smart default is the
        // first row the user sees — mirrors Shopify Admin's "Suggested" pin.
        if (!suggestedDestLocId) return base;
        const idx = base.findIndex(l => String(l.shopify_location_id) === String(suggestedDestLocId));
        if (idx <= 0) return base;   // already at top or missing → no-op
        const reordered = [base[idx], ...base.slice(0, idx), ...base.slice(idx + 1)];
        return reordered;
    })();

    // V3 (triggerClass) renders icon + label + description rows; V1/V2 keep the plain text rows.
    const MENU_ICONS = {
        label: 'M5 3h10v4H5zM4 8h12a1 1 0 0 1 1 1v5h-3v3H6v-3H3V9a1 1 0 0 1 1-1zm4 5v2h4v-2z',
        check: 'M8.2 13.6 4.6 10l1.2-1.2 2.4 2.4 6-6 1.2 1.2z',
        pin: 'M10 2a5 5 0 0 0-5 5c0 3.4 5 11 5 11s5-7.6 5-11a5 5 0 0 0-5-5zm0 7a2 2 0 1 1 0-4 2 2 0 0 1 0 4z',
        x: 'M6.2 5 10 8.8 13.8 5 15 6.2 11.2 10l3.8 3.8-1.2 1.2L10 11.2 6.2 15 5 13.8 8.8 10 5 6.2z',
    };
    const menuItem = ({ onClick, icon, label, desc, danger = false }) => triggerClass ? (
        <button key={label} type="button" className={`odv3-mi odv3-mi--row${danger ? ' odv3-mi--danger' : ''}`}
                onClick={onClick} disabled={!danger && allDisabled}>
            <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor" aria-hidden="true" className="odv3-mi-ico"><path d={MENU_ICONS[icon]}/></svg>
            <span className="odv3-mi-tx"><span className="odv3-mi-l">{label}</span><span className="odv3-mi-s">{desc}</span></span>
        </button>
    ) : (
        <button key={label} className="fo-ellipsis-item" onClick={onClick}
                disabled={danger ? undefined : allDisabled}
                style={danger ? { color: '#b91c1c', borderTop: '1px solid #f3f4f6' } : undefined}>
            {label}
        </button>
    );

    return (
        <div className="fo-ellipsis-wrap" ref={wrapRef}>
            <button
                className={triggerClass || 'fo-ellipsis-btn'}
                aria-expanded={triggerClass ? menuOpen : undefined}
                onClick={() => setMenuOpen(v => !v)}
                disabled={triggerDisabled}
                style={triggerClass ? {
                    opacity: triggerDisabled ? 0.45 : 1,
                    cursor: triggerDisabled ? 'not-allowed' : 'pointer',
                } : {
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    fontSize: 13, fontWeight: 500, width: 'auto',
                    opacity: triggerDisabled ? 0.45 : 1,
                    cursor: triggerDisabled ? 'not-allowed' : 'pointer',
                }}
                title={triggerDisabled
                    ? (pickedUpAt ? 'Order picked up — no further action' : 'No actions available')
                    : 'Fulfillment actions'}
            >
                {triggerClass
                    ? <>Fulfillment<svg viewBox="0 0 20 20" width="12" height="12" fill="currentColor" aria-hidden="true" style={{ color: 'var(--v3-muted)' }}><path d="M5.5 7.5 10 12l4.5-4.5-1-1L10 10 6.5 6.5z"/></svg></>
                    : 'Fulfillment ▾'}
            </button>
            {fbmRates && (
                <FbmRatePicker open rates={fbmRates.rates} amazonTier={fbmRates.amazon_tier} onClose={() => setFbmRates(null)}
                    onPick={async (rate) => {
                        const d = fbmRates; setFbmRates(null); setBusy(true); setError(null);
                        try { openBuyAndPrint(d, rate); await finishAfterOpen(d.beforeIds); }
                        catch (e) { setError(e?.message || 'Network error'); }
                        finally { setBusy(false); }
                    }} />
            )}
            {menuOpen && (
                <div className={triggerClass ? 'odv3-menu-pop' : 'fo-ellipsis-menu'}>
                    {showPrintShipping && menuItem({ onClick: handlePrintShipping, icon: 'label',
                        label: 'Fulfill & Print Label', desc: 'Buy the shipping label and mark these items fulfilled' })}
                    {showFulfillNoLabel && menuItem({ onClick: handleFulfillNoLabel, icon: 'check',
                        label: isNoShipping ? 'Mark as Fulfilled' : 'Fulfill Without Shipping Label',
                        desc: 'Mark fulfilled; label handled elsewhere' })}
                    {showTransfer && menuItem({ onClick: handleTransferPickup, icon: 'pin',
                        label: 'Transfer To Pickup Location', desc: 'Send these items to the pickup store' })}
                    {showOverrideTransfer && menuItem({ onClick: handleTransferOverridePickup, icon: 'pin',
                        label: `Transfer to Override Pickup${overrideName ? ` (${overrideName})` : ''}`,
                        desc: 'Send these items to the overridden pickup store' })}
                    {showReadyForPickup && menuItem({ onClick: handleReadyForPickup, icon: 'check',
                        label: 'Mark Ready For Pickup', desc: 'These items are ready for the customer' })}
                    {showMarkPickedUp && menuItem({ onClick: handleMarkPickedUp, icon: 'check',
                        label: 'Mark as Picked Up', desc: 'The customer has collected these items' })}
                    {showChangeLocation && (
                        <>
                            {triggerClass && (showPrintShipping || showFulfillNoLabel || showTransfer || showOverrideTransfer || showReadyForPickup || showMarkPickedUp) && <div className="odv3-mi-div"/>}
                            {menuItem({ onClick: openChangeLoc, icon: 'pin',
                                label: 'Change Location…', desc: 'Move this fulfillment to another location' })}
                        </>
                    )}
                    {!!cancelMode && (
                        <>
                            {triggerClass && <div className="odv3-mi-div"/>}
                            {menuItem({ onClick: openCancelFulfillment, icon: 'x', danger: true,
                                label: cancelMode === 'pickup' ? 'Cancel Pickup…' : 'Cancel Fulfillment…',
                                desc: 'Undo this fulfillment', plainDisabled: false })}
                        </>
                    )}
                    {!showPrintShipping && !showFulfillNoLabel && !showTransfer && !showReadyForPickup && !showMarkPickedUp && !showChangeLocation && !showOverrideTransfer && !cancelMode && (
                        <div className="fo-ellipsis-item" style={{ color: '#6b7280', cursor: 'default' }}>
                            No actions available
                        </div>
                    )}
                </div>
            )}
            {error && (
                <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, padding: '6px 10px', background: '#fee2e2', color: '#991b1b', borderRadius: 6, fontSize: 12, whiteSpace: 'nowrap', zIndex: 1000 }}>
                    {error}
                </div>
            )}
            {busy && (
                <div style={{
                    position: 'fixed', inset: 0,
                    background: 'rgba(15,23,42,0.55)',
                    zIndex: 9999,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <div style={{
                        background: '#ffffff',
                        padding: '36px 56px',
                        borderRadius: 14,
                        textAlign: 'center',
                        minWidth: 360,
                        boxShadow: '0 18px 50px rgba(15,23,42,0.35)',
                    }}>
                        <div style={{
                            width: 48, height: 48,
                            border: '4px solid #e5e7eb',
                            borderTopColor: '#2563eb',
                            borderRadius: '50%',
                            margin: '0 auto 20px',
                            animation: 'fulfillSpin 0.8s linear infinite',
                        }} />
                        <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>
                            {busyLabel}
                        </div>
                        <div style={{ fontSize: 13, color: '#6b7280', marginTop: 10 }}>
                            {busySec}s
                        </div>
                        <style>{`@keyframes fulfillSpin { to { transform: rotate(360deg); } }`}</style>
                    </div>
                </div>
            )}
            {/* Change Location modal — Shopify Admin-style location picker.
                Fires POST /api/orders/{id}/change-fo-location. Two modes
                via the "Also move physical inventory" toggle:
                  OFF (default) — FO-only reassign, on_hand unchanged.
                  ON            — inventoryTransferCreate + FO move,
                                  on_hand physically moves. */}
            {changeLocOpen && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
                    zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }} onClick={() => !changeLocSubmitting && setChangeLocOpen(false)}>
                    <div onClick={(e) => e.stopPropagation()} style={{
                        background: '#fff', borderRadius: 12, minWidth: 720, maxWidth: 820,
                        padding: '20px 24px 16px', boxShadow: '0 24px 60px rgba(15,23,42,0.35)',
                        // 2026-09-18 (Eric): the modal is a DOM child of the V2 fulfillment
                        // header cell, which is white-space:nowrap / text-align:right —
                        // reset both so the panels wrap inside the modal instead of
                        // running off the right edge.
                        whiteSpace: 'normal', textAlign: 'left',
                    }}>
                        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 4 }}>Change location</div>
                        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 14 }}>
                            Currently at{' '}
                            <strong>{fo?.assignedLocation?.name || fo?.assignedLocation?.location?.name || 'Unknown'}</strong>.
                        </div>
                        {/* Two-panel selector — click a panel to choose the
                            mode. Selected panel = pastel green highlight.
                            Default: LEFT (routing-only) so operator must
                            explicitly click the RIGHT panel to move stock. */}
                        {(() => {
                            const currentLocName = fo?.assignedLocation?.name
                                                    || fo?.assignedLocation?.location?.name
                                                    || 'the current location';
                            const panelStyle = (selected) => ({
                                flex: 1,
                                padding: '12px 14px',
                                border: `2px solid ${selected ? '#66c184' : '#e2e8f0'}`,
                                background: selected ? '#e6f7ec' : '#f8fafc',
                                borderRadius: 8,
                                cursor: changeLocSubmitting ? 'default' : 'pointer',
                                transition: 'background 0.12s, border-color 0.12s',
                            });
                            const titleStyle = (selected) => ({
                                fontSize: 13, fontWeight: 700,
                                color: selected ? '#166534' : '#111827',
                                marginBottom: 6,
                                textTransform: 'uppercase',
                                letterSpacing: 0.04,
                                fontSize: 12,
                            });
                            const bodyStyle = { fontSize: 12.5, color: '#374151', lineHeight: 1.5 };
                            return (
                                <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                                    <div
                                        role="radio"
                                        aria-checked={!changeLocMoveInventory}
                                        style={panelStyle(!changeLocMoveInventory)}
                                        onClick={() => !changeLocSubmitting && setChangeLocMoveInventory(false)}>
                                        <div style={titleStyle(!changeLocMoveInventory)}>
                                            Do NOT move inventory <span style={{ fontWeight:500, color:'#6b7280', textTransform:'none', letterSpacing:0 }}>(default)</span>
                                        </div>
                                        <div style={bodyStyle}>
                                            Reassign the product fulfillment to the destination location.
                                            <code> On hand</code> does <strong>NOT</strong> move. No physical
                                            inventory transfer happens. If there are 10 of an item at the
                                            current and destination location, and you are moving a single
                                            item, then there will still be 10 at each location, but now the
                                            destination location has 1 unit committed instead of the source.
                                        </div>
                                    </div>
                                    <div
                                        role="radio"
                                        aria-checked={changeLocMoveInventory}
                                        style={panelStyle(changeLocMoveInventory)}
                                        onClick={() => !changeLocSubmitting && setChangeLocMoveInventory(true)}>
                                        <div style={titleStyle(changeLocMoveInventory)}>
                                            Move inventory
                                        </div>
                                        <div style={bodyStyle}>
                                            Reassign the product fulfillment <strong>AND</strong> physically
                                            move the stock. <code>On hand</code> at <strong>{currentLocName}</strong>{' '}
                                            decreases and <code>on hand</code> at the destination increases for
                                            each item on the FO. If there are 10 of an item at the current and
                                            destination location, and you are moving a single item, then the
                                            current will have <strong>9</strong> and the destination will have
                                            <strong> 11</strong>.
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                        <input
                            type="text"
                            placeholder="Search locations…"
                            value={changeLocSearch}
                            onChange={(e) => setChangeLocSearch(e.target.value)}
                            style={{
                                width: '100%', padding: '8px 10px', border: '1px solid #d1d5db',
                                borderRadius: 6, fontSize: 14, marginBottom: 10,
                            }}
                            autoFocus
                        />
                        <div style={{
                            maxHeight: 260, overflowY: 'auto', border: '1px solid #e5e7eb',
                            borderRadius: 6,
                        }}>
                            {filteredChangeLocs.length === 0 ? (
                                <div style={{ padding: 12, color: '#6b7280', fontSize: 13 }}>
                                    {changeLocList.length === 0 ? 'Loading…' : 'No locations match.'}
                                </div>
                            ) : filteredChangeLocs.map(l => {
                                const isSuggested = suggestedDestLocId
                                                    && String(l.shopify_location_id) === String(suggestedDestLocId);
                                return (
                                <label key={l.shopify_location_id}
                                    style={{
                                        display: 'flex', gap: 10, padding: '8px 12px',
                                        borderBottom: '1px solid #f3f4f6', cursor: 'pointer',
                                        alignItems: 'center', fontSize: 14,
                                        background: String(changeLocSelected) === String(l.shopify_location_id)
                                                    ? '#eff6ff' : 'transparent',
                                    }}>
                                    <input type="radio"
                                        name={`change-loc-${fo?.id}`}
                                        value={l.shopify_location_id}
                                        checked={String(changeLocSelected) === String(l.shopify_location_id)}
                                        onChange={() => setChangeLocSelected(l.shopify_location_id)} />
                                    <span>{l.name}</span>
                                    {isSuggested && (
                                        <span style={{
                                            marginLeft: 6, padding: '1px 8px', fontSize: 11, fontWeight: 600,
                                            borderRadius: 999, background: '#dbeafe', color: '#1e40af',
                                        }}>
                                            Suggested
                                        </span>
                                    )}
                                </label>
                                );
                            })}
                        </div>
                        {changeLocError && (
                            <div style={{ marginTop: 10, padding: '8px 10px', background: '#fee2e2',
                                          color: '#991b1b', borderRadius: 6, fontSize: 13 }}>
                                {changeLocError}
                            </div>
                        )}
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
                            <button onClick={() => setChangeLocOpen(false)}
                                disabled={changeLocSubmitting}
                                style={{ padding: '7px 14px', border: '1px solid #d1d5db',
                                         borderRadius: 6, background: '#fff', cursor: 'pointer',
                                         fontSize: 13, fontWeight: 600 }}>
                                Cancel
                            </button>
                            <button onClick={submitChangeLoc}
                                disabled={changeLocSubmitting || !changeLocSelected}
                                style={{ padding: '7px 14px', border: 'none', borderRadius: 6,
                                         background: !changeLocSelected || changeLocSubmitting ? '#94a3b8' : '#111827',
                                         color: '#fff', cursor: (!changeLocSelected || changeLocSubmitting) ? 'not-allowed' : 'pointer',
                                         fontSize: 13, fontWeight: 600 }}>
                                {changeLocSubmitting
                                    ? (changeLocMoveInventory ? 'Moving inventory & FO…' : 'Changing…')
                                    : (changeLocMoveInventory ? 'Move inventory & change location' : 'Change location')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {pickedUpOpen && (() => {
                const storeName = currentLocName || 'this location';
                const itemCount = (fo?.lineItems?.edges || []).reduce((s, e) => s + (Number(e?.node?.totalQuantity) || 1), 0);
                return (
                    <Modal
                        open
                        onClose={() => setPickedUpOpen(false)}
                        title="Mark as Picked Up"
                        primaryAction={{ content: 'Mark as Picked Up', onAction: submitMarkPickedUp }}
                        secondaryActions={[{ content: 'Cancel', onAction: () => setPickedUpOpen(false) }]}
                    >
                        <Modal.Section>
                            <BlockStack gap="300">
                                <Text as="p">
                                    Marks this whole fulfillment ({itemCount} {itemCount === 1 ? 'item' : 'items'}) as picked up at <strong>{storeName}</strong> and closes it in Shopify.
                                    {!pickupReadyAt && ' It was never marked Ready For Pickup — that step is skipped.'}
                                </Text>
                                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                                    <input
                                        id="fo-picked-up-notify"
                                        type="checkbox"
                                        checked={pickedUpNotify}
                                        onChange={(e) => setPickedUpNotify(e.target.checked)}
                                        style={{ marginTop: 3 }}
                                    />
                                    <span>
                                        <div style={{ fontWeight: 600 }}>Notify the customer</div>
                                        <div style={{ fontSize: 12.5, color: '#6b7280' }}>Sends Shopify's "Picked up" email. Off by default — leave it off when the customer is standing in front of you, or you are cleaning up pickups.</div>
                                    </span>
                                </label>
                            </BlockStack>
                        </Modal.Section>
                    </Modal>
                );
            })()}
            {cancelOpen && (() => {
                const panel = (selected) => ({
                    flex: 1, padding: '12px 14px', borderRadius: 8,
                    border: `2px solid ${selected ? '#66c184' : '#e2e8f0'}`,
                    background: selected ? '#e6f7ec' : '#f8fafc',
                    cursor: cancelSubmitting ? 'default' : 'pointer',
                });
                const title = (selected) => ({ fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: 0.04, marginBottom: 6, color: selected ? '#166534' : '#111827' });
                const body = { fontSize: 12.5, color: '#374151', lineHeight: 1.5 };
                const dflt = <span style={{ fontWeight: 500, color: '#6b7280', textTransform: 'none', letterSpacing: 0 }}>(default)</span>;
                const storeName = currentLocName || 'the current location';
                const sourceName = cancelLocNames[String(cancelTransferredFrom)] || `location ${cancelTransferredFrom}`;
                const fulfilledAt = fulfillment?.createdAt ? new Date(fulfillment.createdAt).toLocaleString() : '';
                // FO line-item count — same field (`totalQuantity` on each
                // fo.lineItems.edges[].node) used for the per-FO quantity
                // remap elsewhere in this file (see foQtyByParentId above).
                const itemCount = (fo?.lineItems?.edges || []).reduce((s, e) => s + (Number(e?.node?.totalQuantity) || 1), 0);
                const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 };
                const isPickupMode = cancelMode === 'pickup';
                const isTransferMode = cancelMode === 'transfer';
                const transferredAt = fo?._pickupState?.transferred_at || null;
                return (
                    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 10000,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                         onClick={() => !cancelSubmitting && setCancelOpen(false)}>
                        <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, minWidth: 720, maxWidth: 820,
                                  padding: '20px 24px 16px', boxShadow: '0 24px 60px rgba(15,23,42,0.35)' }}>
                            <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 4 }}>{isPickupMode ? 'Cancel pickup' : 'Cancel fulfillment'}</div>
                            {isPickupMode ? (
                                <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 14 }}>
                                    {transferredAt ? `Transferred to ${storeName} on ${new Date(transferredAt).toLocaleDateString()}` : `At ${storeName}`}
                                    {pickupReadyAt ? ` · Ready for pickup since ${new Date(pickupReadyAt).toLocaleDateString()}` : ''}
                                    {` · ${itemCount} item${itemCount === 1 ? '' : 's'}.`}
                                    {' '}Shopify has no way to un-mark Ready for Pickup, so OpsHub marks the order picked up and cancels that fulfillment right away (no customer email). Picked, pressed and packed marks stay as they are.
                                </div>
                            ) : isTransferMode ? (
                                <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 14 }}>
                                    {`Transferred to ${storeName}${transferredAt ? ` on ${new Date(transferredAt).toLocaleDateString()}` : ''} · ${itemCount} item${itemCount === 1 ? '' : 's'}. The fulfillment order goes back to ${sourceName}.`}
                                </div>
                            ) : (
                                <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 14 }}>
                                    Fulfilled at <strong>{storeName}</strong>{fulfilledAt ? ` on ${fulfilledAt}` : ''} · {itemCount} item{itemCount === 1 ? '' : 's'}. The order reopens in Shopify; picked, pressed and packed marks stay as they are.
                                </div>
                            )}

                            {cancelMode === 'fulfilled' && cancelHasLabel && (
                                <div style={{ marginBottom: 14 }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.04, marginBottom: 6 }}>Shipping label</div>
                                    <div style={panel(true)}>
                                        <div style={title(true)}>The label will be voided</div>
                                        <div style={body}>Shopify refunds the postage. Only possible while the carrier hasn't scanned the package. If Shopify refuses, nothing is cancelled: cancel it from Shopify admin instead.</div>
                                    </div>
                                    {cancelVoidLabel && (
                                        <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>Reason for voiding</div>
                                                <select value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} style={inputStyle} disabled={cancelSubmitting}>
                                                    <option value="">Select</option>
                                                    {VOID_REASON_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                                </select>
                                            </div>
                                            {cancelReason === 'OTHER' && (
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>Description</div>
                                                    <input type="text" value={cancelDesc} onChange={(e) => setCancelDesc(e.target.value)} style={inputStyle} disabled={cancelSubmitting} />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {isPickupMode ? (
                                <div style={{ fontSize: 13, color: '#374151', marginBottom: 14 }}>
                                    {`Stays at ${storeName}. Ready for Pickup is cleared${cancelTransferredFrom ? `; use Cancel Fulfillment afterwards to move it back to ${sourceName}` : ''}.`}
                                </div>
                            ) : isTransferMode ? (
                                <div style={{ marginBottom: 14 }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.04, marginBottom: 6 }}>Move back to {sourceName}</div>
                                    <div style={{ display: 'flex', gap: 10 }}>
                                        <div role="radio" aria-checked={!cancelMoveInv} style={panel(!cancelMoveInv)}
                                             onClick={() => !cancelSubmitting && setCancelMoveInv(false)}>
                                            <div style={title(!cancelMoveInv)}>Move the FO back — do NOT move inventory (default)</div>
                                            <div style={body}>
                                                Reassign the product fulfillment to the destination location.
                                                <code> On hand</code> does <strong>NOT</strong> move. No physical
                                                inventory transfer happens. If there are 10 of an item at the
                                                current and destination location, and you are moving a single
                                                item, then there will still be 10 at each location, but now the
                                                destination location has 1 unit committed instead of the source.
                                            </div>
                                        </div>
                                        <div role="radio" aria-checked={cancelMoveInv} style={panel(cancelMoveInv)}
                                             onClick={() => !cancelSubmitting && setCancelMoveInv(true)}>
                                            <div style={title(cancelMoveInv)}>Move the product with the FO — move inventory</div>
                                            <div style={body}>
                                                Reassign the product fulfillment <strong>AND</strong> physically
                                                move the stock. <code>On hand</code> at <strong>{storeName}</strong>{' '}
                                                decreases and <code>on hand</code> at the destination increases for
                                                each item on the FO. If there are 10 of an item at the current and
                                                destination location, and you are moving a single item, then the
                                                current will have <strong>9</strong> and the destination will have
                                                <strong> 11</strong>.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                cancelWasTransferred && (
                                <div style={{ marginBottom: 14 }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.04, marginBottom: 6 }}>Location</div>
                                    <div style={{ display: 'flex', gap: 10 }}>
                                        <div role="radio" aria-checked={!cancelRevert} style={panel(!cancelRevert)}
                                             onClick={() => !cancelSubmitting && setCancelRevert(false)}>
                                            <div style={title(!cancelRevert)}>Leave at {storeName} {dflt}</div>
                                            <div style={body}>The reopened fulfillment stays at <strong>{storeName}</strong>. Inventory is not touched.</div>
                                        </div>
                                        <div role="radio" aria-checked={cancelRevert} style={panel(cancelRevert)}
                                             onClick={() => !cancelSubmitting && setCancelRevert(true)}>
                                            <div style={title(cancelRevert)}>Move back to {sourceName}</div>
                                            <div style={body}>The reopened fulfillment is moved back to <strong>{sourceName}</strong>, the location it was transferred from. Choose below whether stock moves with it.</div>
                                        </div>
                                    </div>
                                    {cancelRevert && (
                                        <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                                            <div role="radio" aria-checked={!cancelMoveInv} style={panel(!cancelMoveInv)}
                                                 onClick={() => !cancelSubmitting && setCancelMoveInv(false)}>
                                                <div style={title(!cancelMoveInv)}>Do NOT move inventory {dflt}</div>
                                                <div style={body}>
                                                    Reassign the product fulfillment to the destination location.
                                                    <code> On hand</code> does <strong>NOT</strong> move. No physical
                                                    inventory transfer happens. If there are 10 of an item at the
                                                    current and destination location, and you are moving a single
                                                    item, then there will still be 10 at each location, but now the
                                                    destination location has 1 unit committed instead of the source.
                                                </div>
                                            </div>
                                            <div role="radio" aria-checked={cancelMoveInv} style={panel(cancelMoveInv)}
                                                 onClick={() => !cancelSubmitting && setCancelMoveInv(true)}>
                                                <div style={title(cancelMoveInv)}>Move inventory</div>
                                                <div style={body}>
                                                    Reassign the product fulfillment <strong>AND</strong> physically
                                                    move the stock. <code>On hand</code> at <strong>{storeName}</strong>{' '}
                                                    decreases and <code>on hand</code> at the destination increases for
                                                    each item on the FO. If there are 10 of an item at the current and
                                                    destination location, and you are moving a single item, then the
                                                    current will have <strong>9</strong> and the destination will have
                                                    <strong> 11</strong>.
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                )
                            )}

                            {cancelError && (
                                <div style={{ marginTop: 4, padding: '8px 10px', background: '#fee2e2', color: '#991b1b', borderRadius: 6, fontSize: 13 }}>{cancelError}</div>
                            )}
                            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
                                <button onClick={() => setCancelOpen(false)} disabled={cancelSubmitting}
                                    style={{ padding: '7px 14px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                                    {cancelMode === 'fulfilled' ? 'Keep fulfillment' : 'Keep as is'}
                                </button>
                                <button onClick={submitCancelFulfillment} disabled={cancelSubmitting}
                                    style={{ padding: '7px 14px', border: 'none', borderRadius: 6, background: cancelSubmitting ? '#94a3b8' : '#b91c1c',
                                             color: '#fff', cursor: cancelSubmitting ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}>
                                    {cancelSubmitting ? 'Cancelling…' : (isPickupMode ? 'Cancel pickup' : 'Cancel fulfillment')}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}

// ── Removed-items card ───────────────────────────────────────────────────────
// Rendered below the FO cards on OrderDetail. Captures line items whose
// originalQuantity exceeds their current quantity — i.e., refunded with
// restock, or removed via Shopify's Order Editing flow. Without this card
// the operator could see a Shopify-side removal but never see it on OpsHub.
// Mirrors the FO-card chrome (header + items table) so it slots cleanly
// into the same column.
function RemovedItemsCard({ orderLineItems, splitBundleMap = {}, portalState = {}, orderTags = null }) {
    const removed = (orderLineItems || [])
        .filter(li => {
            const orig = li.originalQuantity ?? li.quantity ?? 0;
            const cur  = li.quantity || 0;
            return orig > cur && (orig - cur) > 0;
        })
        // Hide invoice-only virtual lines (Free Returns + Package Protection /
        // Processing Fee / TPHF) — they're never physically removed in any
        // meaningful sense.
        .filter(li => !isVirtual(li.title))
        .map(li => {
            const orig = li.originalQuantity ?? li.quantity ?? 0;
            const cur  = li.quantity || 0;
            const removedQty = orig - cur;
            // ItemsTable's BundleRows/LineItemRow render `li.quantity` for the
            // qty column. Set it to the REMOVED count for this card so the
            // operator reads "this many were taken off the order".
            return { ...li, quantity: removedQty, currentQuantity: removedQty };
        });

    if (removed.length === 0) return null;

    const totalRemoved = removed.reduce((s, li) => s + (li.quantity || 0), 0);

    return (
        <div className="card" style={{ border: '1px solid #fca5a5' }}>
            <div className="fo-card-header" style={{ backgroundColor: '#fef2f2' }}>
                <span className="fo-pick-badge fo-pick-badge--unpicked" style={{ background: '#fecaca', color: '#7f1d1d' }}>
                    REMOVED / REFUNDED
                </span>
                <span className="fo-card-location" style={{ color: '#7f1d1d' }}>
                    {totalRemoved} item{totalRemoved === 1 ? '' : 's'} taken off the order
                </span>
            </div>
            <div className="fo-card-items-wrap">
                <ItemsTable
                    lineItems={removed}
                    sortMode="order"
                    splitBundleMap={splitBundleMap}
                    portalState={portalState}
                    orderTags={orderTags}
                />
            </div>
        </div>
    );
}


function FulfillmentOrderCard({ fo, fulfillment, orderId, orderName = '', orderLineItems, onFulfilled, storedLabel, orderCancelled, splitBundleMap = {}, portalState = {}, onPortalStateRefresh = null, storePickupOverride = '', displayMode = 'legacy', displayLineItems = null, shippedLineItemIds = null, orderTags = null, siblingFos = [], tpOverridesByName = null, tpReqByLiId = null, bulkShipmentLabels = [], onToast = null, fbmMoved = false, shippedQtyByLineItem = {} }) {
    const [trackingNumber, setTracking]   = useState('');
    const [carrier, setCarrier]           = useState('UPS');
    const [fulfilling, setFulfilling]     = useState(false);
    const [actionMsg, setActionMsg]       = useState(null);
    const [actionError, setActionError]   = useState(null);
    const [showLabelModal, setShowLabel]  = useState(false);
    const [sortMode, setSortMode]         = useState('order');

    const { user } = useAuth();
    const { activeStore } = useStore();
    const storeFetch = useAuthFetch();
    const [ellipsisOpen, setEllipsisOpen] = useState(false);
    const [selectMode, setSelectMode] = useState(null); // 'pick'|'unpick'|'press'|'unpress'
    const [selectedKeys, setSelectedKeys] = useState(new Set());
    const [markSubmitting, setMarkSubmitting] = useState(false);
    const ellipsisRef = useRef(null);

    // Close ellipsis menu on outside click
    useEffect(() => {
        if (!ellipsisOpen) return;
        const handler = (e) => {
            if (ellipsisRef.current && !ellipsisRef.current.contains(e.target)) setEllipsisOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [ellipsisOpen]);

    // displayMode lets the parent split a single FO into multiple cards
    // (one per Fulfillment + one for remaining unfulfilled qty), mirroring
    // Shopify Admin. 'fulfilled' cards are always rendered as Closed/Done
    // even when the underlying FO is still IN_PROGRESS (because some other
    // batch is still pending). 'pending' uses fo.status as before.
    const isOpen = displayMode === 'fulfilled'
        ? false
        : (fo.status === 'OPEN' || fo.status === 'IN_PROGRESS');
    const pickup = isPickupMethod(fo.deliveryMethod?.methodType);
    const isShipping = (fo.deliveryMethod?.methodType || '').toUpperCase() === 'SHIPPING';

    // Pickup workflow state: transfer → mark-ready → mark-picked-up.
    // Honor the order-level store_pickup_override metafield: when set, the
    // override is the destination store the customer will pick up from, NOT
    // Shopify's original deliveryMethod.presentedName. Without this fall-back,
    // the green chip on the FO header (and the at-pickup-location check
    // downstream) keeps reading the original store even after an admin sets
    // the override via Tools → Override Pickup Location.
    const pickupLocationName = (storePickupOverride || '').trim()
        || fo.deliveryMethod?.presentedName
        || '';
    const assignedLocationName = fo.assignedLocation?.name || '';
    const atPickupLocation = pickup && pickupLocationName && assignedLocationName === pickupLocationName;
    const pickupReadyAt = fo._pickupState?.ready_for_pickup_at || null;
    // Full 3-state pickup machine restored: transfer → mark-ready → mark-picked-up.
    // The ready_for_pickup_at transition is still tracked (so Store Status can
    // show "Ready For Pickup" when Shopify POS marks it), but the UI button
    // for that step is intentionally hidden in the action-area render below —
    // the operator at the destination store does that mark in Shopify POS,
    // not from OrderDetail. This keeps the state machine consistent for
    // orders that DO get RFP'd via the webhook path.
    const pickupStep = !pickup ? null
        : !atPickupLocation ? 'transfer'
        : !pickupReadyAt     ? 'mark-ready'
        :                      'mark-picked-up';

    // Use order-level lineItems (correct positional order) filtered by this FO's membership.
    // fo.lineItems.edges only has { lineItem: { id } } after the API fix.
    //
    // When the parent provides `displayLineItems` (the per-card subset for
    // the new Fulfillment-expanded model), use it directly. Otherwise fall
    // back to FO-level membership: for OPEN/IN_PROGRESS FOs drop edges with
    // remainingQuantity===0 (those have been moved to another FO and
    // fulfilled there but Shopify keeps stale refs); CLOSED FOs keep all
    // edges as the historical fulfilled set.
    const useDisplayList = Array.isArray(displayLineItems);
    const foEdgesForDisplay = (fo.lineItems?.edges || []).filter(e => {
        if (!isOpen) return true;
        return (e.node?.remainingQuantity || 0) > 0;
    });
    const foLineItemIds = new Set(
        foEdgesForDisplay.map(e => e.node.lineItem?.id).filter(Boolean)
    );
    // Compute instance indices over the WHOLE ORDER's line items (not the
    // FO-filtered subset) so pack_instances keys stay globally-consistent. A
    // rework entry keyed '2' on the order's 2nd sock must still match when that
    // sock renders alone inside its own FO card.
    const orderInstanceIndexMap = (() => {
        const map = {};
        const skuCounts = {}; const skuCumQty = {};
        const grouped = groupLineItems(orderLineItems || [], orderTags);
        // Per-FO bundle attribution: when two bundle parents with the same
        // child SKU split shipping (one parent shipped, one pending), the
        // FIRST instance of the child SKU gets the picked credit. Iterate
        // SHIPPED parents first so shipped instances claim lower indices
        // and the picked-count lights up under the correct parent. Without
        // this, the pending parent's child renders as picked and the
        // shipped parent's renders as unpicked (SURF-59194 case).
        const orderedGroups = (() => {
            if (!shippedLineItemIds || shippedLineItemIds.size === 0) return grouped;
            const shipped = [], pending = [];
            for (const g of grouped) {
                (shippedLineItemIds.has(g.item.id) ? shipped : pending).push(g);
            }
            return [...shipped, ...pending];
        })();
        const countSku = (sku, id, qty) => {
            const k = sku || `__no_sku__${id}`;
            skuCounts[k] = (skuCounts[k] || 0) + 1;
            const offset = skuCumQty[k] || 0;
            skuCumQty[k] = offset + qty;
            return { idx: skuCounts[k], offset };
        };
        for (const { item, children } of orderedGroups) {
            const parent = countSku(item.sku, item.id, item.quantity || 1);
            const childResults = children.map(c => countSku(c.sku, c.id, c.quantity || 1));
            map[item.id] = {
                parentSkuIdx:    parent.idx,
                parentSkuOffset: parent.offset,
                childSkuIndices: childResults.map(r => r.idx),
                childSkuOffsets: childResults.map(r => r.offset),
            };
        }
        return map;
    })();
    // Per-FO quantity per parent OrderLineItem.id. Shopify's "split fulfillment"
    // creates two FOs that share the SAME parent lineItem.id (e.g. line "L size
    // qty 10" splits into FO-A totalQuantity=8 and FO-B totalQuantity=2). Without
    // this remap, both FO cards render the parent quantity (10) and look like
    // duplicates. Use the FO's foLineItem.totalQuantity for THIS card.
    const foQtyByParentId = (() => {
        const m = {};
        foEdgesForDisplay.forEach(e => {
            const n = e.node;
            const pid = n.lineItem?.id;
            if (pid) {
                // For OPEN/IN_PROGRESS FOs, sum remainingQuantity (what's
                // still owed by this FO). For CLOSED FOs, keep totalQuantity
                // since remainingQuantity is 0 once fulfilled.
                const q = isOpen
                    ? (n.remainingQuantity || 0)
                    : (n.totalQuantity || 0);
                m[pid] = (m[pid] || 0) + q;
            }
        });
        return m;
    })();
    const lineItems = (useDisplayList
        ? displayLineItems
        : (foLineItemIds.size > 0
            ? orderLineItems
                .filter(li => foLineItemIds.has(li.id))
                .map(li => {
                    const foQty = foQtyByParentId[li.id];
                    if (foQty == null || foQty === li.quantity) return li;
                    return { ...li, quantity: foQty, currentQuantity: foQty };
                })
            : orderLineItems)
    ).filter(li => !isVirtual(li.title));  // hide Processing Fee, Free Returns + Package Protection

    // FO is "done" = items physically left the warehouse = items necessarily
    // picked + pressed. Three signals count:
    //   1. CLOSED FO with at least one SUCCESS fulfillment (shipped or
    //      picked-up via Shopify-Admin/POS — `fulfillments` is on the GraphQL
    //      response).
    //   2. Per-FO transferred_at on `fo._pickupState` (transferred to a
    //      pickup destination — items left the source warehouse even though
    //      Shopify shows the destination FO as IN_PROGRESS until pickup).
    //   3. Per-FO ready_for_pickup_at / picked_up_at on `fo._pickupState`
    //      (RFP'd or already collected — items definitely left).
    // Without these signals the per-FO foPickStats / foPressStats chips read
    // item_state directly and stay at 0/N even though the FO clearly shipped.
    const foPickupState = fo._pickupState || {};
    // Warehouse pickup workflow (2026-06-29): BE sets fo._warehousePickupOpen
    // when the FO is at a Warehouse-named loc + fulfilled within 14 days. In
    // that window the FE should treat the FO as still open (picker hasn't
    // physically scanned), not as the workflow-invariant "FULLY PICKED".
    // 2026-07-27: transferred_at only implies "items left the source" when
    // the transfer destination is a retail store (pickup transfer). A
    // warehouse→warehouse transfer, or a transfer followed by a subsequent
    // move to another loc, does NOT mean the work is done. Gating on
    // dest_is_store mirrors the backend's _xfer_is_pickup check at
    // app.py:8180. SP-324941 canonical: transferred Lancaster→Tinton Falls
    // Warehouse then moved to Rochester — nothing picked, was rendering
    // FULLY PICKED / FULLY PRESSED.
    // The `dest_is_store === false` check is deliberately strict — if the
    // backend hasn't been redeployed yet (`dest_is_store` will be undefined)
    // we KEEP the old behavior and treat any transferred_at as pickup, so
    // rolling out the FE ahead of the BE doesn't regress legitimate pickup
    // transfers. Only DOWNGRADE the invariant when the backend has
    // affirmatively told us the destination is not a store.
    const _xferLooksLikePickup =
        !!foPickupState.transferred_at && foPickupState.dest_is_store !== false;
    // 2026-07-30: single source of truth is the backend-computed
    // effective_state (see compute_fo_effective_state in database.py).
    // state >= 1 means items are done being worked at source
    // (transferred, RFP, or picked up) → workflow-invariant "shipped"
    // for picked/packed=True chip rendering. Timestamp-based fallbacks
    // removed — they lit up stale rfp/pua from split+merge inheritance
    // and rendered OPEN FOs at source as FULLY PICKED (SP-258657
    // canonical: OPEN FO 8085781 at Fair Oaks source had stale rfp
    // from the historical cycle, foIsClosedShipped=true, card showed
    // FULLY PICKED even though items were pending).
    const _foEffectiveState = foPickupState.effective_state;
    // Label-first warehouses (Tinton Falls + Eatontown): the FO closes with
    // fulfilledAt the moment the label prints, but that's just paperwork —
    // no items have been picked. Suppress the shipped-FO=picked invariant
    // here so item badges + FO-card FULLY PICKED stay off until the picker
    // actually scans. Mirrors the backend _filtered_counts_for_loc gate.
    const _foAtLabelFirst = isLabelFirstWarehouse(fo?.assignedLocation?.location?.id?.split('/').pop());
    const foIsClosedShipped =
        !_foAtLabelFirst
        && !fo._warehousePickupOpen && (
            (typeof _foEffectiveState === 'number' && _foEffectiveState >= 1)
            || displayMode === 'fulfilled'
        );
    const foPortalState = (() => {
        const liNum = (li) => String(li?.id || '').split('/').pop();
        if (foIsClosedShipped) {
            const skuToFoQty = {};
            for (const li of lineItems) {
                if (!li.sku) continue;
                skuToFoQty[li.sku] = (skuToFoQty[li.sku] || 0) + (li.quantity || 0);
            }
            const out = { ...portalState };
            for (const [sku, qty] of Object.entries(skuToFoQty)) {
                if (qty <= 0) continue;
                out[sku] = {
                    ...(portalState[sku] || {}),
                    qty_total:   qty,
                    qty_picked:  qty,
                    qty_pressed: qty,
                };
            }
            // 2026-10-22 (SURF-93865): the per-line-item picked count (psForSku prefers it) was left at the
            // event-log value, so the second card of a line shipped 7 + 15 read "7/15 Picked" on a shipped
            // card. A shipped card's units are picked by definition, per line item as well as per SKU.
            if (portalState._byLineItem) {
                out._byLineItem = { ...portalState._byLineItem };
                for (const li of lineItems) {
                    const id = liNum(li);
                    if (id && (li.quantity || 0) > 0) out._byLineItem[id] = { ...(out._byLineItem[id] || {}), qty_picked: li.quantity };
                }
            }
            return out;
        }
        // 2026-10-22 (SURF-93865): an OPEN card of a line that partly shipped from another card must not
        // count the shipped units as its own picks: take them off the per-line-item picked count.
        if (portalState._byLineItem) {
            let changed = false;
            const byLi = { ...portalState._byLineItem };
            for (const li of lineItems) {
                const id = liNum(li);
                const shipped = (shippedQtyByLineItem || {})[id] || 0;
                if (!id || shipped <= 0 || !byLi[id]) continue;
                byLi[id] = { ...byLi[id], qty_picked: Math.max(0, (byLi[id].qty_picked || 0) - shipped) };
                changed = true;
            }
            if (changed) portalState = { ...portalState, _byLineItem: byLi };
        }
        // OPEN FO: when the backend says nothing's picked or pressed at this
        // FO, zero out qty_picked / qty_pressed on portalState entries for
        // this FO's line item SKUs (and their split-key children) so the
        // per-line-item badges read UNPICKED / not pressed. Otherwise an
        // embellishment scan or cascade-lifted parent value bleeds in and
        // makes the bundle look picked when its parent garment wasn't.
        const pickAllZero  = fo?._pickStats  && fo._pickStats.picked  === 0;
        const pressAllZero = fo?._pressStats && fo._pressStats.pressed === 0;
        if (!pickAllZero && !pressAllZero) return portalState;
        const out = { ...portalState };
        const skuToClear = new Set();
        for (const li of lineItems) {
            if (li.sku) skuToClear.add(li.sku);
        }
        const matchKey = (k) => {
            if (skuToClear.has(k)) return true;
            for (const s of skuToClear) {
                if (k.startsWith(s + '!') || k.startsWith(s + '#') || k.startsWith(s + '|')) return true;
            }
            return false;
        };
        for (const k of Object.keys(out)) {
            if (!matchKey(k)) continue;
            const orig = out[k] || {};
            out[k] = {
                ...orig,
                ...(pickAllZero  ? { qty_picked:  0 } : {}),
                ...(pressAllZero ? { qty_pressed: 0 } : {}),
            };
        }
        return out;
    })();

    const itemCount = lineItems.reduce((s, li) => s + (li.quantity || 0), 0);

    // Has Team Portal items = any lineItem has a lineItemGroup OR soccer portal custom attributes (e.g. standalone items like socks/shorts)
    const hasTeamPortalItems = lineItems.some(li =>
        li.lineItemGroup?.id ||
        (li.customAttributes || []).some(a => a.key === 'Player Name' || a.key === 'Club')
    );

    // Per-FO pick progress from portalState (excludes virtual items).
    // Counts every non-virtual line item — SKU'd lines look up portalState
    // by SKU (with split-key aggregation), no-SKU embellishment lines look
    // up by `__no_sku__<line_item_gid>` to match the picking writer's key
    // scheme. Without the no_sku branch, embellishment children of pressed
    // bundles were excluded from both numerator and denominator, so a
    // 4-item bundle (parent + 3 logo lines) read 1 of 1 (only the SKU'd
    // parent) instead of 4 of 4.
    const foPickStats = (() => {
        // displayMode-aware (2026-05-31): when the parent split an
        // IN_PROGRESS FO into one card per Fulfillment + one for the
        // remaining unfulfilled qty, the FO-wide _pickStats is wrong for
        // BOTH cards. Compute per-card from displayLineItems:
        //   - 'fulfilled' card → workflow invariant (FULLY PICKED)
        //   - 'pending' card → real per-card pick state
        // For 'legacy' cards keep the prior behavior (FO-wide stat).
        if (displayMode === 'fulfilled') {
            // Warehouse pickup workflow (2026-06-29): if BE flagged this FO
            // as warehouse-pickup-open, prefer the real backend pickStats
            // (reflects item_state) over the workflow invariant.
            if (fo._warehousePickupOpen && fo._pickStats
                && typeof fo._pickStats.picked === 'number') {
                return { total: fo._pickStats.total, picked: fo._pickStats.picked };
            }
            // Label-first warehouses: skip the shipped-implies-picked
            // workflow invariant. Prefer real BE pickStats when present;
            // otherwise derive from lineItemFlags (which the backend gate
            // now populates via per-SKU distribution reading item_state).
            // Never fall through to `picked = total` — the whole point is
            // the label print doesn't imply the units were picked.
            if (_foAtLabelFirst) {
                const t = (lineItems || []).reduce((s, li) =>
                    isVirtual(li.title) ? s : s + (li.quantity || 0), 0);
                if (fo._pickStats && typeof fo._pickStats.picked === 'number') {
                    return { total: fo._pickStats.total || t, picked: fo._pickStats.picked };
                }
                let p = 0;
                const flags = fo._lineItemFlags || {};
                for (const li of (lineItems || [])) {
                    if (isVirtual(li.title)) continue;
                    if (flags[li.id]?.picked) p += (li.quantity || 0);
                }
                return t > 0 ? { total: t, picked: p } : null;
            }
            const t = (lineItems || []).reduce((s, li) =>
                isVirtual(li.title) ? s : s + (li.quantity || 0), 0);
            return t > 0 ? { total: t, picked: t } : null;
        }
        if (displayMode === 'pending') {
            // #N split-key aggregation for per-player number splits. The line
            // item's SKU (e.g. "STHL-TUS1.5-VEGAS") may be stored in item_state
            // under a #N variant ("STHL-TUS1.5-VEGAS#39") when picker logged
            // per-player. Sum across siblings so the FO header doesn't read
            // "Unpicked" while the per-line badges correctly show 1/1 Picked.
            const _psNumSplit = (key) => {
                if (!key) return 0;
                let q = 0;
                if (foPortalState[key]) q += foPortalState[key].qty_picked || 0;
                for (const [k, v] of Object.entries(foPortalState)) {
                    if (!v || k === key) continue;
                    if (k.startsWith(key + '#')) q += v.qty_picked || 0;
                }
                return q;
            };
            // Iterate lineItems FLAT — they already contain bundle parents
            // AND children as separate entries. Previously we wrapped this
            // in groupLineItems(...) and iterated [item, ...children] which
            // double-counted children (a 25-bundle order summed to 250
            // instead of 84). The grouping was only needed for #N split-key
            // aggregation on the picked count, which lives inside _psNumSplit
            // and doesn't need the parent/child structure.
            //
            // 2026-07-15: when the SAME SKU appears in multiple line items
            // on this FO (e.g. TOWU3 as 2 separate lines qty=1), the naive
            // `picked += min(_psNumSplit(key), qty)` per-line credited the
            // full portalState count against EACH line — 1 real pick on
            // TOWU3 across 2 line items became "2 picked" on the chip.
            // Track remaining picks per key so each real pick is consumed
            // once as we walk the line items.
            // 2026-08-02 Stage 2: prefer per-line-item picked counts from
            // portalState._byLineItem. When a specific LI has line_item_id-
            // scoped events, use them directly — stops phantom FOs from
            // borrowing SKU-aggregated picks that were actually consumed by
            // a sibling shipped FO (SP-285761 class). Fall back to SKU
            // aggregation (FIFO consumption) for LIs with no per-LI events.
            const _byLI = portalState?._byLineItem || {};
            let total = 0, picked = 0;
            const _remainingByKey = {};
            for (const li of (lineItems || [])) {
                if (isVirtual(li.title)) continue;
                const qty = li.quantity || 0;
                if (qty <= 0) continue;
                total += qty;
                const _liNum = String(li.id || '').split('/').pop();
                const _perLi = _liNum ? _byLI[_liNum] : null;
                if (_perLi) {
                    picked += Math.min(_perLi.qty_picked || 0, qty);
                    continue;
                }
                const key = li.sku || `__no_sku__${li.id}`;
                if (!(key in _remainingByKey)) _remainingByKey[key] = _psNumSplit(key);
                const use = Math.min(_remainingByKey[key] || 0, qty);
                picked += use;
                _remainingByKey[key] = (_remainingByKey[key] || 0) - use;
            }
            return total > 0 ? { total, picked } : null;
        }
        // Prefer backend-computed per-FO stats when present (2026-05-22) —
        // uses events + workflow invariant + shipped-FO subtraction so the
        // badge ties out exactly with reality, even when shared SKUs are
        // split across FOs. Falls back to the legacy psForSku-derived
        // computation when the backend hasn't supplied them.
        if (fo._pickStats && typeof fo._pickStats.picked === 'number') {
            return { total: fo._pickStats.total, picked: fo._pickStats.picked };
        }
        if (!hasTeamPortalItems) return null;
        // Per-parent-line accounting that matches the line-item badges. For
        // each parent line: sum (parent's slice + sum of children's slices).
        // Each slice = min(qty_picked − preceding-slot offset, this line's
        // qty_total). The event log is authoritative — children are not
        // clamped by their parent's pick count anymore (see 2026-05-19 fix).
        // Aggregation is per-line, not per-SKU, so shared-SKU embellishments
        // spread across multiple bundles attribute correctly via lineOffset.
        const allGroups = groupLineItems(orderLineItems, orderTags);
        const resolveKey = (li) => li.sku || `__no_sku__${li.id}`;

        // Aggregate qty_picked across split-key siblings of `sku`. The
        // picker splits per-player physical units like STHL-TUS1.5-VEGAS#14,
        // STHL-TUS1.5-VEGAS#15 — same product, one row per jersey number.
        // foPortalState[sku] returns nothing when the row was stored under
        // a #N variant. Narrow scope: ONLY # suffix (player number split).
        // Skip ! (personalization name = embellishment child) to preserve
        // the 2026-05-22 SP-246758 fix that kept embellishment scans from
        // marking the parent garment as picked.
        const psForSkuNumSplit = (key) => {
            if (!key) return null;
            const matches = [];
            if (foPortalState[key]) matches.push(foPortalState[key]);
            for (const [k, v] of Object.entries(foPortalState)) {
                if (!v || k === key) continue;
                if (k.startsWith(key + '#')) matches.push(v);
            }
            if (matches.length === 0) return null;
            const agg = { qty_total: 0, qty_picked: 0, qty_pressed: 0, qty_packed: 0 };
            for (const m of matches) {
                agg.qty_total   += m.qty_total   || 0;
                agg.qty_picked  += m.qty_picked  || 0;
                agg.qty_pressed += m.qty_pressed || 0;
                agg.qty_packed  += m.qty_packed  || 0;
            }
            return agg;
        };

        const skuRunQty = {};
        const lineOffset = {};
        const recordOffset = (key, id, qty) => {
            lineOffset[id] = skuRunQty[key] || 0;
            skuRunQty[key] = (skuRunQty[key] || 0) + qty;
        };
        for (const { item, children } of allGroups) {
            recordOffset(resolveKey(item), item.id, item.quantity || 1);
            for (const c of children) {
                recordOffset(resolveKey(c), c.id, c.quantity || 1);
            }
        }

        let total = 0, picked = 0;
        const foGroups = groupLineItems(lineItems, orderTags);
        // 2026-05-22: closed/shipped FOs show as fully picked (workflow
        // invariant — items shipped were picked, regardless of whether a
        // pick event was logged for every unit). This bypasses the
        // shared-SKU offset accounting that previously caused a fulfilled
        // FO to read "22/23 PICKED" when one line item's offset exceeded
        // the FO's qty_picked.
        if (foIsClosedShipped) {
            for (const { item: parent, children } of foGroups) {
                if (isVirtual(parent.title)) continue;
                const parentQty = parent.quantity || 1;
                if (parentQty <= 0) continue;
                total  += parentQty;
                picked += parentQty;
                for (const c of children) {
                    if (isVirtual(c.title)) continue;
                    const cQty = c.quantity || 1;
                    if (cQty <= 0) continue;
                    total  += cQty;
                    picked += cQty;
                }
            }
            return total > 0 ? { total, picked } : null;
        }
        for (const { item: parent, children } of foGroups) {
            if (isVirtual(parent.title)) continue;
            const parentQty = parent.quantity || 1;
            if (parentQty <= 0) continue;
            // 2026-05-22: read qty_picked from the parent row ONLY (no
            // psForSku aggregation), so a scanned embellishment paper doesn't
            // make the parent garment appear picked. SP-246758 had its OPEN
            // FO's track-jacket parent show as FULLY PICKED because the
            // !Enzo Monsalve embellishment child had qty_picked=1; physically
            // the parent jacket was never scanned.
            const pKey = resolveKey(parent);
            const pPs = psForSkuNumSplit(pKey);
            const pOff = lineOffset[parent.id] || 0;
            const parentPicked = Math.min(Math.max(0, (pPs?.qty_picked || 0) - pOff), parentQty);
            total  += parentQty;
            picked += parentPicked;
            for (const c of children) {
                if (isVirtual(c.title)) continue;
                const cQty = c.quantity || 1;
                if (cQty <= 0) continue;
                const cKey = resolveKey(c);
                // Same parent-only rule for embellishment children — read
                // qty_picked from the row whose group_key matches the child
                // SKU directly. Use # split aggregation (per-player number
                // variants of the same physical item).
                const cPs = psForSkuNumSplit(cKey);
                const cOff = lineOffset[c.id] || 0;
                const childPicked = Math.min(Math.max(0, (cPs?.qty_picked || 0) - cOff), cQty);
                total  += cQty;
                picked += childPicked;
            }
        }
        return total > 0 ? { total, picked } : null;
    })();

    // Per-FO press progress: counts UNITS of bundle parents pressed, not parent
    // line items. Matches foPickStats's unit denominator so the FO card shows
    // matching "X/Y PICKED" and "X/Y BUNDLES PRESSED" totals. Previously this
    // returned 1 line = 1 bundle regardless of qty, which is why a parent line
    // with qty 5 unpressed read as "1 unpressed" → 43/44 instead of 525/530.
    //
    // Per-line offset (skuOffsets) lets us subtract prior order-wide units of
    // this SKU from the per-SKU qty_pressed scalar, so each parent line's
    // pressed-unit count is correct even when several lines share a SKU.
    const foPressStats = (() => {
        // displayMode-aware (2026-05-31): mirror foPickStats above.
        // Bundle parents are SKUs that have at least one child in the
        // order-wide grouping AND at least one embellishment-typed child
        // (matches getPressingItems' filter). Cards whose displayLineItems
        // contain no bundle parent return null so the badge hides entirely.
        if (displayMode === 'fulfilled' || displayMode === 'pending') {
            const allGroups = groupLineItems(orderLineItems || [], orderTags);
            const bpSet = new Set();
            for (const { item, children } of allGroups) {
                if (!item?.sku) continue;
                const hasEmbell = (children || []).some(c =>
                    EMBELLISHMENT_TYPES.has(
                        c.productType || c.variant?.product?.productType || ''
                    )
                );
                if (hasEmbell) bpSet.add(item.sku);
            }
            let total = 0, pressed = 0;
            for (const li of (lineItems || [])) {
                if (isVirtual(li.title)) continue;
                if (!bpSet.has(li.sku)) continue;
                const qty = li.quantity || 0;
                if (qty <= 0) continue;
                total += qty;
                if (displayMode === 'fulfilled') {
                    pressed += qty;  // workflow invariant
                } else {
                    const ps = foPortalState[li.sku];
                    pressed += Math.min(ps?.qty_pressed || 0, qty);
                }
            }
            return total > 0 ? { total, pressed } : null;
        }
        if (fo._pressStats && typeof fo._pressStats.pressed === 'number') {
            return { total: fo._pressStats.total, pressed: fo._pressStats.pressed };
        }
        if (!hasTeamPortalItems) return null;
        const allGroups = groupLineItems(orderLineItems, orderTags);
        const skuOffsets = {};        // running unit-count per SKU across order
        const lineOffset = {};        // parent line id -> its unit offset
        const lineInstance = {};      // parent line id -> its 1-based line-instance index
        const skuLineCounts = {};
        for (const { item } of allGroups) {
            const key = item.sku || `__no_sku__${item.id}`;
            lineOffset[item.id] = skuOffsets[key] || 0;
            skuOffsets[key] = (skuOffsets[key] || 0) + (item.quantity || 1);
            skuLineCounts[key] = (skuLineCounts[key] || 0) + 1;
            lineInstance[item.id] = skuLineCounts[key];
        }
        const foBundles = groupLineItems(lineItems, orderTags).filter(g => g.children.length > 0);
        let total = 0, pressed = 0;
        for (const { item } of foBundles) {
            const qty = item.quantity || 0;
            if (qty <= 0) continue;
            const sku = item.sku || `__no_sku__${item.id}`;
            const ps = psForSku(foPortalState, sku) || foPortalState[sku];
            total += qty;
            const offset = lineOffset[item.id] || 0;
            // Prefer press_instances (per-unit map). Each press_instances key
            // is a 1-based unit index ACROSS the order, so we sum only keys
            // falling inside this parent line's window [offset+1, offset+qty].
            const pi = ps?.press_instances || {};
            const hasMap = Object.keys(pi).length > 0;
            if (hasMap) {
                for (let i = offset + 1; i <= offset + qty; i++) {
                    if (pi[String(i)]?.pressed) pressed++;
                }
            } else {
                // Scalar fallback: clamp the SKU-wide qty_pressed scalar into
                // this parent line's [offset+1..offset+qty] window.
                const qtyPressed = ps?.qty_pressed || 0;
                pressed += Math.min(Math.max(0, qtyPressed - offset), qty);
            }
        }
        return total > 0 ? { total, pressed } : null;
    })();

    // Items shown in pick modal: every line item individually (not merged by SKU)
    const pickModalItems = lineItems.map(li => {
        const gk = li.sku || `__no_sku__${li.id}`;
        const ps = psForSku(foPortalState, gk) || foPortalState[gk];
        const gaugeTotal = (ps?.qty_total > 0) ? ps.qty_total : (li.quantity || 1);
        return {
            groupKey: gk,
            itemKey: li.id,  // unique per line item instance
            title: li.title,
            sku: li.sku || '',
            qty: li.quantity || 1,
            image: li.image?.url || null,
            pickPct: ps ? Math.round((ps.qty_picked || 0) / gaugeTotal * 100) : null,
        };
    });

    // Items shown in press modal: only bundles (items with embellishment children)
    const pressModalItems = groupLineItems(lineItems, orderTags).filter(g => g.children.length > 0).map(g => {
        const parent = g.item;
        const gk = parent.sku || `__no_sku__${parent.id}`;
        const ps = psForSku(foPortalState, gk) || foPortalState[gk];
        const label = parent.title;
        const gaugeTotal = (ps?.qty_total > 0) ? ps.qty_total : (parent.quantity || 1);
        return {
            groupKey: gk,
            title: label,
            sku: parent.sku || '',
            qty: gaugeTotal,
            image: parent.image?.url || null,
            pressPct: ps ? Math.round((ps.qty_pressed || 0) / gaugeTotal * 100) : null,
        };
    });

    const openSelectMode = (mode) => {
        setEllipsisOpen(false);
        setSelectedKeys(new Set());
        setSelectMode(mode);
    };

    const toggleKey = (key) => {
        setSelectedKeys(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const cancelSelect = () => { setSelectMode(null); setSelectedKeys(new Set()); };

    const handleSelectAll = () => {
        const isPick = selectMode === 'pick' || selectMode === 'unpick';
        const allKeys = isPick
            ? pickModalItems.map(item => item.itemKey)
            : pressModalItems.map(item => item.groupKey);
        const allSelected = allKeys.every(k => selectedKeys.has(k));
        setSelectedKeys(allSelected ? new Set() : new Set(allKeys));
    };

    const handleConfirmMark = async () => {
        if (selectedKeys.size === 0 || markSubmitting) return;
        setMarkSubmitting(true);
        const isPick = selectMode === 'pick' || selectMode === 'unpick';
        const items = isPick ? pickModalItems : pressModalItems;
        let groups;
        if (isPick) {
            // 2026-08-02 revised: send one group entry per SELECTED line item
            // (not per SKU). Old behavior aggregated by groupKey and passed
            // only the FIRST line_item_id, so checking multiple LIs of the
            // same SKU landed only one un-pick — the other LIs kept showing
            // as picked because their per-LI event never fired.
            // total_qty per SKU is still needed so the backend's upsert can
            // seed qty_total when the row is being created fresh.
            const totalByGroup = {};
            for (const item of items) {
                if (!totalByGroup[item.groupKey]) totalByGroup[item.groupKey] = 0;
                totalByGroup[item.groupKey] += item.qty;
            }
            const foGid = fo?.id || '';
            const foNum = foGid.split('/').pop() || null;
            groups = [];
            for (const item of items) {
                if (!selectedKeys.has(item.itemKey)) continue;
                const ik = item.itemKey || '';
                const liNum = (ik && !ik.startsWith('__no_sku__') && !ik.startsWith('bundle:'))
                    ? String(ik).split('/').pop() : null;
                groups.push({
                    group_key: item.groupKey,
                    qty: item.qty,
                    total_qty: totalByGroup[item.groupKey],
                    line_item_id: liNum,
                    fulfillment_order_id: foNum,
                });
            }
        } else {
            const foGid = fo?.id || '';
            const foNum = foGid.split('/').pop() || null;
            groups = items
                .filter(item => selectedKeys.has(item.groupKey))
                .map(item => ({
                    group_key: item.groupKey, qty: item.qty,
                    fulfillment_order_id: foNum,
                }));
        }
        const actionMap = {
            pick:   'mark_selected_picked',
            unpick: 'mark_selected_not_picked',
            press:  'mark_selected_pressed',
            unpress:'mark_selected_not_pressed',
        };
        // 2026-07-29: include this FO's shopify_location_id so admin
        // un-mark events land under the same loc as the original pick /
        // press. Without it, admin pick_unmark writes with loc=NULL,
        // splits the events lift's DISTINCT ON (group_key, loc)
        // aggregation, and the picked/pressed lift keeps stamping
        // per-item badges as done even after admin un-marked.
        const foLocId = (fo?.assignedLocation?.location?.id || '')
            .split('/').pop() || '';
        // 2026-08-09: refuse to submit when we can't resolve the FO's
        // loc — this used to silently POST with an empty
        // shopify_location_id, and downstream events + item_state landed
        // loc-less (SP-335186 class). Backend now 400s on that shape,
        // but the FE early-return gives a clearer error to the operator.
        if (!foLocId) {
            setMarkSubmitting(false);
            alert('This FO has no assigned location — cannot mark items. Reload the order and try again.');
            return;
        }
        try {
            const res = await storeFetch(`/api/pressing/orders/${orderId}/tools`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action:              actionMap[selectMode],
                    order_name:          orderName,
                    groups,
                    shopify_location_id: foLocId,
                }),
            });
            if (!res.ok) throw new Error('failed');
            cancelSelect();
            onPortalStateRefresh?.();
        } catch {
            alert('Action failed. Please try again.');
        } finally {
            setMarkSubmitting(false);
        }
    };

    const MODAL_TITLES = {
        pick:    'Mark Items As Picked',
        unpick:  'Mark Items As Not Picked',
        press:   'Mark Bundles As Pressed',
        unpress: 'Mark Bundles As Not Pressed',
    };
    const CONFIRM_LABELS = {
        pick:    'Mark As Picked',
        unpick:  'Mark As Not Picked',
        press:   'Mark As Pressed',
        unpress: 'Mark As Not Pressed',
    };

    const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

    const handleFulfill = async () => {
        setFulfilling(true); setActionMsg(null); setActionError(null);
        try {
            const res = await storeFetch(`/api/orders/${orderId}/fulfill`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    fulfillment_order_ids: [fo.id],
                    tracking_number: trackingNumber || null,
                    tracking_company: carrier,
                    notify_customer: false,
                }),
            });
            const data = await res.json();
            if (res.ok) { setActionMsg('Fulfilled successfully.'); onFulfilled?.(); }
            else setActionError(data.detail || JSON.stringify(data));
        } catch (e) { setActionError(e.message); }
        finally { setFulfilling(false); }
    };

    const handlePickupStep = async (step) => {
        const endpointByStep = {
            'transfer':        'transfer-to-pickup',
            'mark-ready':      'mark-ready-for-pickup',
            'mark-picked-up':  'mark-picked-up',
        };
        const successMsgByStep = {
            'transfer':        'Transferred to pickup location.',
            'mark-ready':      'Ready-for-pickup email sent.',
            'mark-picked-up':  'Marked as picked up.',
        };
        const endpoint = endpointByStep[step];
        if (!endpoint) return;
        setFulfilling(true); setActionMsg(null); setActionError(null);
        try {
            const res = await storeFetch(`/api/orders/${orderId}/${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ fulfillment_order_id: fo.id }),
            });
            const data = await res.json();
            if (res.ok) { setActionMsg(successMsgByStep[step]); onFulfilled?.(); }
            else setActionError(data.detail || JSON.stringify(data));
        } catch (e) { setActionError(e.message); }
        finally { setFulfilling(false); }
    };

    const handleReprint = () => {
        const sk = activeStore || 'soccerpost';
        let url;
        if (storedLabel) {
            const tail = (storedLabel.shopify_label_gid || '').split('/').pop();
            url = `/api/shipping-labels/${tail}/combined-html?store=${encodeURIComponent(sk)}`;
        } else {
            const qs = new URLSearchParams({
                fulfillment_order_id: fo.id,
                skip_transfer: 'true',
                store: sk,
            });
            url = `/api/orders/${orderId}/packing-slip-print?${qs}`;
        }
        // In standalone PWA (iPad Add-to-Home-Screen), window.open('_blank')
        // launches the URL in regular Safari, OUTSIDE the PWA window — and
        // the popup's "✕ Close Label" button has no opener/parent to talk
        // back to, so the close action does nothing. Navigate the parent
        // PWA window directly with &return=<current-url> so the close
        // button can navigate back. iPad's Safari only accepts the FIRST
        // window.print() per page lifecycle, but each navigation here is
        // a fresh document, so the auto-print fires cleanly every time.
        const isStandalonePWA = (typeof window !== 'undefined') && (
            window.navigator?.standalone === true
            || (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)
        );
        if (isStandalonePWA) {
            const sep = url.includes('?') ? '&' : '?';
            const ret = encodeURIComponent(window.location.href);
            window.location.href = `${url}${sep}return=${ret}`;
            return;
        }
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    // CLOSED FO whose only fulfillment was cancelled — label was voided
    const isCancelledLabel = !isOpen && fulfillment?.status === 'CANCELLED' && fo.requestStatus === 'UNSUBMITTED';
    const isTrulyFulfilled = !isOpen && !isCancelledLabel;

    const headerBg = isOpen ? '#fff8e7' : (isCancelledLabel && !orderCancelled) ? '#fff4f4' : isTrulyFulfilled ? '#f0faf4' : '#fafafa';
    const badge = isOpen
        ? <FulfillmentBadge status="open" />
        : isCancelledLabel && !orderCancelled
            ? <FulfillmentBadge status="cancelled" />
            : isTrulyFulfilled
                ? <FulfillmentBadge status="closed" />
                : <FulfillmentBadge status="cancelled" />;

    return (
        <div className="card" style={{ border: `1px solid ${isCancelledLabel ? '#fca5a5' : '#e5e7eb'}` }}>
            {/* Card header */}
            <div className="fo-card-header" style={{ backgroundColor: headerBg }}>
                {badge}
                {fo.assignedLocation?.name && (
                    <span className="fo-card-location">📍 {fo.assignedLocation.name}</span>
                )}
                {foPickStats && (
                    <span className={`fo-pick-badge ${
                        foPickStats.picked >= foPickStats.total
                            ? 'fo-pick-badge--full'
                            : foPickStats.picked === 0
                                ? 'fo-pick-badge--unpicked'
                                : 'fo-pick-badge--partial'}`}>
                        {foPickStats.picked >= foPickStats.total
                            ? `✓ FULLY PICKED`
                            : foPickStats.picked === 0
                                ? 'UNPICKED'
                                : `${foPickStats.picked}/${foPickStats.total} PICKED`}
                    </span>
                )}
                {foPressStats && foPressStats.total > 0 && (
                    <span className={`fo-pick-badge ${foPressStats.pressed >= foPressStats.total ? 'fo-pick-badge--pressed-full' : 'fo-pick-badge--pressed-partial'}`}>
                        {foPressStats.pressed >= foPressStats.total
                            ? `✓ FULLY PRESSED`
                            : `${foPressStats.pressed}/${foPressStats.total} BUNDLES PRESSED`}
                    </span>
                )}
                {fo._pickupState?.picked_up_at && (
                    <span className="pickup-badge pickup-badge--picked-up">ORDER PICKED UP</span>
                )}
                {/* When the FO is already CLOSED in Shopify (isTrulyFulfilled) but our
                    pickup_state row is missing or incomplete, treat it as picked up.
                    A closed PICK_UP FO with no explicit picked_up_at means the customer
                    collected it in Shopify POS (or admin marked it picked up directly)
                    bypassing OpsHub's pickup endpoints. Without this guard the badge
                    flashes "IN TRANSIT" for orders that were finished days ago. */}
                {!fo._pickupState?.picked_up_at && isTrulyFulfilled && pickup && (
                    <span className="pickup-badge pickup-badge--picked-up">ORDER PICKED UP</span>
                )}
                {/* 2026-07-30 unified: badge derives from the backend-computed
                    effective_state (see compute_fo_effective_state in
                    database.py). Single source of truth — replaces the
                    prior tangled derivation from timestamps + freshness
                    checks + retail-loc detection.
                      0 = pending → no badge (items at source or voided)
                      1 = IN TRANSIT (at retail, moved, not marked ready)
                      2 = READY FOR PICKUP (at retail, ready or in-stock)
                      3 = ORDER PICKED UP (rendered by earlier branches
                          before this — kept for backwards-compat display) */}
                {(() => {
                    if (!pickup) return null;
                    const ps = fo._pickupState || {};
                    if (ps.picked_up_at) return null;         // already rendered above
                    if (isTrulyFulfilled) return null;        // already rendered above
                    const st = ps.effective_state;
                    if (st === 2) {
                        return <span className="pickup-badge pickup-badge--rfp">READY FOR PICKUP</span>;
                    }
                    if (st === 1) {
                        return <span className="pickup-badge pickup-badge--transferred">IN TRANSIT</span>;
                    }
                    return null;
                })()}
                {selectMode ? (
                    <div className="fo-select-bar">
                        <span className="fo-select-bar__mode">{MODAL_TITLES[selectMode]}</span>
                        <button className="fo-select-bar__select-all" onClick={handleSelectAll}>
                            {(() => {
                                const isPick = selectMode === 'pick' || selectMode === 'unpick';
                                const allKeys = isPick
                                    ? pickModalItems.map(item => item.itemKey)
                                    : pressModalItems.map(item => item.groupKey);
                                return allKeys.every(k => selectedKeys.has(k)) ? 'Clear All' : 'Select All';
                            })()}
                        </button>
                        <button className="fo-select-bar__cancel" onClick={cancelSelect}>Cancel</button>
                        <button
                            className="fo-select-bar__apply"
                            onClick={handleConfirmMark}
                            disabled={selectedKeys.size === 0 || markSubmitting}
                        >
                            {markSubmitting ? 'Saving…' : CONFIRM_LABELS[selectMode]}
                        </button>
                    </div>
                ) : (
                    <div className="fo-card-actions">
                        <div className="fo-ellipsis-wrap" ref={ellipsisRef}>
                            <button
                                className="fo-ellipsis-btn"
                                onClick={() => setEllipsisOpen(v => !v)}
                                aria-label="Mark items"
                                title="Mark items"
                            >
                                …
                            </button>
                            {ellipsisOpen && (
                                <div className="fo-ellipsis-menu">
                                    <button className="fo-ellipsis-item" onClick={() => openSelectMode('pick')}>Mark As Picked</button>
                                    <button className="fo-ellipsis-item" onClick={() => openSelectMode('unpick')}>Mark As Not Picked</button>
                                    <div className="fo-ellipsis-divider" />
                                    <button className="fo-ellipsis-item" onClick={() => openSelectMode('press')}>Mark As Pressed</button>
                                    <button className="fo-ellipsis-item" onClick={() => openSelectMode('unpress')}>Mark As Not Pressed</button>
                                </div>
                            )}
                        </div>
                        <FulfillmentMenu
                            fo={fo}
                            fulfillment={fulfillment}
                            orderId={orderId}
                            isOpen={isOpen}
                            isShipping={isShipping}
                            hasLabel={!!storedLabel}
                            pickup={pickup}
                            atPickupLocation={atPickupLocation}
                            pickupReadyAt={pickupReadyAt}
                            pickedUpAt={fo._pickupState?.picked_up_at || null}
                            isCancelledLabel={isCancelledLabel}
                            isTrulyFulfilled={isTrulyFulfilled}
                            storeKey={activeStore || 'soccerpost'}
                            storeFetch={storeFetch}
                            siblingFos={siblingFos}
                            storePickupOverride={storePickupOverride}
                            fbmMoved={fbmMoved}
                            onAfterAction={() => { onPortalStateRefresh?.(); onFulfilled?.(); }}
                            onToast={onToast}
                        />
                    </div>
                )}
            </div>

            {/* Meta row */}
            <div className="fo-card-meta">
                {isOpen && fo.fulfillBy && (
                    <span>🗓 Fulfill by <strong className="text-secondary">{fmtDate(fo.fulfillBy)}</strong></span>
                )}
                {isTrulyFulfilled && fulfillment?.createdAt && (
                    <span>📅 Fulfilled {fmtDate(fulfillment.createdAt)}</span>
                )}
                {isCancelledLabel && !orderCancelled && (
                    <span className="fo-card-meta-voided">Label voided — items still need to be shipped</span>
                )}
                <span>
                    {pickup ? '🏪 ' : '🚚 '}
                    {pickup && pickupLocationName
                        ? <StoreBadge name={pickupLocationName} />
                        : (() => {
                            const _mt = fo.deliveryMethod?.methodType;
                            const _presented = (fo.deliveryMethod?.presentedName || '').trim();
                            // For SHIPPING, prefer the carrier title from Shopify
                            // (e.g. "Ground Advantage", "Priority Mail", "Team
                            // Portal Flat Rate Shipping") so multi-FO orders
                            // show the per-FO method rather than the generic
                            // "Shipping" word. Keep hardcoded labels for
                            // pickup / local delivery / none.
                            if (_mt === 'SHIPPING') return _presented || 'Shipping';
                            return DELIVERY_LABELS[_mt] || _presented || _mt || 'Shipping';
                          })()}
                </span>
                {isTrulyFulfilled && (fulfillment?.trackingInfo || []).map((t, i) => (
                    <span key={i}>
                        📦 {t.company} {t.url
                            ? <a href={t.url} target="_blank" rel="noreferrer" className="fo-card-meta-tracking-link">{t.number}</a>
                            : t.number}
                    </span>
                ))}
                {hasRole(user, ['admin', 'site_manager', 'user', 'franchise', 'store_manager'])
                    && (storedLabel || isTrulyFulfilled || atPickupLocation || fo._pickupState?.transferred_at)
                    && lineItems.some(li => !isVirtual(li.title)) && (
                    <span className="fo-card-label-actions">
                        <button onClick={handleReprint} className="btn btn--secondary btn--slim">
                            {storedLabel ? '🖨 View / Reprint Shipping & Packing Label'
                                         : '🖨 View / Reprint Packing Slip'}
                        </button>
                    </span>
                )}
                {(bulkShipmentLabels || []).filter(l => String(l.fulfillment_order_id) === String((fo.id || '').split('/').pop())).length > 0 && (
                    <div className="fo-card-bulk-labels">
                        {(bulkShipmentLabels || [])
                            .filter(l => String(l.fulfillment_order_id) === String((fo.id || '').split('/').pop()))
                            .map(l => (
                                <div key={l.id} className="fo-card-bulk-label">
                                    🚚 Bulk shipment{' '}
                                    {l.tracking_url
                                        ? <a href={l.tracking_url} target="_blank" rel="noreferrer">{l.tracking_number}</a>
                                        : l.tracking_number}
                                    {' · '}{new Date(l.scanned_at).toLocaleString()}{l.portal_username ? ` · ${l.portal_username}` : ''}
                                </div>
                            ))}
                    </div>
                )}
            </div>

            {/* Sort toggle + Line items */}
            <div className="fo-card-sort-bar">
                <span className="fo-card-sort-label">Sort by</span>
                <div className="fo-card-sort-toggle">
                    {[['order', '📋 Order'], ['product', '📦 Product'], ['player', '👤 Player']].map(([mode, label], i) => (
                        <button
                            key={mode}
                            onClick={() => setSortMode(mode)}
                            className={`fo-card-sort-btn ${sortMode === mode ? 'fo-card-sort-btn--active' : 'fo-card-sort-btn--inactive'}${i > 0 ? ' fo-card-sort-btn--right' : ''}`}
                        >{label}</button>
                    ))}
                </div>
            </div>
            <div className="fo-card-items-wrap">
                <ItemsTable lineItems={lineItems} sortMode={sortMode} splitBundleMap={splitBundleMap} portalState={foPortalState} selectMode={selectMode} selectedKeys={selectedKeys} onToggleKey={toggleKey} instanceIndexMap={orderInstanceIndexMap} orderTags={orderTags}
                    foAllUnpicked={!foIsClosedShipped && foPickStats && foPickStats.picked === 0}
                    foAllUnpressed={!foIsClosedShipped && foPressStats && foPressStats.pressed === 0}
                    /* 2026-06-29: previously dropped fo._lineItemFlags when
                       foIsClosedShipped=true and let foPortalState drive the
                       per-LI badges. That over-relied on an order-wide
                       instance offset (instanceIndexMap) against a per-FO
                       qty_picked budget, mis-attributing the 2nd instance of
                       a SKU as UNPICKED for shipped FOs (SP-259968 LANGH4
                       class — same SKU appears in 2 FOs, both shipped, only
                       one renders picked). Backend now stamps picked=true
                       for every LI in shipped FOs (line-item collection
                       includes totalQuantity=0 lines), so the BE flag is
                       authoritative — trust it. Fall back to portalState
                       only when BE provided no flag dict at all. */
                    lineItemFlags={fo._lineItemFlags || null}
                    tpOverridesByName={tpOverridesByName}
                    tpReqByLiId={tpReqByLiId} />
            </div>

            {/* Action area — only for open FOs */}
            {isOpen && (
                <div className="fo-card-action-area">
                    {actionMsg   && <div className="fo-card-action-success">{actionMsg}</div>}
                    {actionError && <div className="fo-card-action-error">{actionError}</div>}

                    {/* No pickup action buttons on OrderDetail — every transition
                        is driven from elsewhere:
                          - Transfer: initiated from the Packing Portal.
                          - Ready For Pickup + Picked Up: marked at the destination
                            store in Shopify POS, propagated to us via webhook.
                        The full state machine is still tracked (see pickupStep
                        derivation above) so Store Status badges and timeline
                        events stay accurate — we just don't render any button. */}
                    {null /* pickup actions intentionally hidden */}
                    {!pickup && null /* shipping label + fulfillment actions are exclusively in the Packing Portal kiosk flow */}
                </div>
            )}

            {/* Shipping label modal */}
            {showLabelModal && (
                <ShippingLabelModal
                    orderId={orderId}
                    fo={fo}
                    onClose={(didPurchase) => { setShowLabel(false); if (didPurchase) onFulfilled?.(); }}
                    onPurchased={() => {}}
                />
            )}

        </div>
    );
}

// ── Shipping Label Modal ──────────────────────────────────────────────────────

function ShippingLabelModal({ orderId, fo, onClose, onPurchased }) {
    const [step, setStep]           = useState('idle');   // idle | loading | rate | buying | done | error
    const [rateInfo, setRateInfo]   = useState(null);
    const [errorMsg, setErrorMsg]   = useState(null);
    const [pdfUrl, setPdfUrl]       = useState(null);
    const pollRef                   = React.useRef(null);
    const storeFetch                = useAuthFetch();

    // Clean up any in-flight poll when modal unmounts
    React.useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);

    const getRate = async () => {
        setStep('loading'); setErrorMsg(null);
        try {
            const res = await storeFetch(`/api/orders/${orderId}/shipping-label/rate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ fulfillment_order_id: fo.id }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || JSON.stringify(data));
            setRateInfo(data);
            setStep('rate');
        } catch (e) { setErrorMsg(e.message); setStep('error'); }
    };

    const startPdfPoll = (labelGid, attempt = 0) => {
        // Shopify needs time after ShippingLabelsPurchase before ShippingLabelsDocumentCreate works.
        // Poll every 5s, up to 12 attempts (60s total).
        if (attempt >= 12) return;
        const labelTail = (labelGid || '').split('/').pop();
        pollRef.current = setTimeout(async () => {
            try {
                const rr = await storeFetch(`/api/shipping-labels/${labelTail}/combined-pdf`);
                if (rr.ok) {
                    const blob = await rr.blob();
                    if (blob && blob.size > 0) { setPdfUrl(URL.createObjectURL(blob)); return; }
                }
            } catch {}
            startPdfPoll(labelGid, attempt + 1);
        }, 5000);
    };

    const purchase = async () => {
        setStep('buying'); setErrorMsg(null);
        try {
            const res = await storeFetch(`/api/orders/${orderId}/shipping-label/purchase`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    fulfillment_order_id: fo.id,
                    label_gid:    rateInfo.label_gid,
                    session_gid:  rateInfo.session_gid,
                    quoted_price: rateInfo.quoted_price,
                    shipping_date: rateInfo.shipping_date,
                    // Carrier / service carry through so PO-Box orders
                    // purchase the same USPS Ground Advantage the rate
                    // call fell back to (defaults to UPS Ground).
                    carrier_code: rateInfo.carrier_code || 'ups_shipping',
                    service_code: rateInfo.service_code || '03',
                }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || JSON.stringify(data));

            setStep('done');
            onPurchased?.();

            // Start polling for the PDF — Shopify needs time before it's available.
            if (data.label_gid) startPdfPoll(data.label_gid);
        } catch (e) { setErrorMsg(e.message); setStep('error'); }
    };

    return (
        <div className="slm-backdrop"
            onClick={e => { if (e.target === e.currentTarget) onClose(step === 'done'); }}
        >
            <div className="slm-dialog">
                {/* Header */}
                <div className="slm-header">
                    <div className="slm-title">Create Shipping Label</div>
                    <button onClick={() => onClose(step === 'done')} className="slm-close">×</button>
                </div>

                {/* Body */}
                <div className="slm-body">
                    {/* PO-Box fallback banner (renders when the rate
                        endpoint fell back to USPS Ground Advantage after
                        UPS Ground returned "not available for this shipment") */}
                    {rateInfo?.fallback_reason && (
                        <div style={{
                            padding: '8px 12px', marginBottom: 12,
                            background: '#fff5d6', border: '1px solid #ecc94b',
                            borderRadius: 6, color: '#744210', fontSize: 13,
                        }}>
                            ⚠️ {rateInfo.fallback_reason}
                        </div>
                    )}

                    {/* Carrier / package info (always shown) */}
                    <div className="slm-carrier-box">
                        <div className="slm-carrier-top">
                            <span className="slm-carrier-icon">📦</span>
                            <span className="slm-carrier-name">{rateInfo?.carrier_name || 'UPS Ground'}</span>
                        </div>
                        <div className="slm-carrier-details">
                            {rateInfo && (
                                <>
                                    <div>Package: <span className="slm-carrier-detail-value">{rateInfo.package_name} ({rateInfo.package_dims})</span></div>
                                    <div>Weight: <span className="slm-carrier-detail-value">{rateInfo.total_weight_lb?.toFixed(2)} lb</span></div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Rate result */}
                    {step === 'rate' && rateInfo && (
                        <div className="slm-rate-box">
                            <div className="slm-rate-label">{rateInfo.carrier_name || 'UPS Ground'} rate</div>
                            <div className="slm-rate-amount">
                                ${parseFloat(rateInfo.quoted_price).toFixed(2)} {rateInfo.currency}
                            </div>
                        </div>
                    )}

                    {/* Error */}
                    {step === 'error' && errorMsg && (
                        <div className="slm-error-box">{errorMsg}</div>
                    )}

                    {/* Done */}
                    {step === 'done' && (
                        <div className="slm-done-box">
                            <div className="slm-done-text">✓ Label purchased successfully</div>
                            {pdfUrl ? (
                                <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="slm-done-link">
                                    🖨 Open & Print Label
                                </a>
                            ) : (
                                <div className="slm-done-loading">Loading label PDF…</div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="slm-footer">
                    <button onClick={() => onClose(step === 'done')} className="slm-btn-cancel">
                        {step === 'done' ? 'Close' : 'Cancel'}
                    </button>

                    {(step === 'idle' || step === 'error') && (
                        <button onClick={getRate} className="slm-btn-action slm-btn-action--blue">
                            Get Rate
                        </button>
                    )}

                    {step === 'loading' && (
                        <button disabled className="slm-btn-action slm-btn-action--grey">
                            Getting rate…
                        </button>
                    )}

                    {step === 'rate' && (
                        <button onClick={purchase} className="slm-btn-action slm-btn-action--green">
                            Confirm &amp; Buy
                        </button>
                    )}

                    {step === 'buying' && (
                        <button disabled className="slm-btn-action slm-btn-action--grey">
                            Purchasing…
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}


// ── Order Event Log ───────────────────────────────────────────────────────────

const EVENT_VERB_LABELS = {
    confirmed:                       'Order confirmed',
    placed:                          'Order placed',
    fulfilled:                       'Fulfilled',
    fulfillment_cancelled:           'Fulfillment cancelled',
    cancellation_reactivated:        'Fulfillment reactivated',
    cancelled:                       'Order cancelled',
    voided:                          'Payment voided',
    refunded:                        'Refunded',
    note_added:                      'Note added',
    tagged:                          'Tagged',
    untagged:                        'Untagged',
    edited:                          'Order edited',
    payment_captured:                'Payment captured',
    fulfillment_order_moved:         'Fulfillment location changed',
    fulfillment_order_transferred:   'Fulfillment transferred',
    fulfillment_order_split:         'Fulfillment split',
    fulfillment_order_merged:        'Fulfillment merged',
};

// Admin-timeline verbs (2026-10-07) arrive as Shopify's eventLabel minus the
// "order_" prefix (mail_sent, payments_charge, sale_success, …) — anything not
// in EVENT_VERB_LABELS reads as "Mail sent" rather than raw snake_case.
const verbLabel = (verb) => EVENT_VERB_LABELS[verb]
    || (verb ? verb.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase()) : 'Event');

// Verbs whose arguments[] payload (origin_location_name, destination_location_name,
// list_item_content) we render in an expandable panel — matches Shopify's admin UI.
const RICH_EVENT_VERBS = new Set([
    'fulfillment_order_moved',
    'fulfillment_order_transferred',
    'fulfillment_order_split',
    'fulfillment_order_merged',
]);

function ShopifyRichEvent({ ev, isLast, fmtEvent, attachments, defaultOpen = false }) {
    const [open, setOpen] = useState(defaultOpen);   // V2 Order Event Log opens every rich event by default (Eric 2026-09-16)
    const [lightbox, setLightbox] = useState(null);
    const args = Array.isArray(ev.arguments) ? ev.arguments.find(a => a && typeof a === 'object') : null;
    const hasRich = !!args && (args.origin_location_name || args.destination_location_name || (args.list_item_content && args.list_item_content.length));
    const imgs = Array.isArray(attachments) ? attachments : [];
    return (
        <div key={`shopify-${ev.id}`} className={`event-log-item${isLast ? '' : ' event-log-item--bordered'}`}>
            <div className="event-log-dot-col">
                <div className="event-log-dot" />
                {!isLast && <div className="event-log-line" />}
            </div>
            <div className="event-log-content">
                <div className="event-log-verb"
                     style={hasRich ? { cursor: 'pointer', userSelect: 'none' } : undefined}
                     onClick={hasRich ? () => setOpen(v => !v) : undefined}>
                    {hasRich && (
                        <span style={{ display: 'inline-block', width: '1em', marginRight: 4, transition: 'transform 0.15s', transform: open ? 'rotate(90deg)' : 'none' }}>▶</span>
                    )}
                    {verbLabel(ev.verb)}
                </div>
                {ev.message && ev.message !== verbLabel(ev.verb) && (
                    <div className="event-log-msg">{renderShopifyMessage(ev.message)}</div>
                )}
                {imgs.length > 0 && (
                    <div className="event-log-attachments"
                         style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                        {imgs.map((att, idx) => (
                            <button key={att.id || idx}
                                    type="button"
                                    onClick={() => setLightbox(att.full_url || att.url)}
                                    style={{ padding: 0, border: '1px solid #e1e3e5',
                                             borderRadius: 4, background: 'transparent',
                                             cursor: 'zoom-in', overflow: 'hidden',
                                             lineHeight: 0 }}
                                    title="Click to view full size">
                                <img src={att.url} alt=""
                                     loading="lazy"
                                     style={{ width: 120, height: 120,
                                              objectFit: 'cover', display: 'block' }} />
                            </button>
                        ))}
                    </div>
                )}
                {lightbox && (
                    <div onClick={() => setLightbox(null)}
                         style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
                                  zIndex: 9999, display: 'flex', alignItems: 'center',
                                  justifyContent: 'center', cursor: 'zoom-out' }}>
                        <img src={lightbox} alt=""
                             style={{ maxWidth: '92vw', maxHeight: '92vh', objectFit: 'contain',
                                      boxShadow: '0 8px 30px rgba(0,0,0,0.5)' }} />
                    </div>
                )}
                {hasRich && open && (
                    <div className="event-log-rich" style={{ marginTop: 8, padding: '8px 12px', background: '#f6f6f7', borderRadius: 4, fontSize: 13 }}>
                        {args.list_item_content && args.list_item_content.length > 0 && (
                            <div style={{ marginBottom: (args.origin_location_name || args.destination_location_name) ? 8 : 0 }}>
                                <div style={{ fontWeight: 600, marginBottom: 4 }}>Items</div>
                                <ul style={{ margin: 0, paddingLeft: 18 }}>
                                    {args.list_item_content.map((it, idx) => (
                                        <li key={idx} style={{ marginBottom: 2 }}>
                                            {it.quantity || 1} × {it.product_title || it.sku || 'Item'}
                                            {it.sku && it.product_title ? <span style={{ color: '#6d7175', marginLeft: 6 }}>{it.sku}</span> : null}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {(args.origin_location_name || args.destination_location_name) && (
                            <div>
                                <div style={{ fontWeight: 600, marginBottom: 4 }}>Fulfillment location</div>
                                <div>
                                    Changed from{' '}
                                    <span style={{ background: '#e4e5e7', padding: '1px 6px', borderRadius: 3 }}>
                                        {args.origin_location_name || '—'}
                                    </span>{' '}
                                    to{' '}
                                    <span style={{ background: '#e4e5e7', padding: '1px 6px', borderRadius: 3 }}>
                                        {args.destination_location_name || '—'}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                <div className="event-log-time">
                    {ev.author && <span className="event-log-author">{ev.author}</span>}
                    {fmtEvent(ev.created_at)}
                </div>
            </div>
        </div>
    );
}

function stripHtml(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
}

// Render a Shopify timeline comment/message HTML safely, preserving
// @mentions as styled chips. Shopify emits mentions as:
//   <a data-protected-personal-information="true" href="…/settings/account/{id}">Name</a>
// Safety: html is parsed by DOMParser into an inert, detached document —
// scripts don't execute and no href is ever rendered (we only read
// textContent). Only whitelisted node shapes become anything renderable;
// everything else flattens to text.
function renderShopifyMessage(html) {
    if (!html) return null;
    let doc;
    try {
        doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
    } catch { return html; }
    const root = doc.body.firstChild;
    if (!root) return html;
    const out = [];
    let keyCounter = 0;
    const walk = (node) => {
        if (node.nodeType === 3) { // text
            out.push(node.nodeValue);
            return;
        }
        if (node.nodeType !== 1) return;
        // Mention: <a data-protected-personal-information="true">Name</a>
        if (node.tagName === 'A' && node.getAttribute('data-protected-personal-information') === 'true') {
            // Match Shopify Admin's mention chip. Pixel-sampled from
            // admin.shopify.com HAR-loaded reference: background #e3e3e3,
            // text #303030 (Polaris --p-color-bg-fill-tertiary + --p-color-text).
            // No border, 4px radius (--p-border-radius-200), ~6px inline padding.
            out.push(
                <span key={`m-${keyCounter++}`}
                      className="od-mention-chip"
                      style={{
                          display: 'inline',
                          padding: '1px 6px',
                          background: '#e3e3e3',
                          color: '#303030',
                          borderRadius: 4,
                          whiteSpace: 'nowrap',
                      }}>
                    {node.textContent}
                </span>
            );
            return;
        }
        // <br> → newline
        if (node.tagName === 'BR') { out.push('\n'); return; }
        // Otherwise: recurse into children (drop the tag, keep content)
        for (const child of node.childNodes) walk(child);
    };
    for (const child of root.childNodes) walk(child);
    return out;
}

const BADGE_EVENT_TYPES = new Set(['pick','pick_scan','pick_manual','press_manual','bundle_finish','pick_unmark','press_unmark','pack_pass']);
const BADGE_PASS_FAIL   = new Set(['pack_fail']);

// Background tint per portal — a very light wash of the bar color for the event-log-item body.
const PORTAL_BG = {
    picking:  '#eff6ff',
    pressing: '#fff7ed',
    packing:  '#f0fdf4',
    print:    '#f5f3ff',
    auto:     '#ecfeff',
    admin:    '#f6f6f7',
    issue:    '#fecaca',
};
// Past-tense verb per portal for the header of a grouped session.
const PORTAL_VERB = {
    picking:  'Picked',
    pressing: 'Pressed',
    packing:  'Packed',
    print:    'Printed',
    issue:    'Issue Reported',
    auto:     'Auto-completed on fulfillment',
    admin:    'System',
};

function portalRowBadge(ev) {
    if (BADGE_EVENT_TYPES.has(ev.event_type)) {
        const evMethod = ev.payload?.method;
        const isScan      = ev.event_type === 'pick_scan'
                         || ((ev.event_type === 'pick'
                              || ev.event_type === 'bundle_finish'
                              || ev.event_type === 'pack_pass') && evMethod === 'scan');
        const isUnpicked  = ev.event_type === 'pick_unmark';
        const isUnpressed = ev.event_type === 'press_unmark';
        let bg, color, text;
        if (isScan)                      { bg = '#bbf7d0'; color = '#166534'; text = 'Scan'; }
        else if (isUnpicked || isUnpressed){ bg = '#fecaca'; color = '#991b1b'; text = isUnpicked ? 'Unpicked' : 'Unpressed'; }
        else                              { bg = '#fed7aa'; color = '#9a3412'; text = 'Manual'; }
        return { bg, color, text };
    }
    if (BADGE_PASS_FAIL.has(ev.event_type)) {
        // pack_fail keeps the explicit Failed badge — failure state outweighs method.
        return { bg: '#fecaca', color: '#991b1b', text: 'Failed' };
    }
    return null;
}

const skuChipStyleNumber = { marginLeft: 6, padding: '1px 6px', borderRadius: 4, background: '#dbeafe', color: '#1e40af', fontSize: 11, fontWeight: 600 };
const skuChipStylePlayer = { marginLeft: 6, padding: '1px 6px', borderRadius: 4, background: '#fce7f3', color: '#9d174d', fontSize: 11, fontWeight: 600 };
function SkuChips({ parsed, item }) {
    // Picking events embed !Player#Number in the group_key — render those
    // straight from `parsed`. Pressing & packing events log the plain base
    // SKU, so fall back to the item's aggregated `players` (lifted from
    // item_state split-key rows by the timeline endpoint) and chip every
    // player tied to this bundle.
    if (parsed.player || parsed.number) {
        return (
            <>
                {parsed.number && <span style={skuChipStyleNumber}>{`#${parsed.number}`}</span>}
                {parsed.player && <span style={skuChipStylePlayer}>{parsed.player}</span>}
            </>
        );
    }
    const players = Array.isArray(item?.players) ? item.players : [];
    if (!players.length) return null;
    return (
        <>
            {players.map((p, idx) => (
                <React.Fragment key={`${p.name || ''}#${p.number || ''}-${idx}`}>
                    {p.number && <span style={skuChipStyleNumber}>{`#${p.number}`}</span>}
                    {p.name   && <span style={skuChipStylePlayer}>{p.name}</span>}
                </React.Fragment>
            ))}
        </>
    );
}

function portalRowText(ev, itemsMap, count = 1) {
    if (BADGE_EVENT_TYPES.has(ev.event_type)) {
        const item = itemsMap[ev.group_key];
        const parsed = parseGroupKey(ev.group_key);
        const skuLabel = (item && item.title) ? `${item.title} / ${parsed.base}` : (parsed.base || '');
        // pack_pass has qty_after=null (single click lifts qty_packed=qty_total
        // in one shot, or one event-per-card with no qty on the event). Fall
        // back to item.qty_total so a 1-event qty=N pack still shows ×N.
        const qtyFallback = ev.event_type === 'pack_pass'
            ? (Number(item?.qty_total) || 1)
            : (Number(ev.qty_after) || 1);
        const qty  = Math.max(count, qtyFallback);
        const text = (qty > 1) ? `${skuLabel} ×${qty}` : skuLabel;
        return <>{text}<SkuChips parsed={parsed} item={item} /></>;
    }
    if (BADGE_PASS_FAIL.has(ev.event_type)) {
        const item = itemsMap[ev.group_key];
        const parsed = parseGroupKey(ev.group_key);
        const skuLabel = (item && item.title) ? `${item.title} / ${parsed.base}` : (parsed.base || '');
        const packQty = Math.max(count, Number(item?.qty_total) || 1);
        const suffix = packQty > 1 ? ` ×${packQty}` : '';
        if (ev.event_type === 'pack_fail') {
            const reason = ev.payload?.reason ? ` — ${ev.payload.reason}` : '';
            const action = ev.payload?.action === 'rework' ? ' (Rework)'
                         : ev.payload?.action === 'partial' ? ' (Partial)' : '';
            const note   = ev.payload?.note ? ` · Note: "${ev.payload.note}"` : '';
            return <>{`${skuLabel}${suffix}${reason}${action}${note}`}<SkuChips parsed={parsed} item={item} /></>;
        }
        return <>{`${skuLabel}${suffix}`}<SkuChips parsed={parsed} item={item} /></>;
    }
    return portalEventLabel(ev);
}

// Collapse consecutive same-type same-group events into a single row with a
// ×N multiplier. Packing portal logs one pack_pass per instance, so qty=3 of
// the same SKU shows 3 stacked rows; this groups them. Same problem for press
// events on multi-instance bundles. Picker events already encode qty_after,
// but we still respect the higher of (count, qty_after).
const COLLAPSE_EVENT_TYPES = new Set([
    'pack_pass', 'pack_fail', 'bundle_finish', 'press_manual',
    'pick', 'pick_scan', 'pick_manual',
]);
function collapseRepeated(events) {
    const out = [];
    for (const ev of events) {
        const last = out[out.length - 1];
        if (last
            && last.ev.event_type === ev.event_type
            && last.ev.group_key === ev.group_key
            && COLLAPSE_EVENT_TYPES.has(ev.event_type)) {
            last.count += 1;
            continue;
        }
        out.push({ ev, count: 1 });
    }
    return out;
}

function EventLog({ orderId, cancelReason, perPage = 10, title = 'Order Timeline', expandMode = false }) {
    // expandMode (V2 Order Event Log, Eric 2026-09-16): show the newest `perPage` of the
    // unioned Shopify + OpsHub events, and a single "View Full Event Log" button that
    // loads everything — no Newer/Older paging, no link to the visual timeline.
    const [showAll, setShowAll] = useState(false);
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState(null);
    const [page, setPage]       = useState(0);
    const PER_PAGE = perPage;   // V2's Timeline tab asks for 50 (Eric 2026-09-16); V1 keeps 10
    const storeFetch            = useAuthFetch();
    const [thread, setThread]   = useState({ comments: [], can_post: false, mirror_as: null, me: null });
    const [editing, setEditing] = useState(null);
    const [replyTo, setReplyTo] = useState(null);
    const [actionError, setActionError] = useState(null);
    const hash                  = useLocation().hash;
    const loadedOnce            = useRef(false);
    const hashHandled           = useRef(null);

    const load = useCallback(async () => {
        if (!loadedOnce.current) setLoading(true);   // reloads after post/edit/delete keep the composer mounted
        setError(null);
        try {
            // Fire all in parallel — attachments and comment-thread failures are non-fatal
            // (attachments degrade to text-only when the HAR session is stale; a 403 on the
            // thread just leaves can_post false).
            const [shopifyRes, portalRes, attachRes, threadRes] = await Promise.all([
                storeFetch(`/api/orders/${orderId}/events`),
                storeFetch(`/api/orders/${orderId}/timeline`),
                storeFetch(`/api/orders/${orderId}/timeline-attachments`),
                storeFetch(`/api/orders/${orderId}/comments`).catch(() => null),
            ]);
            const threadData  = (threadRes && threadRes.ok) ? await threadRes.json().catch(() => null) : null;
            const threadSafe  = threadData || { comments: [], can_post: false, mirror_as: null, me: null };
            setThread(threadSafe);
            if (!shopifyRes.ok) throw new Error('Failed to load events');
            const shopifyData = await shopifyRes.json();
            const portalData  = portalRes.ok ? await portalRes.json() : { events: [], items: {} };
            const attachData  = attachRes.ok ? await attachRes.json() : { attachments_by_event: {} };
            const attByEvent  = attachData.attachments_by_event || {};

            const itemsMap = portalData.items || {};

            // Imported Shopify comments are also in the REST events (verb 'comment'):
            // drop those already in the OpsHub thread so each shows once.
            const threadEventIds = new Set((threadSafe.comments || []).map(c => c.shopify_event_id).filter(Boolean).map(String));
            const shopifyEntries = (shopifyData.events || [])
                .filter(ev => !(ev.verb === 'comment' && threadEventIds.has(String(ev.id))))
                .map(ev => ({
                    _kind:      'shopify',
                    _ts:        new Date(ev.created_at).getTime(),
                    ev,
                    attachments: attByEvent[String(ev.id)] || null,
                }));
            const commentEntries = (threadSafe.comments || []).map(c => ({ _kind: 'comment', _ts: new Date(c.created_at).getTime(), c }));
            // Hide embellishment-child rows: the bundle parent's event
            // represents the whole bundle; per-child lifts are internal
            // accounting (packing an emb doesn't happen — at the moment of
            // packing the emb is already on the shirt). Filter before
            // buildSessions so children can't spawn or extend sessions.
            const _bundleChildBases = bundleChildSet(portalData.bundle_membership || {});
            // Filter always runs — the product_type=EMB fallback in
            // isBundleChildEvent catches standalone embellishments not
            // listed in bundle_membership_json (e.g. SP-255433's
            // STHL-PROBLOCK1-WHT which has product_type='Number').
            const _visibleEvents = (portalData.events || []).filter(
                e => !isBundleChildEvent(e, _bundleChildBases, itemsMap)
            );
            const allEvents = _visibleEvents;
            const sessions = buildSessions(allEvents);
            const sessionEntries = sessions.map(s => ({
                _kind:    'session',
                _ts:      s.endTs,
                session:  s,
                itemsMap,
                allEvents,
            }));

            const merged = [...shopifyEntries, ...sessionEntries, ...commentEntries].sort((a, b) => b._ts - a._ts);
            setEntries(merged);
            loadedOnce.current = true;
        } catch (e) { setError(e.message); }
        finally     { setLoading(false); }
    }, [orderId]); // eslint-disable-line

    useEffect(() => { loadedOnce.current = false; load(); }, [load]);

    // Mark mentions read whenever the loaded thread mentions the viewer.
    useEffect(() => {
        if (loading) return;
        const meId = thread.me && thread.me.user_id;
        const mentionsMe = meId != null && (thread.comments || []).some(c =>
            Object.keys(c.mentions || {}).includes(String(meId)) ||
            new RegExp(`\\[@u${meId}\\|`).test(c.body || ''));
        if (mentionsMe) storeFetch(`/api/orders/${orderId}/mentions/seen`, { method: 'POST' }).catch(() => {});
    }, [loading, thread]); // eslint-disable-line

    // Deep link (#comment-{id}): page/expand to the target first, scroll once it is rendered.
    useEffect(() => {
        if (loading || !hash || !hash.startsWith('#comment-') || hashHandled.current === hash) return;
        const idx = entries.findIndex(en => en._kind === 'comment' && `#comment-${en.c.id}` === hash);
        if (idx >= 0) {
            if (expandMode) {
                if (idx >= PER_PAGE && !showAll) { setShowAll(true); return; }
            } else {
                const needed = Math.floor(idx / PER_PAGE);
                if (Math.min(page, Math.max(0, Math.ceil(entries.length / PER_PAGE) - 1)) !== needed) { setPage(needed); return; }
            }
        }
        hashHandled.current = hash;
        if (idx >= 0) setTimeout(() => { const el = document.getElementById(hash.slice(1)); if (el) el.scrollIntoView({ block: 'center' }); }, 50);
        storeFetch(`/api/orders/${orderId}/mentions/seen`, { method: 'POST' }).catch(() => {});
    }, [loading, entries, page, showAll, hash]); // eslint-disable-line

    const focusComposer = () => setTimeout(() => {
        const t = document.querySelector('.cm-composer .cm-editor');
        if (t) { t.scrollIntoView({ block: 'center' }); t.focus(); }
    }, 0);
    const composerEl = thread.can_post ? (
        <div hidden={Boolean(editing)}>
            <CommentComposer orderId={orderId} storeFetch={storeFetch} mirrorAs={thread.mirror_as}
                prefill={replyTo ? { token: tokenFor(replyTo), nonce: replyTo.nonce } : null}
                onCancel={replyTo ? () => setReplyTo(null) : undefined}
                onPosted={() => { setReplyTo(null); load(); setTimeout(() => load(), 2500); }} />
        </div>
    ) : null;

    if (loading) return (
        <Card title={title}>
            <div className="od-timeline-status">Loading…</div>
        </Card>
    );
    if (error) return (
        <Card title={title}>
            <div className="od-timeline-error">{error}</div>
        </Card>
    );
    if (!entries.length) return (
        <Card title={title}>
            {composerEl}
            <div className="od-timeline-status">No timeline entries yet.</div>
        </Card>
    );

    const totalPages  = Math.ceil(entries.length / PER_PAGE);
    const safePage    = Math.min(page, totalPages - 1);
    const pageEntries = expandMode
        ? (showAll ? entries : entries.slice(0, PER_PAGE))
        : entries.slice(safePage * PER_PAGE, safePage * PER_PAGE + PER_PAGE);

    const fmtEvent = (iso) => {
        if (!iso) return '';
        return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
    };
    const fmtDate = (ts) => new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const fmtTime = (ts) => new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

    return (
        <Card title={title}>
            {actionError && <Banner tone="critical" onDismiss={() => setActionError(null)}>{actionError}</Banner>}
            {composerEl}
            <div>
                {pageEntries.map((entry, i) => {
                    const isLast = i === pageEntries.length - 1;
                    if (entry._kind === 'comment') {
                        const c = entry.c;
                        if (editing && editing.id === c.id) {
                            return <CommentComposer key={`edit-${c.id}`} orderId={orderId} storeFetch={storeFetch} mirrorAs={thread.mirror_as}
                                       commentId={c.id} initial={{ body: c.body, images: c.images }} onCancel={() => setEditing(null)}
                                       onPosted={() => { setEditing(null); load(); setTimeout(() => load(), 2500); }} />;
                        }
                        return <CommentEntry key={`c-${c.id}`} c={c} highlight={hash === `#comment-${c.id}`}
                                   onReply={thread.can_post ? (x) => {
                                       // A Shopify comment whose author has no OpsHub account: no token, just focus the composer.
                                       setReplyTo(x.author_user_id ? { user_id: x.author_user_id, name: x.author_name, mention_id: null, nonce: Date.now() } : null);
                                       focusComposer();
                                   } : undefined}
                                   onEdit={setEditing}
                                   onDelete={async (x) => {
                                       try {
                                           const r = await storeFetch(`/api/comments/${x.id}`, { method: 'DELETE' });
                                           if (!r || !r.ok) throw new Error('Could not delete the comment');
                                           setActionError(null);
                                       } catch (e) { setActionError(e.message || 'Could not delete the comment'); }
                                       load();
                                   }} />;
                    }
                    if (entry._kind === 'session') {
                        const s    = entry.session;
                        // Admin-portal sessions: infer workflow domain from
                        // the events themselves (e.g. mark-picked → picking).
                        // Keeps the right header/color when the action was
                        // taken via the Pressing Tools dropdown instead of a
                        // PIN-portal kiosk session. Falls back to generic
                        // "System" styling when events span multiple domains.
                        const adminDomain = sessionDomain(s);
                        const colKey = adminDomain || s.portal;
                        const col  = portalColor(colKey);
                        const bg   = PORTAL_BG[colKey] || PORTAL_BG.admin;
                        const baseVerb = PORTAL_VERB[colKey] || col.label;
                        const verb = adminDomain ? `${baseVerb} (Admin)` : baseVerb;
                        const footerTime = s.startTs === s.endTs
                            ? fmtEvent(new Date(s.startTs).toISOString())
                            : `${fmtEvent(new Date(s.startTs).toISOString())} – ${fmtTime(s.endTs)}`;
                        // Auto-cascade sessions: collapse N raw events (one
                        // pick_complete + one pack_pass per group_key) into
                        // a single row per group_key with combined chips, so
                        // "3 items got auto-completed" reads as 3 rows
                        // instead of 6 alternating ones.
                        if (s.portal === 'auto') {
                            const rows = consolidateAutoEvents(s.events, entry.allEvents);
                            // Fully suppressed by manual events — render
                            // nothing rather than an empty auto card.
                            if (!rows.length) return null;
                            return (
                                <div key={`session-${i}-${s.startTs}`} className={`event-log-item event-log-item--portal${isLast ? '' : ' event-log-item--bordered'}`} style={{ background: bg }}>
                                    <div className="event-log-dot-col">
                                        <div className="event-log-dot" style={{ background: col.bar, borderColor: col.bar }} />
                                        {!isLast && <div className="event-log-line" />}
                                    </div>
                                    <div className="event-log-content">
                                        <div className="event-log-verb">{verb}</div>
                                        <div className="event-log-portal-rows">
                                            {rows.map((r, j) => {
                                                const item = entry.itemsMap[r.group_key];
                                                const parsed = parseGroupKey(r.group_key);
                                                const niceBase = formatGroupKey(parsed.base, '');
                                                const base = (item && item.title) ? (niceBase ? `${item.title} / ${niceBase}` : item.title) : niceBase;
                                                return (
                                                    <div key={r.group_key || j} className="event-log-portal-row">
                                                        {r.picked && (
                                                            <span className="event-log-portal-row__badge" style={{ background: '#bbf7d0', color: '#166534' }}>
                                                                Picked
                                                            </span>
                                                        )}
                                                        {r.pressed && (
                                                            <span className="event-log-portal-row__badge" style={{ background: '#fed7aa', color: '#9a3412' }}>
                                                                Pressed
                                                            </span>
                                                        )}
                                                        {r.packed && (
                                                            <span className="event-log-portal-row__badge" style={{ background: '#bbf7d0', color: '#166534' }}>
                                                                Packed
                                                            </span>
                                                        )}
                                                        <span className="event-log-portal-row__text">{base}<SkuChips parsed={parsed} item={item} /></span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <div className="event-log-time">{footerTime}</div>
                                    </div>
                                </div>
                            );
                        }
                        return (
                            <div key={`session-${i}-${s.startTs}`} className={`event-log-item event-log-item--portal${isLast ? '' : ' event-log-item--bordered'}`} style={{ background: bg }}>
                                <div className="event-log-dot-col">
                                    <div className="event-log-dot" style={{ background: col.bar, borderColor: col.bar }} />
                                    {!isLast && <div className="event-log-line" />}
                                </div>
                                <div className="event-log-content">
                                    <div className="event-log-verb">{verb}</div>
                                    <div className="event-log-portal-rows">
                                        {collapseRepeated(s.events.filter(e => !isCascadePressAutoPick(e) && !isSessionBoundary(e))).map(({ ev, count }, j) => {
                                            const badge = portalRowBadge(ev);
                                            const isPickup = ev.event_type === 'picked_up';
                                            const sigUrl   = isPickup ? (ev.payload?.signature_data_url) : null;
                                            const custName = isPickup ? (ev.payload?.customer_name) : null;
                                            return (
                                                <div key={ev.id || j} className="event-log-portal-row" style={isPickup ? { display: 'block' } : undefined}>
                                                    {badge && (
                                                        <span
                                                            className="event-log-portal-row__badge"
                                                            style={{ background: badge.bg, color: badge.color }}
                                                        >
                                                            {badge.text}
                                                        </span>
                                                    )}
                                                    {(() => {
                                                        const mgrOv = ev.payload?.manager_override;
                                                        if (!mgrOv || (!mgrOv.name && !mgrOv.first_name)) return null;
                                                        const mgrName = mgrOv.name
                                                            || `${mgrOv.first_name || ''} ${mgrOv.last_name || ''}`.trim();
                                                        return (
                                                            <span
                                                                className="event-log-portal-row__badge"
                                                                style={{
                                                                    background: '#dcfce7', color: '#166534',
                                                                    border: '1px solid #86efac', fontWeight: 700,
                                                                    marginLeft: 4,
                                                                }}
                                                                title="Scan-only override authorized by this manager"
                                                            >
                                                                {`Manager Override — ${mgrName}`}
                                                            </span>
                                                        );
                                                    })()}
                                                    <span className="event-log-portal-row__text">
                                                        {portalRowText(ev, entry.itemsMap, count)}
                                                    </span>
                                                    {isPickup && (custName || sigUrl) && (
                                                        <div style={{ marginTop: 6, padding: '8px 10px', background: '#fff', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                                                            {custName && (
                                                                <div style={{ fontSize: 13, marginBottom: sigUrl ? 6 : 0 }}>
                                                                    <strong>Customer Printed Name:</strong> {custName}
                                                                </div>
                                                            )}
                                                            {sigUrl && (
                                                                <div>
                                                                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Customer Signature</div>
                                                                    <img src={sigUrl} alt="Customer signature"
                                                                         style={{ maxWidth: 320, maxHeight: 100, background: '#fff', border: '1px solid #d1d5db', borderRadius: 4 }} />
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="event-log-time">
                                        {s.displayName && <span className="event-log-author">{s.displayName}</span>}
                                        {footerTime}
                                    </div>
                                </div>
                            </div>
                        );
                    }
                    const ev = entry.ev;
                    // Any comment with image attachments goes through
                    // ShopifyRichEvent — it's the only path that renders
                    // the attachment thumbnails. RICH_EVENT_VERBS still
                    // captures the expandable "Change Location" flow.
                    if (RICH_EVENT_VERBS.has(ev.verb) || (entry.attachments && entry.attachments.length > 0)) {
                        return <ShopifyRichEvent defaultOpen={expandMode} key={`shopify-${ev.id || i}`} ev={ev} isLast={isLast}
                                                 fmtEvent={fmtEvent}
                                                 attachments={entry.attachments} />;
                    }
                    return (
                        <div key={`shopify-${ev.id || i}`} className={`event-log-item${isLast ? '' : ' event-log-item--bordered'}`}>
                            <div className="event-log-dot-col">
                                <div className="event-log-dot" />
                                {!isLast && <div className="event-log-line" />}
                            </div>
                            <div className="event-log-content">
                                <div className="event-log-verb">
                                    {verbLabel(ev.verb)}
                                </div>
                                {ev.message && ev.message !== verbLabel(ev.verb) && (
                                    <div className="event-log-msg">{renderShopifyMessage(ev.message)}</div>
                                )}
                                {ev.verb === 'cancelled' && cancelReason && (
                                    <div className="event-log-msg">
                                        Reason: {CANCEL_REASON_LABELS[cancelReason] || cancelReason.replace(/_/g, ' ').toLowerCase()}
                                    </div>
                                )}
                                <div className="event-log-time">
                                    {ev.author && <span className="event-log-author">{ev.author}</span>}
                                    {fmtEvent(ev.created_at)}
                                </div>
                            </div>
                        </div>
                    );
                })}

                {/* Pagination */}
                {!expandMode && totalPages > 1 && (
                    <div className="event-log-pagination">
                        <button
                            disabled={safePage === 0}
                            onClick={() => setPage(p => Math.max(0, p - 1))}
                            className="event-log-page-btn">
                            ← Newer
                        </button>
                        <span className="event-log-page-info">
                            {safePage * PER_PAGE + 1}–{Math.min(safePage * PER_PAGE + PER_PAGE, entries.length)} of {entries.length}
                        </span>
                        <button
                            disabled={safePage >= totalPages - 1}
                            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                            className="event-log-page-btn">
                            Older →
                        </button>
                    </div>
                )}
                {expandMode ? (
                    entries.length > PER_PAGE && (
                        <div style={{ textAlign: 'center', padding: '10px 0 4px' }}>
                            {showAll
                                ? <span style={{ fontSize: 12, color: '#6d7175' }}>Showing all {entries.length} events</span>
                                : <button type="button" className="event-log-page-btn" onClick={() => setShowAll(true)}>
                                      View Full Event Log ({entries.length} events)
                                  </button>}
                        </div>
                    )
                ) : (
                    <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
                        <a href={`/orders/${orderId}?view=timeline`} style={{ fontSize: 12, color: '#2c6ecb' }}>View Full Timeline</a>
                    </div>
                )}
            </div>
        </Card>
    );
}


function parseTimelineParams(searchParams) {
    const vs = parseInt(searchParams.get('vs'), 10);
    const ve = parseInt(searchParams.get('ve'), 10);
    const now = Date.now();
    if (
        !isFinite(vs) || !isFinite(ve) ||
        vs >= ve ||
        vs > now ||
        Math.abs(vs - now) > 10 * 365 * 86400000 ||
        Math.abs(ve - now) > 10 * 365 * 86400000
    ) return null;
    return { vs, ve };
}

// ── Main component ────────────────────────────────────────────────────────────

export default function OrderDetail() {
    const [searchParamsForFlag] = useSearchParams();
    const { user } = useAuth();
    // The preference lives in the auth context, so flipping the switch while
    // on an order re-renders the other view at once.
    const v = searchParamsForFlag.get('v');
    // The nav "v3" switch (users.preferences.orders_v3) opens V3; ?v=1 forces
    // classic and ?v=2 opens V2 explicitly. The old orders_v2 preference no
    // longer routes anywhere (its switch was replaced by v3).
    if (v === '3' || (v !== '1' && v !== '2' && !!user?.preferences?.orders_v3)) return <OrderDetailV3 />;
    if (v === '2') return <OrderDetailV2 />;
    return <OrderDetailV1 />;
}

function OrderDetailV1() {
    const { orderId, storeSlug } = useParams();
    const navigate    = useNavigate();
    const { user, emulating, startEmulation, stopEmulation } = useAuth();
    const { activeStore, setActiveStore, STORE_DISPLAY_NAMES: _storeNames } = useStore();
    const _storeFetch = useAuthFetch();
    const [searchParams, setSearchParams] = useSearchParams();
    const location       = useLocation();

    // ── URL-driven emulation (2026-07-06) ─────────────────────────────────
    // Mirror of OrdersTanStack's URL↔emu sync so that opening an order via
    // a link with `?loc=<sid>` sets emulation to that loc — makes split-
    // order views/prints route through the specific location's perspective.
    // `?loc=all` clears emulation. Ref-guarded to prevent ping-pong with
    // any other setSearchParams callers, and gated on urlEmuLocs being
    // loaded so the first render (emulating=null) doesn't clobber an
    // incoming ?loc=<sid> to ?loc=all.
    const [urlEmuLocs, setUrlEmuLocs] = React.useState([]);
    // 2026-09-02: "Review address issues" dialog state (hooks must sit above any early return).
    const [addrReviewOpen, setAddrReviewOpen] = useState(false);
    const [addrReview, setAddrReview] = useState(null);      // GET /address-review payload
    const [addrMode, setAddrMode] = useState('review');       // 'review' | 'edit'
    const [addrChoice, setAddrChoice] = useState('suggestion');
    const [addrSaveCust, setAddrSaveCust] = useState(false);
    const [addrForm, setAddrForm] = useState(null);
    const [addrBusy, setAddrBusy] = useState(false);
    const [addrErr, setAddrErr] = useState('');
    React.useEffect(() => {
        let cancelled = false;
        _storeFetch('/api/locations').then(r => r.ok ? r.json() : []).then(rows => {
            if (!cancelled) setUrlEmuLocs(Array.isArray(rows) ? rows : []);
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [_storeFetch, activeStore]);

    const urlLocParam = searchParams.get('loc');
    const lastSyncedLoc = React.useRef(null);
    React.useEffect(() => {
        if (urlLocParam == null) return;
        if (String(lastSyncedLoc.current) === String(urlLocParam)) return;
        if (urlLocParam === 'all' || urlLocParam === '') {
            lastSyncedLoc.current = 'all';
            if (emulating) stopEmulation();
            return;
        }
        if (!urlEmuLocs.length) return;
        const target = urlEmuLocs.find(l =>
            String(l.shopify_location_id) === String(urlLocParam)
        );
        if (!target) return;
        lastSyncedLoc.current = String(target.shopify_location_id);
        if (emulating?.shopify_location_id !== target.shopify_location_id) {
            startEmulation({
                id: target.id, name: target.name,
                shopify_location_id: target.shopify_location_id,
                is_franchise: target.is_franchise || false,
                store_key: activeStore,
            });
        }
    }, [urlLocParam, urlEmuLocs, emulating, startEmulation, stopEmulation, activeStore]);

    React.useEffect(() => {
        if (!urlEmuLocs.length) return;
        const desired = emulating?.shopify_location_id
            ? String(emulating.shopify_location_id) : 'all';
        if (String(lastSyncedLoc.current) === desired) return;
        if (String(urlLocParam || '') === desired) {
            lastSyncedLoc.current = desired;
            return;
        }
        lastSyncedLoc.current = desired;
        const next = new URLSearchParams(searchParams);
        next.set('loc', desired);
        setSearchParams(next, { replace: true });
    }, [emulating, urlLocParam, urlEmuLocs, searchParams, setSearchParams]);
    // Polaris-styled toast for print-restriction (replaces window.alert).
    // Auto-dismisses after 4s. {msg, key} re-mounts on repeat clicks.
    const [odPrintToast, setOdPrintToast] = useState(null);
    useEffect(() => {
        if (!odPrintToast) return;
        const t = setTimeout(() => setOdPrintToast(null), 4000);
        return () => clearTimeout(t);
    }, [odPrintToast]);

    // "Back to Orders" target preserves the page/filters the user was on. Sources,
    // in priority order:
    //   1. location.state.backTo — set by Orders.jsx#openOrder when navigating in
    //   2. sessionStorage('ordersBackTo') — survives a refresh of OrderDetail
    //   3. /store/:sk/orders — clean fallback when arriving at OrderDetail directly
    const { emulating: _emuForBack } = useAuth();
    const backToHref = (() => {
        const fromState = location.state?.backTo;
        if (fromState) {
            try { sessionStorage.setItem('ordersBackTo', fromState); } catch {}
        }
        let cached = fromState || null;
        if (!cached) { try { cached = sessionStorage.getItem('ordersBackTo'); } catch {} }
        return resolveOrdersBackTo(cached, activeStore || storeSlug, _emuForBack);
    })();

    // Prev/Next cursor — read the ordered numeric-id list saved by
    // OrdersTanStack#openOrder. Only valid for the same store the list was
    // captured from (sessionStorage survives across stores in the same tab).
    // Exposed as { ids, idx } where idx == -1 means "current order isn't in
    // the cached list" (e.g. user landed via deep link or after re-filter).
    const prevNextCursor = useMemo(() => {
        const sk = activeStore || storeSlug || 'soccerpost';
        try {
            const raw = sessionStorage.getItem('ordersList');
            if (!raw) return { ids: [], idx: -1, store: sk };
            const env = JSON.parse(raw);
            const ids = Array.isArray(env?.ids) ? env.ids : [];
            const listStore = env?.store || sk;
            // Don't navigate prev/next across stores — different listings.
            if (listStore !== sk) return { ids: [], idx: -1, store: sk };
            const idx = ids.indexOf(String(orderId));
            return { ids, idx, store: sk };
        } catch {
            return { ids: [], idx: -1, store: sk };
        }
    }, [orderId, activeStore, storeSlug]);
    const prevOrderId = prevNextCursor.idx > 0
        ? prevNextCursor.ids[prevNextCursor.idx - 1] : null;
    const nextOrderId = (prevNextCursor.idx >= 0 && prevNextCursor.idx < prevNextCursor.ids.length - 1)
        ? prevNextCursor.ids[prevNextCursor.idx + 1] : null;
    const goAdjacent = (id) => {
        if (!id) return;
        navigate(`/store/${prevNextCursor.store}/orders/${id}`,
                 { state: { backTo: backToHref } });
    };

    const [order, setOrder]           = useState(null);
    const [resolvedStore, setResolvedStore] = useState(null);
    const [loading, setLoading]       = useState(true);
    const [error, setError]           = useState(null);
    const [storedLabels, setLabels]   = useState([]);
    const [portalState, setPortalState] = useState({});

    // Team Portal number override lookup — keyed by player name so the
    // ItemsTable can style the number red without threading player_id through
    // every row. Empty {} means no overrides in play for this order.
    const tpOverridesByName = useMemo(() => {
        const out = {};
        for (const ov of ((order && order.tp_number_overrides) || [])) {
            const nm = (ov && ov.player_name || '').trim();
            if (nm) out[nm] = ov;
        }
        return out;
    }, [order]);

    // Team Portal per-line-item requirement classifications (REQUIRED /
    // DEFAULT / RECOMMENDED / OFF-SPEC). Keyed by Shopify LI gid so the
    // ItemsTable can render the badge next to each parent title. Silent
    // {} when the backend returns no tp_requirements block (non-TP order,
    // or an order last cached before tp_player_ids/tp_team_ids populated).
    const tpReqByLiId = useMemo(() => {
        const out = {};
        for (const l of (order?.tp_requirements?.line_items || [])) {
            if (l && l.line_item_gid) out[l.line_item_gid] = l;
        }
        return out;
    }, [order]);

    // "Copied" feedback for the customer email copy button. Stamped to a
    // monotonic id so consecutive copies still re-trigger the badge animation.
    const [emailCopiedAt, setEmailCopiedAt] = useState(0);

    // Print state
    const [printReady, setPrintReady]           = useState(false);
    const [showPrintConfirm, setShowPrintConfirm] = useState(false);
    const [printFull, setPrintFull] = useState(false);   // 2026-09-25: reprint every line, not just unfulfilled
    // 2026-09-26 (Eric): 'Merge NJ Warehouses' — offered when the order has open items at
    // both Tinton Falls and Eatontown and this print is scoped to one of them. Every
    // print then goes through the Print order modal; the choice sticks per browser.
    const [showPrintOptions, setShowPrintOptions] = useState(false);
    const [mergeNj, setMergeNjState] = useState(() => readMergeNjPref());
    const setMergeNj = (on) => { setMergeNjState(on); writeMergeNjPref(on); };
    const [printMergedLocs, setPrintMergedLocs] = useState(null);   // [{id, name, bins, overstockBins}] while a merged ticket is being built
    const [showOnHoldConfirm, setShowOnHoldConfirm] = useState(false);
    const [printBins, setPrintBins]             = useState({});   // {lineItem_gid: bin_string}
    const [printOverstockBins, setPrintOverstockBins] = useState({}); // {lineItem_gid: overstock_bin_string}
    const [printPreviews, setPrintPreviews]     = useState({});   // {product_gid: {fontFamily, fontUrl, ...}}
    // Preview settings for the ORDER VIEW itself (separate from print — the
    // order-detail page displays embellishment thumbs in bundle sub-rows and
    // wants the same rendered-number treatment as the printout).
    const [numberPreviews, setNumberPreviews]   = useState({});
    const printPopupRef  = useRef(null);
    const printSourceRef = useRef(null);
    // Per-store printout footer HTML — backend-sanitized. Fetched once per
    // store change; passed to OrderPrintCard which renders at the bottom of
    // the print page. Falls back to the bundled default if the fetch fails.
    const [printoutFooterHtml, setPrintoutFooterHtml] = useState(null);
    useEffect(() => {
        const sk = resolvedStore || activeStore || 'soccerpost';
        let cancelled = false;
        authenticatedFetch(`/api/store/printout-footer?store=${sk}`)
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (!cancelled && d?.html != null) setPrintoutFooterHtml(d.html); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [resolvedStore, activeStore]);

    // When the order was found in a different store, force all subsequent API calls to use that store
    const storeFetch = useCallback((url, options = {}, useStore = true) => {
        if (resolvedStore && !url.includes('?store=')) {
            const sep = url.includes('?') ? '&' : '?';
            return _storeFetch(`${url}${sep}store=${resolvedStore}`, options, false);
        }
        return _storeFetch(url, options, useStore);
    }, [_storeFetch, resolvedStore]);

    const timelineParams = parseTimelineParams(searchParams);
    const [showTimeline, setShowTimeline]           = useState(() => searchParams.get('view') === 'timeline');
    const [initialViewStart, setInitialViewStart]   = useState(() => timelineParams?.vs);
    const [initialViewEnd,   setInitialViewEnd]     = useState(() => timelineParams?.ve);
    const [refundModalOpen, setRefundModalOpen]     = useState(false);
    const [reportIssueOpen, setReportIssueOpen]     = useState(false);

    // Presence (who is viewing this order)
    const [viewers, setViewers] = useState([]);

    // "View In" menu state
    const [viewMenuOpen, setViewMenuOpen] = useState(false);
    const viewMenuRef                     = useRef(null);

    useEffect(() => {
        if (!viewMenuOpen) return;
        const handler = (e) => {
            if (viewMenuRef.current && !viewMenuRef.current.contains(e.target)) {
                setViewMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [viewMenuOpen]);

    // "Tools" menu state
    const [toolsMenuOpen, setToolsMenuOpen] = useState(false);
    const toolsMenuRef                      = useRef(null);

    // "Change Pickup Location" modal state
    const [showPickupLocModal, setShowPickupLocModal] = useState(false);
    const [pickupLocList,      setPickupLocList]      = useState([]);
    const [pickupLocSelected,  setPickupLocSelected]  = useState('');
    const [pickupLocLoading,   setPickupLocLoading]   = useState(false);
    const [pickupLocSaving,    setPickupLocSaving]    = useState(false);
    const [pickupLocError,     setPickupLocError]     = useState(null);

    const openPickupLocModal = useCallback(async () => {
        setToolsMenuOpen(false);
        setPickupLocError(null);
        setPickupLocSelected(order?.store_pickup_override || '');
        setShowPickupLocModal(true);
        setPickupLocLoading(true);
        try {
            const res = await storeFetch('/api/store-pickup-locations');
            const data = await res.json();
            setPickupLocList(data.locations || []);
        } catch (e) {
            setPickupLocError('Failed to load location list: ' + e.message);
        } finally {
            setPickupLocLoading(false);
        }
    }, [order, storeFetch]);

    const handlePickupLocSave = useCallback(async () => {
        if (!pickupLocSelected) return;
        setPickupLocSaving(true);
        setPickupLocError(null);
        try {
            const res = await storeFetch(`/api/orders/${orderId}/store-pickup-override`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ location: pickupLocSelected }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.detail || `HTTP ${res.status}`);
            }
            setShowPickupLocModal(false);
            await loadOrder();
        } catch (e) {
            setPickupLocError(e.message);
        } finally {
            setPickupLocSaving(false);
        }
    }, [orderId, pickupLocSelected, storeFetch]);

    const handlePickupLocClear = useCallback(async () => {
        setPickupLocSaving(true);
        setPickupLocError(null);
        try {
            const res = await storeFetch(`/api/orders/${orderId}/store-pickup-override`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ clear: true }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.detail || `HTTP ${res.status}`);
            }
            setShowPickupLocModal(false);
            await loadOrder();
        } catch (e) {
            setPickupLocError(e.message);
        } finally {
            setPickupLocSaving(false);
        }
    }, [orderId, storeFetch]);

    useEffect(() => {
        if (!toolsMenuOpen) return;
        const handler = (e) => {
            if (toolsMenuRef.current && !toolsMenuRef.current.contains(e.target)) setToolsMenuOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [toolsMenuOpen]);

    const handleResync = useCallback(async () => {
        setToolsMenuOpen(false);
        try {
            const res = await storeFetch(`/api/orders/${orderId}/resync`, { method: 'POST' });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                alert(`Resync failed: ${data.detail || res.status}`);
                return;
            }
            await loadOrder();
        } catch (e) {
            alert(`Resync failed: ${e.message}`);
        }
    }, [orderId, storeFetch]);

    const handleToolAction = useCallback(async (action) => {
        setToolsMenuOpen(false);
        const confirmMessages = {
            reset_picking:    'Reset all picking history for this order? This cannot be undone.',
            reset_pressing:   'Reset all pressing history for this order? This cannot be undone.',
            reset_packing:    'Reset all packing history for this order? This cannot be undone.',
            mark_all_picked:  'Mark all items as fully picked?',
            mark_all_pressed: 'Mark all items as fully pressed?',
        };
        if (!window.confirm(confirmMessages[action])) return;
        const groups = Object.entries(portalState)
            .filter(([k]) => k !== '_pressing_summary')
            .map(([group_key, state]) => ({
                group_key,
                qty: state.qty_total || 1,
            }));
        try {
            const res = await storeFetch(`/api/pressing/orders/${orderId}/tools`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action, order_name: order?.name, groups }),
            });
            if (!res.ok) throw new Error('failed');
            const state = await storeFetch(`/api/orders/${orderId}/portal-state`)
                .then(r => r.ok ? r.json() : {});
            setPortalState(state);
        } catch {
            alert('Action failed. Please try again.');
        }
    }, [orderId, order, portalState]);

    useEffect(() => { loadOrder(); }, [orderId]);

    // SKU notifications for this order — {sku: message}. Rendered in red
    // next to the SKU column in both bundle and line-item rows below.
    const [skuNotifications, setSkuNotifications] = useState({});
    useEffect(() => {
        if (!orderId || orderId === 'null') return;
        storeFetch(`/api/sku-notifications/for-order/${orderId}`)
            .then(r => r.ok ? r.json() : { sku_notifications: {} })
            .then(j => setSkuNotifications(j.sku_notifications || {}))
            .catch(() => setSkuNotifications({}));
    }, [orderId, storeFetch]);

    // Populate session cache for pickup-store colors if user landed here directly
    // without visiting the Orders list first. One-shot; subsequent navigations
    // inside this tab reuse the cached map.
    useEffect(() => {
        try {
            if (sessionStorage.getItem('ops_store_colors_v1')) return;
        } catch {}
        storeFetch('/api/store-colors')
            .then(r => r.ok ? r.json() : {})
            .then(m => {
                try { sessionStorage.setItem('ops_store_colors_v1', JSON.stringify(m || {})); } catch {}
            })
            .catch(() => {});
    }, [storeFetch]);

    // Presence: heartbeat every 8s while viewing this order
    useEffect(() => {
        if (!orderId || orderId === 'null') return;
        const beat = () => {
            storeFetch(`/api/orders/${orderId}/presence`, { method: 'POST' })
                .then(r => r.ok ? r.json() : [])
                .then(setViewers)
                .catch(() => {});
        };
        beat();
        const interval = setInterval(beat, 8000);
        return () => clearInterval(interval);
    }, [orderId]); // eslint-disable-line

    const loadOrder = async () => {
        if (!orderId || orderId === 'null') return;
        setLoading(true); setError(null);
        try {
            const orderRes = await _storeFetch(`/api/orders/gid://shopify/Order/${orderId}`);
            if (!orderRes.ok) throw new Error('Order not found');
            const orderData = await orderRes.json();
            setOrder(orderData);
            // Backend returns _store_key = the actual store the order was found in
            const rs = orderData._store_key;
            if (rs) {
                setResolvedStore(rs);
                // If we're on a /store/:slug/... path and the order actually belongs to a
                // different store, replace the URL so the slug reflects reality. The
                // route re-mount will reload the order from the correct store.
                // Exception: when the user came from /store/all/orders, keep the
                // 'all' slug so Back returns to the All-Stores list. Internal
                // fetches still use the resolved store via ?store=<rs>.
                if (storeSlug && storeSlug !== 'all' && rs !== storeSlug && rs !== 'all') {
                    stopEmulation();
                    navigate(`/store/${rs}/orders/${orderId}`, { replace: true });
                    return;
                }
                // Legacy route (no storeSlug): keep the old setActiveStore behavior.
                // When the URL slug is explicitly 'all', leave activeStore alone so
                // back-navigation returns to the All-Stores list.
                if (storeSlug !== 'all' && rs !== activeStore && rs !== 'all') {
                    setActiveStore(rs);
                    stopEmulation();
                }
                // Emulate the order's location so View As matches — but ONLY when
                // the user already has an emulation set. If they were at "All
                // Locations" (no emulation), keep them there so the Back button
                // returns to All Locations instead of bouncing into a specific FO.
                // If the existing emulation matches one of the order's FOs, KEEP it
                // so opening a multi-FO order doesn't silently switch their scope.
                //
                // 2026-07-06: also skip this whole legacy switcher when the URL
                // has a `?loc=` param — URL is source of truth in that case, and
                // this block would compete with the URL→emulation sync effect
                // above.
                const _hasLocParam = searchParams.get('loc') != null;
                if (emulating && !_hasLocParam) {
                    const fos = (orderData.fulfillmentOrders?.edges || []).map(e => e.node);
                    const foLocIds = fos
                        .map(fo => fo.assignedLocation?.location?.id?.split('/')?.pop())
                        .filter(Boolean);
                    const currentEmuMatchesFO = foLocIds.some(id =>
                        String(id) === String(emulating.shopify_location_id)
                    );
                    if (!currentEmuMatchesFO && foLocIds.length > 0) {
                        const shopifyLocId = foLocIds[0];
                        _storeFetch(`/api/locations?store=${rs}`)
                            .then(r => r.ok ? r.json() : [])
                            .then(locs => {
                                const match = locs.find(l => String(l.shopify_location_id) === shopifyLocId);
                                if (match) startEmulation({ id: match.id, name: match.name, shopify_location_id: match.shopify_location_id, is_franchise: match.is_franchise || false, store_key: rs });
                            })
                            .catch(() => {});
                    }
                }
            }
            const sq = rs ? `?store=${rs}` : '';
            // Load labels and portal state from the resolved store. If the order
            // is fulfilled but our DB has no labels (e.g. label bought outside
            // Operations Hub — via Shopify Admin / ShipStation / etc), ask the
            // backend to ingest existing labels from Shopify and re-fetch. Same
            // self-healing path the Packing Portal uses (QCActionBar.AlreadyDoneBar).
            (async () => {
                try {
                    const fetchLabels = async () => {
                        const r = await _storeFetch(`/api/orders/${orderId}/shipping-labels${sq}`);
                        if (!r.ok) return [];
                        const d = await r.json();
                        return d.labels || [];
                    };
                    let arr = await fetchLabels();
                    const status = (orderData?.displayFulfillmentStatus || '').toUpperCase();
                    const isShipped = status === 'FULFILLED' || status === 'PARTIALLY_FULFILLED';
                    // Only roles the recover endpoint accepts — otherwise every
                    // CS page-view of a label-less fulfilled order fires a
                    // guaranteed 403 into the logs (noise, no user impact).
                    const canRecover = hasRole(user, ['admin', 'franchise', 'site_manager', 'user', 'store_manager']);
                    if (arr.length === 0 && isShipped && canRecover) {
                        try {
                            await _storeFetch(
                                `/api/orders/${orderId}/shipping-labels/recover${sq}`,
                                { method: 'POST' },
                            );
                            arr = await fetchLabels();
                        } catch (_) { /* keep empty array */ }
                    }
                    setLabels(arr);
                } catch (_) { setLabels([]); }
            })();
            _storeFetch(`/api/orders/${orderId}/portal-state${sq}`)
                .then(r => r.ok ? r.json() : {})
                .then(state => setPortalState(state))
                .catch(() => {});
            // Load player-number-preview settings so embellishment thumbs in
            // the order view render the actual printed number in the product's
            // Team-Portal typeface (same treatment as the printout).
            _storeFetch(`/api/orders/${orderId}/number-previews${sq}`)
                .then(r => r.ok ? r.json() : { previews: {} })
                .then(d => setNumberPreviews(d.previews || {}))
                .catch(() => {});
        } catch (e) { setError(e.message); }
        finally { setLoading(false); }
    };

    // Write print content to popup when rendered. Wait for images in the
    // hidden source div to finish loading first — SmartProductImage's
    // onLoad canvas analysis (which sets the grey background for
    // white-on-white products) has to fire + re-render BEFORE we capture
    // innerHTML, otherwise the popup sees the pre-load state without any
    // grey backgrounds baked in.
    useEffect(() => {
        if (!printReady || !printSourceRef.current || !printPopupRef.current) return;
        const popup = printPopupRef.current;
        if (popup.closed) { setPrintReady(false); return; }
        let cancelled = false;
        const captureAndWrite = () => {
            if (cancelled) return;
            if (popup.closed) { setPrintReady(false); return; }
            const content = printSourceRef.current.innerHTML;
            popup.document.open();
            popup.document.write(buildPopupHTML(content));
            popup.document.close();
        // Tag as printed at the current location using the new per-location endpoint.
        // Resolve the in-scope shopify_location_id from emulating or the order's FO location.
        const _printSloc = emulating?.shopify_location_id || order?._shopify_location_id || null;
        // 2026-09-26: a merged NJ ticket tags both warehouses (each had items on it).
        const _slocs = printMergedLocs ? printMergedLocs.map(l => l.id) : (_printSloc ? [_printSloc] : []);
        // One request for every warehouse on the ticket (2026-10-07, B2B-93748).
        if (_slocs.length) {
            storeFetch(`/api/orders/${orderId}/printed_at_location`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ shopify_location_id: String(_slocs[0]), shopify_location_ids: _slocs.map(String), action: 'add' }),
            }).catch(() => {});
        }
        // Log print event to timeline
        const alreadyPrinted = isPrinted(order?.tags);
        storeFetch(`/api/orders/${orderId}/log-event`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                event_type: alreadyPrinted ? 'order_reprint' : 'order_print',
                order_name: order?.name,
                portal_username: user?.user || null,
                payload: { full_order: !!printFull, ...(printMergedLocs ? { merged_nj_warehouses: true, locations: printMergedLocs.map(l => l.name) } : {}) },
            }),
        }).catch(() => {});
        setPrintReady(false);
        setPrintMergedLocs(null);
        printPopupRef.current = null;
        };
        // Wait for every <img> in the hidden print source to load (or error),
        // then give SmartProductImage one animation frame to settle its
        // state-driven grey background, THEN capture + write to popup.
        const imgs = Array.from(printSourceRef.current.querySelectorAll('img'));
        const pending = imgs.filter(i => !i.complete || i.naturalWidth === 0);
        if (pending.length === 0) {
            requestAnimationFrame(() => requestAnimationFrame(captureAndWrite));
        } else {
            let left = pending.length;
            const tick = () => { if (--left <= 0) requestAnimationFrame(() => requestAnimationFrame(captureAndWrite)); };
            pending.forEach(img => {
                img.addEventListener('load',  tick, { once: true });
                img.addEventListener('error', tick, { once: true });
            });
            // Safety net — fire at 2s so a hung image never blocks the popup forever.
            setTimeout(() => { if (!cancelled) captureAndWrite(); }, 2000);
        }
        return () => { cancelled = true; };
    }, [printReady, orderId]);

    // Fetch bin locations for the Bin column in the new print layout. Best-
    // effort — if no picking metafield configured, returns {} and the column
    // stays blank. Scope to the viewing location: without ?shopify_location_id
    // the BE auto-picks the first FO's location, which on multi-location
    // orders is often Tinton Falls Warehouse instead of the user's view.
    const fetchBins = useCallback(async () => {
        let sloc = emulating?.shopify_location_id || '';
        if (!sloc) {
            const myLocsForStore = (user?.locations || [])
                .filter(l => (l.store_key || 'soccerpost') === (activeStore || 'soccerpost')
                          && l.shopify_location_id && String(l.shopify_location_id) !== '0');
            if (myLocsForStore.length === 1) sloc = String(myLocsForStore[0].shopify_location_id);
        }
        const url = sloc
            ? `/api/orders/${orderId}/bin-locations?shopify_location_id=${encodeURIComponent(sloc)}`
            : `/api/orders/${orderId}/bin-locations`;
        try {
            const res = await storeFetch(url);
            if (!res.ok) return { bins: {}, overstock: {} };
            const data = await res.json();
            return { bins: data.bins || {}, overstock: data.overstock || {} };
        } catch (_) { return { bins: {}, overstock: {} }; }
    }, [storeFetch, orderId, emulating, user, activeStore]);
    // Fetch per-product player-number-preview metaobjects so the printout
    // can render a player-number thumbnail in the product's own typeface
    // (Team-Portal font/color) instead of the generic Shopify image.
    const fetchPreviews = useCallback(async () => {
        try {
            const res = await storeFetch(`/api/orders/${orderId}/number-previews`);
            if (!res.ok) return {};
            const data = await res.json();
            return data.previews || {};
        } catch (_) { return {}; }
    }, [storeFetch, orderId]);

    const _printScopeSloc = (() => {
        if (emulating?.shopify_location_id) return String(emulating.shopify_location_id);
        const my = (user?.locations || []).filter(l => (l.store_key || 'soccerpost') === (activeStore || 'soccerpost') && l.shopify_location_id && String(l.shopify_location_id) !== '0');
        return my.length === 1 ? String(my[0].shopify_location_id) : null;
    })();
    const njLocsOnOrder = useMemo(() => njWarehouseLocsOnOrder(order, { all: printFull }), [order, printFull]);
    const njOfferOD = njMergeAllowed(activeStore) && njLocsOnOrder.length === 2 && !!_printScopeSloc && njLocsOnOrder.some(l => String(l.id) === _printScopeSloc);
    const startPrint = useCallback((merge) => {
        setShowPrintOptions(false);
        const popup = window.open('', '', 'width=920,height=700,scrollbars=yes,resizable=yes');
        if (!popup) { alert('Please allow popups for this site to enable printing.'); return; }
        popup.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Loading…</title></head>\n<body style="font-family:sans-serif;padding:40px;color:#374151"><p>Loading order…</p></body></html>`);
        printPopupRef.current = popup;
        if (merge) {
            Promise.all([
                Promise.all(njLocsOnOrder.map(async l => {
                    try {
                        const r = await storeFetch(`/api/orders/${orderId}/bin-locations?shopify_location_id=${encodeURIComponent(l.id)}`);
                        const j = r.ok ? await r.json() : {};
                        return { ...l, bins: j.bins || {}, overstockBins: j.overstock || {} };
                    } catch (_) { return { ...l, bins: {}, overstockBins: {} }; }
                })),
                fetchPreviews(),
            ]).then(([locs, previews]) => { setPrintMergedLocs(locs); setPrintPreviews(previews); setPrintReady(true); });
            return;
        }
        setPrintMergedLocs(null);
        Promise.all([fetchBins(), fetchPreviews()]).then(([binsResp, previews]) => {
            setPrintBins(binsResp.bins || {});
            setPrintOverstockBins(binsResp.overstock || {});
            setPrintPreviews(previews); setPrintReady(true);
        });
    }, [njLocsOnOrder, storeFetch, orderId, fetchBins, fetchPreviews]);

    const handlePrint = useCallback(() => {
        // Block on-hold orders first — show the on-hold modal with a
        // "Print anyway" escape. Per spec, all roles get the same override.
        const ffStatus = (order?._cache?.effective_fulfillment_status
                       ?? order?.displayFulfillmentStatus
                       ?? '').toUpperCase();
        if (ffStatus === 'ON_HOLD') {
            setShowOnHoldConfirm(true);
            return;
        }
        if (njOfferOD) { setShowPrintOptions(true); return; }   // 2026-09-26: the Print order modal decides
        // Show "already printed" confirmation if order has any print tag (legacy or per-location).
        const alreadyTagged = isPrinted(order?.tags) || getPerLocationPrintTags(order?.tags).length > 0;
        if (alreadyTagged) {
            setShowPrintConfirm(true);
            return;
        }
        const popup = window.open('', '', 'width=920,height=700,scrollbars=yes,resizable=yes');
        if (!popup) { alert('Please allow popups for this site to enable printing.'); return; }
        // eslint-disable-next-line no-restricted-globals
        popup.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Loading…</title></head>\n<body style="font-family:sans-serif;padding:40px;color:#374151"><p style="font-size:15px">Loading…</p></body></html>`);
        printPopupRef.current = popup;
        Promise.all([fetchBins(), fetchPreviews()]).then(([binsResp, previews]) => {
            setPrintBins(binsResp.bins || {});
            setPrintOverstockBins(binsResp.overstock || {});
            setPrintPreviews(previews); setPrintReady(true);
        });
    }, [order?.tags, order?._cache, order?.displayFulfillmentStatus, fetchBins, fetchPreviews, njOfferOD]);

    const handlePrintFromOnHold = useCallback(() => {
        // User confirmed "Print anyway" past the on-hold gate. Now route
        // through the same already-printed check as a normal print.
        setShowOnHoldConfirm(false);
        if (njOfferOD) { setShowPrintOptions(true); return; }
        const alreadyTagged = isPrinted(order?.tags) || getPerLocationPrintTags(order?.tags).length > 0;
        if (alreadyTagged) {
            setShowPrintConfirm(true);
            return;
        }
        const popup = window.open('', '', 'width=920,height=700,scrollbars=yes,resizable=yes');
        if (!popup) { alert('Please allow popups for this site to enable printing.'); return; }
        // eslint-disable-next-line no-restricted-globals
        popup.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Loading…</title></head>\n<body style="font-family:sans-serif;padding:40px;color:#374151"><p style="font-size:15px">Loading…</p></body></html>`);
        printPopupRef.current = popup;
        Promise.all([fetchBins(), fetchPreviews()]).then(([binsResp, previews]) => {
            setPrintBins(binsResp.bins || {});
            setPrintOverstockBins(binsResp.overstock || {});
            setPrintPreviews(previews); setPrintReady(true);
        });
    }, [order?.tags, fetchBins, fetchPreviews, njOfferOD]);

    const handlePrintConfirmed = useCallback(() => {
        setShowPrintConfirm(false);
        const popup = window.open('', '', 'width=920,height=700,scrollbars=yes,resizable=yes');
        if (!popup) { alert('Please allow popups for this site to enable printing.'); return; }
        // eslint-disable-next-line no-restricted-globals
        popup.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Loading…</title></head>\n<body style="font-family:sans-serif;padding:40px;color:#374151"><p style="font-size:15px">Loading…</p></body></html>`);
        printPopupRef.current = popup;
        Promise.all([fetchBins(), fetchPreviews()]).then(([binsResp, previews]) => {
            setPrintBins(binsResp.bins || {});
            setPrintOverstockBins(binsResp.overstock || {});
            setPrintPreviews(previews); setPrintReady(true);
        });
    }, [fetchBins, fetchPreviews]);

    const fmt = (iso) => iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
    const money = (set) => set?.shopMoney ? `$${parseFloat(set.shopMoney.amount).toFixed(2)}` : '—';

    if (loading) return <div className="od-loading">Loading order…</div>;
    if (error)   return <div className="od-error">{error}</div>;
    if (!order)  return null;

    const fos = (order.fulfillmentOrders?.edges || []).map(e => e.node);

    // Each time Shopify fulfills/unfulfills an item it creates a new FO with a higher numeric ID.
    // Only show the newest FO per lineItem — older FOs for the same lineItem are stale duplicates.
    const foNumId = (fo) => parseInt(fo.id.split('/').pop(), 10);

    // For each lineItem, find the highest FO ID that contains it.
    const newestFOIdForLineItem = {};
    fos.forEach(fo => {
        const id = foNumId(fo);
        (fo.lineItems?.edges || []).forEach(e => {
            const liId = e.node.lineItem?.id;
            if (liId && (!newestFOIdForLineItem[liId] || id > newestFOIdForLineItem[liId])) {
                newestFOIdForLineItem[liId] = id;
            }
        });
    });

    // Show a FO only if it is the newest FO for at least one of its lineItems.
    // FOs with no detectable lineItem IDs (e.g. lineItem:null in the edges) are only shown
    // when NO other FO has lineItem data — otherwise they cause all items to render twice.
    const anyFOHasLineItems = fos.some(fo =>
        (fo.lineItems?.edges || []).some(e => e.node.lineItem?.id)
    );
    // A FO that owns its own real shipment (non-CANCELLED Fulfillment via the
    // FO's `fulfillments` connection) is always its own concurrent ship — never
    // a stale duplicate. Without this, split fulfillments hide the older half
    // because the newer split FO claims "newest" status for the shared
    // lineItem.id and the older one fails the newest-id check below.
    const foHasOwnShipment = (fo) =>
        (fo.fulfillments?.edges || [])
            .some(e => (e.node?.status || '').toUpperCase() === 'SUCCESS');
    // OPEN / IN_PROGRESS FOs with remaining quantity > 0 are live work and must
    // ALWAYS show, regardless of newest-id. After a fulfillment SPLIT (e.g.
    // partial shipment), the OLDER FO retains the unfulfilled portion (split-
    // remaining items) while the NEWER split-off FO ships and CLOSEs with a
    // higher numeric ID. Without this carve-out the older OPEN FO got filtered
    // out as "stale" and the operator couldn't see the pending units —
    // SP-262461 canonical case (2026-06-18): 2 of 3 ADID-5157967-L socks stuck
    // in the older OPEN FO, invisible on the Order Detail page.
    const foIsLiveOpen = (fo) => {
        if (!['OPEN', 'IN_PROGRESS'].includes(fo.status)) return false;
        return (fo.lineItems?.edges || []).some(e => (e.node?.remainingQuantity || 0) > 0);
    };
    const visibleFOs = fos.filter(fo => {
        const id = foNumId(fo);
        const foLIs = (fo.lineItems?.edges || []).map(e => e.node.lineItem?.id).filter(Boolean);
        if (foLIs.length === 0) return !anyFOHasLineItems;
        if (foIsLiveOpen(fo)) return true;
        if (foHasOwnShipment(fo)) return true;
        return foLIs.some(liId => newestFOIdForLineItem[liId] === id);
    });

    // Open FOs first, then closed
    const sortedFOs = [
        ...visibleFOs.filter(fo => fo.status === 'OPEN' || fo.status === 'IN_PROGRESS'),
        ...visibleFOs.filter(fo => fo.status !== 'OPEN' && fo.status !== 'IN_PROGRESS'),
    ];

    // Closed-with-cancelled-label FOs are "ghost" shipping label voids — hide
    // by default, show on demand. Mirrors the isCancelledLabel check in the card.
    const isCancelledLabelFO = (fo) => {
        if (fo.status === 'OPEN' || fo.status === 'IN_PROGRESS') return false;
        if (fo.requestStatus !== 'UNSUBMITTED') return false;
        const foLiIds = (fo.lineItems?.edges || []).map(e => e.node.lineItem?.id).filter(Boolean);
        const matches = (order.fulfillments || []).filter(f =>
            (f.fulfillmentLineItems?.edges || []).some(e => foLiIds.includes(e.node.lineItem.id))
        );
        const f = matches.find(x => x.status !== 'CANCELLED') || matches[0] || null;
        return f?.status === 'CANCELLED';
    };
    // For cancelled orders, ALL fulfillments are auto-cancelled in Shopify,
    // which would make `isCancelledLabelFO` filter out every FO and leave the
    // page empty. Keep all FOs visible so the operator can still see the
    // product list — the FO card itself renders cancelled-order rows faded
    // out via `REMOVED_ROW_STYLE`.
    //
    const fulfillments = order.fulfillments || [];
    // Build map: lineItem.id → fulfillment (for matching closed FOs to their fulfillment)
    const liToFulfillment = {};
    fulfillments.forEach(f => {
        (f.fulfillmentLineItems?.edges || []).forEach(e => {
            liToFulfillment[e.node.lineItem.id] = f;
        });
    });
    const getFulfillment = (fo) => {
        // Prefer the FO's own fulfillments connection — it disambiguates split
        // fulfillments where two FOs share a lineItem.id but each got its own
        // Fulfillment record. Falls back to lineItem.id matching for older
        // queries that don't return fo.fulfillments.
        const ownIds = (fo.fulfillments?.edges || []).map(e => e.node?.id).filter(Boolean);
        if (ownIds.length) {
            const ownMatches = fulfillments.filter(f => ownIds.includes(f.id));
            const own = ownMatches.find(f => f.status !== 'CANCELLED') || ownMatches[0];
            if (own) return own;
        }
        const foLiIds = (fo.lineItems?.edges || []).map(e => e.node.lineItem.id);
        const matches = fulfillments.filter(f =>
            (f.fulfillmentLineItems?.edges || []).some(e => foLiIds.includes(e.node.lineItem.id))
        );
        // Prefer a non-cancelled fulfillment; fall back to cancelled if nothing else
        return matches.find(f => f.status !== 'CANCELLED') || matches[0] || null;
    };

    // For voided / superseded shipping FOs (label was bought, then voided —
    // common when an operator re-creates a label after editing the address):
    // Shopify keeps the old FO around as CLOSED but with no fulfillment row,
    // so `isCancelledLabelFO` (which checks fulfillment.status === CANCELLED)
    // doesn't catch them. Filter them out using the storedLabels list as the
    // source of truth — the backend already validates labels against Shopify
    // and only returns currently-valid (non-voided) ones, so any CLOSED
    // shipping FO whose ID isn't in that set is a stale record we should hide.
    const validLabelFoIds = new Set((storedLabels || []).map(l => l.fulfillment_order_id));
    const isStaleShippingFO = (fo) => {
        if (fo.status !== 'CLOSED') return false;
        // Pickup FOs don't have shipping labels — leave them alone (the
        // pickup-state logic elsewhere handles their display).
        if (fo.deliveryMethod?.methodType === 'PICK_UP') return false;
        // No matching valid label AND no fulfillment record → stale.
        if (validLabelFoIds.has(fo.id)) return false;
        const f = getFulfillment(fo);
        if (f && f.status !== 'CANCELLED') return false;
        return true;
    };
    const displayedFOs = !!order.cancelledAt
        ? sortedFOs
        : sortedFOs.filter(fo => !isCancelledLabelFO(fo) && !isStaleShippingFO(fo));

    const orderLineItems = (order.lineItems?.edges || []).map(e => e.node);

    // Line-item IDs that have at least one fulfillment row (i.e. physically
    // shipped from somewhere). Used by FulfillmentOrderCard to attribute
    // bundle-child picked status to the SHIPPED parent first when two
    // parents on the same FO share the same child SKU.
    const shippedLineItemIdSet = (() => {
        const s = new Set();
        for (const f of (order.fulfillments || [])) {
            if ((f.status || '').toUpperCase() === 'CANCELLED') continue;
            for (const e of (f.fulfillmentLineItems?.edges || [])) {
                const id = e.node?.lineItem?.id;
                if (id) s.add(id);
            }
        }
        return s;
    })();

    // ── Expand each FO into per-Fulfillment cards ───────────────────────────
    // Shopify Admin renders one card per Fulfillment record (a single FO with
    // multiple shipped batches becomes multiple Fulfilled cards), plus a
    // separate Unfulfilled card for any items still owed by an IN_PROGRESS
    // FO. We mirror that model here.
    //
    // Internal transfer FOs (deliveryMethod.methodType === 'NONE') are
    // hidden — they're store-to-store transfers, not customer-facing
    // shipments, and Shopify Admin hides them too.
    //
    // EXCEPTION: a NONE-method FO whose line items do NOT also appear on
    // some other FO is an orphan customer-facing FO (usually a Shopify /
    // Cleverific order-edit that added a line item without re-attaching a
    // delivery method). Those must render regardless of OPEN vs CLOSED —
    // if we hide them the item is either invisible (OPEN) or the
    // fulfillment card disappears the moment the operator ships (CLOSED).
    // SP-277248 canonical (2026-07-31): "Adidas CCYSC Custom Entrada 22
    // Youth Track Jacket" added via edit created a methodType=NONE FO
    // that was being swallowed as a transfer, and after fulfillment the
    // "you shipped this" card also vanished.
    const fulfillmentsList = order.fulfillments || [];
    // 2026-10-22 (SURF-93865): units of each line item already shipped, across every live fulfilment. A line
    // split across fulfilments (22 hats shipped 7 + 15) must not let every card claim the same picks.
    const shippedQtyByLineItem = (() => {
        const m = {};
        for (const f of (fulfillmentsList || [])) {
            if ((f?.status || '').toUpperCase() === 'CANCELLED') continue;
            for (const e of (f?.fulfillmentLineItems?.edges || [])) {
                const id = String(e?.node?.lineItem?.id || '').split('/').pop();
                if (id) m[id] = (m[id] || 0) + (e?.node?.quantity || 0);
            }
        }
        return m;
    })();
    const displayCards = [];
    // Lookup for the "unhide orphan NONE-method FO" check below — we need
    // titles to distinguish a real orphan customer FO (SP-277248: added
    // custom jacket line item) from a virtual fee FO (SP-264695: "Team
    // Portal Flat Rate Shipping" fee bucket). Building a Map once instead
    // of iterating orderLineItems per-FO.
    const orderLiById = new Map(orderLineItems.map(li => [li.id, li]));
    displayedFOs.forEach(fo => {
        const dm = (fo.deliveryMethod?.methodType || '').toUpperCase();
        let hideAsTransfer = dm === 'NONE';
        // 2026-10-23 (SURF-106068, Eric): a fulfilment order holding only virtual lines (the Processing Fee
        // split off and auto-fulfilled on its own) rendered as an empty card, since the table hides virtual
        // lines. Nothing to pick, press or ship there: it gets no card at all.
        const _liIdsOfFo = (fo.lineItems?.edges || []).map(e => e.node?.lineItem?.id).filter(Boolean);
        if (!hideAsTransfer && _liIdsOfFo.length > 0
            && _liIdsOfFo.every(id => { const li = orderLiById.get(id); return li && isVirtual(li.title); })) {
            return;
        }
        if (hideAsTransfer) {
            const myLiIds = new Set(
                (fo.lineItems?.edges || [])
                    .map(e => e.node?.lineItem?.id)
                    .filter(Boolean)
            );
            // Only consider unhiding when the FO carries at least one REAL
            // (non-virtual-titled) line item. Fee-only virtual FOs
            // (methodType=NONE + all LIs matching VIRTUAL_TITLES) stay
            // hidden — they aren't inventory work.
            const realLiIds = new Set(
                [...myLiIds].filter(id => {
                    const li = orderLiById.get(id);
                    return li && !isVirtual(li.title);
                })
            );
            if (realLiIds.size > 0) {
                // A CLOSED FO whose every fulfillment was cancelled no longer
                // "covers" its lines: Shopify re-opens them on a NEW FO, and
                // that new FO must render (SP-373959, 2026-10-06: the Teamatical
                // Number/Name FO vanished after Cancel Fulfillment because the
                // dead FO still listed the same line items).
                const isDeadFO = (other) => {
                    if ((other.status || '').toUpperCase() !== 'CLOSED') return false;
                    const ids = (other.fulfillments?.edges || []).map(e => e.node?.id).filter(Boolean);
                    return !ids.some(id => {
                        const f = fulfillmentsList.find(x => x.id === id);
                        return f && (f.status || '').toUpperCase() !== 'CANCELLED';
                    });
                };
                const coveredElsewhere = fos.some(other =>
                    other.id !== fo.id && !isDeadFO(other) &&
                    (other.lineItems?.edges || [])
                        .some(e => realLiIds.has(e.node?.lineItem?.id))
                );
                if (!coveredElsewhere) hideAsTransfer = false;
            }
        }
        const cardsBefore = displayCards.length;

        // 1) One Fulfilled card per non-cancelled fulfillment owned by this
        //    FO, ordered by createdAt ASC to match Shopify's F1/F2/F3 numbering.
        if (!hideAsTransfer) {
            const ownIds = (fo.fulfillments?.edges || [])
                .map(e => e.node?.id)
                .filter(Boolean);
            const ownFulfs = ownIds
                .map(id => fulfillmentsList.find(f => f.id === id))
                .filter(f => f && (f.status || '').toUpperCase() !== 'CANCELLED')
                .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));

            for (const f of ownFulfs) {
                const fLines = (f.fulfillmentLineItems?.edges || [])
                    .map(e => e.node)
                    .filter(n => n.lineItem?.id);
                const qtyById = {};
                fLines.forEach(n => {
                    qtyById[n.lineItem.id] = (qtyById[n.lineItem.id] || 0) + (n.quantity || 0);
                });
                const cardLineItems = orderLineItems
                    .filter(li => qtyById[li.id] != null)
                    .map(li => ({
                        ...li,
                        quantity: qtyById[li.id],
                        currentQuantity: qtyById[li.id],
                    }));
                displayCards.push({
                    key: `${fo.id}#${f.id}`,
                    fo,
                    fulfillment: f,
                    displayMode: 'fulfilled',
                    displayLineItems: cardLineItems,
                });
            }
        }

        // 2) Pending card: items still owed by an OPEN/IN_PROGRESS FO,
        //    using `remainingQuantity > 0` as Shopify does.
        const foIsOpen = fo.status === 'OPEN' || fo.status === 'IN_PROGRESS';
        if (foIsOpen && !hideAsTransfer) {
            const remEdges = (fo.lineItems?.edges || [])
                .filter(e => (e.node?.remainingQuantity || 0) > 0);
            if (remEdges.length > 0) {
                const remQtyById = {};
                remEdges.forEach(e => {
                    const id = e.node.lineItem?.id;
                    if (id) remQtyById[id] = (remQtyById[id] || 0) + (e.node.remainingQuantity || 0);
                });
                const cardLineItems = orderLineItems
                    .filter(li => remQtyById[li.id] != null)
                    .map(li => ({
                        ...li,
                        quantity: remQtyById[li.id],
                        currentQuantity: remQtyById[li.id],
                    }));
                displayCards.push({
                    key: `${fo.id}#pending`,
                    fo,
                    fulfillment: null,
                    displayMode: 'pending',
                    displayLineItems: cardLineItems,
                });
            }
        }

        // 3) Fallback: FO produced no fulfilled or pending cards (cancelled
        //    label, voided shipment, edge case) — render a legacy single
        //    card so the operator still sees the FO.
        if (displayCards.length === cardsBefore && !hideAsTransfer) {
            displayCards.push({
                key: fo.id,
                fo,
                fulfillment: getFulfillment(fo),
                displayMode: 'legacy',
                displayLineItems: null,
            });
        }
    });

    // Open/unfulfilled cards always render before fulfilled cards (matches
    // Shopify Admin). Within each group, fulfilled cards keep their
    // chronological order (createdAt ASC, so F1, F2, F3, F4 line up).
    const _modeRank = (m) => (m === 'pending' ? 0 : m === 'fulfilled' ? 1 : 2);
    displayCards.sort((a, b) => {
        const r = _modeRank(a.displayMode) - _modeRank(b.displayMode);
        if (r !== 0) return r;
        const ta = new Date(a.fulfillment?.createdAt || 0).getTime();
        const tb = new Date(b.fulfillment?.createdAt || 0).getTime();
        return ta - tb;
    });
    const totalItems = orderLineItems.reduce((s, li) => s + (li.quantity || 0), 0);

    // Resolve which location the print should be SCOPED to. Order:
    //   1. emulating.shopify_location_id when explicitly emulating
    //   2. for non-admin/non-customer_service: the user's single allowed
    //      shopify_location_id on the active store — when they have exactly
    //      one. (Multi-loc users without emulation are forced to pick.)
    // Returns null when the print should NOT be scoped (admin viewing all
    // locations) or when the user has multi-loc + no emulation (block print).
    const isAdminOD = hasRole(user, ['admin']) || hasRole(user, ['customer_service']);
    const odPrintLocationId = (() => {
        if (emulating?.shopify_location_id) return String(emulating.shopify_location_id);
        if (isAdminOD) return null; // admin: null = print all locations (allowed)
        const myLocsForStore = (user?.locations || [])
            .filter(l => (l.store_key || 'soccerpost') === (activeStore || 'soccerpost')
                      && l.shopify_location_id && String(l.shopify_location_id) !== '0');
        if (myLocsForStore.length === 1) return String(myLocsForStore[0].shopify_location_id);
        return null; // multi-loc non-admin without emulation → ambiguous
    })();
    // odPrintLocationInScope: true when print is unambiguously scoped.
    //   - admin without emulation viewing all locations: blocked (existing behavior)
    //   - admin emulating: allowed
    //   - non-admin with single-loc context OR explicit emulation: allowed
    //   - non-admin with multi-loc and no emulation: BLOCKED (must pick first) — fixes bug
    //     where Karen's print included items from a location she doesn't own.
    const odPrintLocationInScope = isAdminOD
        ? !!emulating?.shopify_location_id
        : !!odPrintLocationId;

    // Show "(printed)" badge when order has any print tag (legacy or per-location)
    const isOrderPrinted = isPrinted(order.tags) || getPerLocationPrintTags(order.tags).length > 0;

    // 2026-07-06 (c): when emulated to a specific loc (?loc=<sid>), prefer
    // that loc's row-level status over the order-wide aggregate. Falls back
    // through the aggregate `effective_fulfillment_status` and finally the
    // legacy Shopify-side derivation.
    const _emLocIdDetail = emulating?.shopify_location_id
        ? String(emulating.shopify_location_id) : null;
    const _perLocStatuses = order?._cache?.per_loc_status
        || order?.per_loc_status
        || [];
    const _rowStatusForEmuLoc = _emLocIdDetail
        ? (_perLocStatuses.find(e => String(e.shopify_location_id) === _emLocIdDetail) || {}).status
        : null;
    const effectiveFulfillmentStatus = _rowStatusForEmuLoc
        ?? order?._cache?.effective_fulfillment_status
        ?? (() => {
        const status = order.displayFulfillmentStatus;
        const liMap = Object.fromEntries(orderLineItems.map(li => [li.id, li]));
        // Items that actually shipped — i.e. line items covered by a
        // fulfillment with status SUCCESS. CLOSED FOs whose only attached
        // fulfillment was CANCELLED (voided-label residue) are NOT in here
        // and won't count as "resolved" / "non-virtual closed" below.
        const successLineItemIds = new Set(
            (order.fulfillments || [])
                .filter(f => (f.status || '').toUpperCase() === 'SUCCESS')
                .flatMap(f => ((f.fulfillmentLineItems?.edges || [])
                    .map(e => e.node?.lineItem?.id).filter(Boolean)))
        );
        const foIsVirtualOnly = (fo) => {
            const foLiIds = (fo.lineItems?.edges || []).map(e => e.node.lineItem?.id).filter(Boolean);
            if (foLiIds.length === 0) return true; // empty/ghost FO
            return foLiIds.every(id => {
                const li = liMap[id];
                return li && isVirtual(li.title);
            });
        };
        const isVoidedFO = (fo) => {
            if (fo.status !== 'CLOSED' || fo._pickupState?.transferred_at) return false;
            const ids = (fo.lineItems?.edges || []).map(e => e.node.lineItem?.id).filter(Boolean);
            return !ids.some(id => successLineItemIds.has(id));
        };
        const realFOs = fos.filter(fo => !foIsVirtualOnly(fo) && !isVoidedFO(fo));
        // OpsHub: treat pickup-transferred-to-store as fulfillment progress.
        //   - All real FOs resolved (CLOSED or pickup-transferred) → FULFILLED
        //   - Some resolved → PARTIALLY_FULFILLED
        // Shopify doesn't fulfill on a pickup move, so without these overrides
        // the native status stays UNFULFILLED even after merchandise leaves.
        if (realFOs.length > 0) {
            const foResolved = (fo) => {
                if (fo.status === 'CLOSED') {
                    // Only count a CLOSED FO as "resolved" if at least one of
                    // its items actually shipped (SUCCESS fulfillment). Orphan
                    // CLOSED FOs from voided labels have all-CANCELLED
                    // fulfillments and must not trigger PARTIALLY_FULFILLED.
                    const foLiIds = (fo.lineItems?.edges || [])
                        .map(e => e.node.lineItem?.id).filter(Boolean);
                    return foLiIds.some(id => successLineItemIds.has(id));
                }
                if (fo.deliveryMethod?.methodType !== 'PICK_UP') return false;
                if (fo._pickupState?.transferred_at) return true;
                // Shopify-side signal: FO is parked at its pickup store even
                // without a DB transfer row (historical move).
                const assigned = fo.assignedLocation?.name || '';
                const presented = fo.deliveryMethod?.presentedName || '';
                if (assigned && presented && assigned === presented) return true;
                return false;
            };
            const resolvedCount = realFOs.filter(foResolved).length;
            if (resolvedCount === realFOs.length) return 'FULFILLED';
            if (resolvedCount > 0 && status === 'UNFULFILLED') return 'PARTIALLY_FULFILLED';
        }
        if (status !== 'PARTIALLY_FULFILLED') return status;
        const closedFOs = fos.filter(fo => fo.status === 'CLOSED');
        if (closedFOs.length === 0) return status;
        let hasNonVirtualClosed = false;
        for (const fo of closedFOs) {
            const foLiIds = (fo.lineItems?.edges || []).map(e => e.node.lineItem?.id).filter(Boolean);
            if (foLiIds.length === 0) continue; // empty/ghost FO — skip
            // Voided/label-cancelled FO: status=CLOSED but none of its line
            // items appear in any SUCCESS fulfillment. Skip — items went back
            // to a fresh OPEN FO and nothing actually shipped from this FO.
            const anyShipped = foLiIds.some(id => successLineItemIds.has(id));
            if (!anyShipped) continue;
            for (const liId of foLiIds) {
                const li = liMap[liId];
                if (!li || !isVirtual(li.title)) { hasNonVirtualClosed = true; break; }
            }
            if (hasNonVirtualClosed) break;
        }
        return hasNonVirtualClosed ? status : 'UNFULFILLED';
    })();

    // Picking / pressing progress summary — SINGLE SOURCE OF TRUTH.
    // Read directly from order._cache (populated by /api/orders/:id from
    // operations_<sk>_order_cache). This is the EXACT same row the Orders
    // LIST reads from, so the chip on this page and the row in the list
    // are guaranteed to match. The frontend no longer infers totals from
    // line-item walking + portalState — that path drifted from the cache
    // computation any time portal events weren't fully captured (split
    // shipments, store transfers, manual fulfills, missed scans).
    const _cachePickPressSummary = (() => {
        const c = order?._cache;
        if (!c) return null;
        const totalItems     = c.items_count || 0;
        let itemsPicked      = Math.min(c.items_picked_total || 0, totalItems);
        const totalBundles   = c.total_bundles || 0;
        let bundlesPressed   = Math.min(c.items_pressed_total || 0, totalBundles);
        // Pack denominator: prefer server's authoritative total_pack_units
        // (unit-scale, orphan-folded for retail-transferred FOs). Falls back
        // to client-side compute for legacy orders whose cache row is still
        // NULL. Prior to 2026-07-22 the server value was unreliable so we
        // always computed client-side; that's no longer needed.
        let totalPackUnits = (c.total_pack_units && c.total_pack_units > 0)
            ? c.total_pack_units
            : 0;
        if (!totalPackUnits) {
            try {
                const _grouped = groupLineItems(orderLineItems || [], order?.tags || []);
                for (const g of _grouped) {
                    if (g.children && g.children.length > 0) {
                        totalPackUnits += 1;
                        continue;
                    }
                    const _it = g.item || {};
                    if (isVirtual(_it.title)) continue;
                    if ((_it.quantity || 0) === 0) continue;
                    const _pt = (_it.productType || _it.variant?.product?.productType || '');
                    if (EMBELLISHMENT_TYPES.has(_pt)) continue;
                    totalPackUnits += (_it.quantity || 1);
                }
            } catch (_e) { /* fall through */ }
        }
        if (!totalPackUnits) totalPackUnits = totalItems;
        let itemsPacked      = Math.min(c.items_packed_total || 0, totalPackUnits);
        if (totalItems === 0) return null;
        // 2026-05-19: removed the client-side parent-clamp override. The
        // backend `recompute_portal_pct` now writes the event-log-truthful
        // items_picked_total directly — no need to recompute or override it
        // here. The clamp it used to apply suppressed real picker activity
        // (embellishments scanned ahead of parent garments) and broke the
        // "event log = badges = pct" invariant.
        //
        // 2026-08-06: FULFILLED = done. When the order's effective status is
        // FULFILLED, every unit physically left the building by definition —
        // itemsPicked / itemsPacked / bundlesPressed MUST equal their
        // denominators regardless of what the cache row's aggregated
        // numerator says. This mirrors the _legacyPickPressSummary override
        // and rides the [[opshub_fulfilled_definition]] rule ("FULFILLED
        // means done, everywhere"). Fixes SP-293447-style shapes where a
        // retail-store loc plays double duty (transfer destination + native
        // fulfillment) and the aggregation SQL zeroes its picked
        // contribution but keeps its items_count in the denominator.
        const orderFullyShipped = (c.effective_fulfillment_status
                                    || order?.displayFulfillmentStatus
                                    || '').toUpperCase() === 'FULFILLED';
        if (orderFullyShipped) {
            if (totalItems > 0)      itemsPicked    = totalItems;
            if (totalPackUnits > 0)  itemsPacked    = totalPackUnits;
            if (totalBundles > 0)    bundlesPressed = totalBundles;
        }
        return {
            totalItems, itemsPicked,
            totalBundles, bundlesPressed,
            totalPackUnits, itemsPacked,
        };
    })();
    // Legacy frontend computation kept as a safety net for orders served
    // before the cache was populated (e.g., during the post-deploy backfill
    // window). Not used when _cache is present.
    const _legacyPickPressSummary = (() => {
        const entries = Object.entries(portalState).filter(([k]) => !k.startsWith('_'));
        if (entries.length === 0) return null;
        // Build SKU → qty map from actual line items for accurate totals
        const skuQty = {};
        for (const li of orderLineItems) {
            if (!li.sku || isVirtual(li.title)) continue;
            if ((li.quantity || 0) === 0) continue; // skip lines removed via Shopify Order Editing
            skuQty[li.sku] = (skuQty[li.sku] || 0) + (li.quantity || 1);
        }
        // Total = sum of Shopify line item quantities (authoritative). Picked =
        // sum of qty_picked across ALL portalState entries — base SKU or split
        // (`SKU!name`, `SKU#number`) — counted once. Previously we iterated the
        // union of both sets and added skuQty + split-row qty_total, which
        // double-counted every bundle that had been split by name/number.
        let totalItems = 0, itemsPicked = 0;
        for (const sku of Object.keys(skuQty)) {
            totalItems += skuQty[sku];
            // psForSku() aggregates split-key rows (`SKU!player`, `SKU#num`,
            // `SKU|name`) under the base SKU once. Iterating raw portalState
            // entries flat-summed both base + split-key rows for the same
            // physical unit, so every player-named bundle counted twice
            // (e.g. SP-246265 read 11/16 picked when only 8 were).
            const ps = psForSku(portalState, sku) || portalState[sku];
            const skuPicked = Math.min(ps?.qty_picked || 0, skuQty[sku]);
            itemsPicked += skuPicked;
        }
        // Order is fully shipped → every unit physically left the building →
        // itemsPicked must equal totalItems regardless of portal scan history
        // (split waves, manual fulfills, store transfers can leave qty_picked
        // stale on item_state). Mirrors the recompute_portal_pct override.
        const orderFullyShipped = effectiveFulfillmentStatus === 'FULFILLED';
        if (orderFullyShipped && totalItems > 0) itemsPicked = totalItems;
        // Press summary from backend
        const grouped = groupLineItems(orderLineItems, order?.tags || []);
        const bundles = grouped.filter(g => g.children.length > 0);
        const skuCounts = {};
        const groupInstanceMap = {};
        for (const { item } of grouped) {
            const key = item.sku || `__no_sku__${item.id}`;
            skuCounts[key] = (skuCounts[key] || 0) + 1;
            groupInstanceMap[item.id] = skuCounts[key];
        }
        let totalBundles = 0, bundlesPressed = 0;
        for (const { item } of bundles) {
            const idx = groupInstanceMap[item.id];
            if (!idx) continue;
            totalBundles++;
            const sku = item.sku || `__no_sku__${item.id}`;
            const ps = psForSku(portalState, sku) || portalState[sku];
            // Per-instance press_instances map is authoritative; legacy
            // scalar `qty_pressed >= idx` only used as a fallback for rows
            // that haven't been touched since the schema migration.
            const pi = ps?.press_instances || {};
            const hasMap = Object.keys(pi).length > 0;
            const isPressed = hasMap
                ? !!pi[String(idx)]?.pressed
                : (ps && (ps.qty_pressed || 0) >= idx);
            if (isPressed) bundlesPressed++;
        }
        // Same FULFILLED override for press: only when the order actually has
        // bundles (totalBundles > 0). A no-bundle order stays at 0/0 and the
        // press chip won't render at all (parity with recompute_portal_pct).
        if (orderFullyShipped && totalBundles > 0) bundlesPressed = totalBundles;
        return { totalItems, itemsPicked, totalBundles, bundlesPressed };
    })();

    // Cache wins. Legacy frontend computation only kicks in if the cache
    // wasn't populated for this order (e.g., served mid-backfill).
    const pickPressSummary = _cachePickPressSummary || _legacyPickPressSummary;

    // ── Detect split bundles (items from the same bundle spread across multiple FOs) ──
    const liToFoId = {};
    fos.forEach(fo => {
        (fo.lineItems?.edges || []).forEach(e => {
            const liId = e.node.lineItem?.id;
            if (liId) liToFoId[liId] = fo.id;
        });
    });
    const groupFoSets = {};
    orderLineItems.forEach(li => {
        const grpId = li.lineItemGroup?.id;
        if (!grpId) return;
        const foId = liToFoId[li.id];
        if (!foId) return;
        if (!groupFoSets[grpId]) groupFoSets[grpId] = new Set();
        groupFoSets[grpId].add(foId);
    });
    // Team Portal optional add-ons (`_soccer_parent_line_item_key` → the
    // garment's `_soccer_key`, e.g. `_soccer_parent_bundle_id` 14788) belong to
    // their garment's bundle even without a Shopify lineItemGroup. Mirrors
    // app.py::_compute_jumbled_bundles (SP-360758: white name lettering routed
    // to Tinton Falls while the jersey stayed at Teams Maryland).
    const attrOf = (li, key) => (li.customAttributes || []).find(a => a.key === key)?.value;
    const parentByKey = {};
    orderLineItems.forEach(li => { const sk = attrOf(li, '_soccer_key'); if (sk) parentByKey[sk] = li; });
    const addonGroupKey = {};   // add-on / synthetic-parent lineItem id → group key
    orderLineItems.forEach(li => {
        if (li.lineItemGroup?.id) return;
        const pk = attrOf(li, '_soccer_parent_line_item_key');
        const parent = pk ? parentByKey[pk] : null;
        if (!parent || parent.id === li.id) return;
        const foChild = liToFoId[li.id], foParent = liToFoId[parent.id];
        if (!foChild || !foParent) return;
        const native = parent.lineItemGroup?.id;
        const grpId = native || `tpkey:${parent.id}`;
        if (!groupFoSets[grpId]) groupFoSets[grpId] = new Set();
        groupFoSets[grpId].add(foParent);
        groupFoSets[grpId].add(foChild);
        addonGroupKey[li.id] = grpId;
        if (!native) addonGroupKey[parent.id] = grpId;
    });
    // 2026-09-25 (Eric): Advanced Configuration → "Ignore Teamatical products
    // in Jumbled Bundles". A bundle whose product carries
    // Teamatical_v1.teamaticalID is split by design (garment from Teamatical
    // Dropship, number child embellished locally); the backend flags it and
    // exposes the switch on the order payload.
    if (order?.jumbled_ignore_teamatical) {
        const tmGroups = new Set();
        orderLineItems.forEach(li => {
        const v = li.product?.teamaticalId?.value ?? li.variant?.product?.teamaticalId?.value;
        if (!v || !String(v).trim()) return;
        const g = li.lineItemGroup?.id || addonGroupKey[li.id];
        if (g) tmGroups.add(g);
        });
        tmGroups.forEach(g => { delete groupFoSets[g]; });
    }
    const splitBundleMap = {};
    let jumbleColorIdx = 0;
    Object.entries(groupFoSets).forEach(([grpId, foSet]) => {
        if (foSet.size > 1) {
            splitBundleMap[grpId] = jumbleColorIdx % 6;
            jumbleColorIdx++;
        }
    });
    // Let rows without a lineItemGroup (add-ons, synthetic parents) find their color by line-item id.
    Object.entries(addonGroupKey).forEach(([liId, grpId]) => { if (splitBundleMap[grpId] != null) splitBundleMap[liId] = splitBundleMap[grpId]; });
    const hasSplitBundles = jumbleColorIdx > 0;

    const isCancelled = !!order.cancelledAt;

    // Tools-menu pickup override (custom.store_pickup_location) replaces any
    // FO-derived pickup destination + forces the order to render as pickup
    // everywhere on the detail page.
    const overrideLoc = (order.store_pickup_override || '').trim() || null;
    const pickupLocations = overrideLoc ? [overrideLoc] : [...new Set(
        sortedFOs
            .filter(fo => isPickupMethod(fo.deliveryMethod?.methodType))
            .map(fo => fo.deliveryMethod?.presentedName || fo.assignedLocation?.name)
            .filter(Boolean)
    )];
    const isPickupOrder = !!overrideLoc || (!order.shippingAddress && pickupLocations.length > 0);
    // 2026-09-02: Shopify-style "Review address issues" dialog (validator + fix).
    const openAddrReview = async () => {
        setAddrReviewOpen(true); setAddrReview(null); setAddrErr(''); setAddrMode('review');
        try {
            const r = await storeFetch(`/api/orders/${orderId}/address-review`);
            const d = await r.json();
            if (!r.ok) { setAddrErr(d.detail || 'Could not load address review.'); return; }
            setAddrReview(d); setAddrChoice(d.suggestion ? 'suggestion' : 'current'); setAddrForm({ ...(d.current || {}) });
        } catch (e) { setAddrErr('Could not load address review.'); }
    };
    const applyAddress = async (address, source) => {
        setAddrBusy(true); setAddrErr('');
        try {
            const r = await storeFetch(`/api/orders/${orderId}/shipping-address`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ address, save_to_customer: addrSaveCust, source }),
            });
            const d = await r.json();
            if (!r.ok) { setAddrErr(d.detail || 'Update failed.'); return; }
            setAddrReviewOpen(false);
            window.location.reload();
        } catch (e) { setAddrErr('Update failed.'); }
        finally { setAddrBusy(false); }
    };
    const addrLine = (a) => a ? [a.address1, a.address2].filter(Boolean).join(', ') : '';

    // Earliest fulfill-by date — prefer backend-computed value (handles pickup-only orders
    // via _soccer_club fallback); fall back to the earliest FO.fulfillBy.
    const fulfillBy = order._fulfill_by
        || sortedFOs.filter(fo => fo.fulfillBy).map(fo => fo.fulfillBy).sort()[0]
        || null;

    if (showTimeline) {
        return (
            <OrderTimeline
                orderId={orderId}
                order={order}
                fulfillBy={fulfillBy}
                initialViewStart={initialViewStart}
                initialViewEnd={initialViewEnd}
                fetchFn={storeFetch}
                onClose={() => {
                    window.history.replaceState(null, '', window.location.pathname);
                    setShowTimeline(false);
                }}
            />
        );
    }

    const fontFaceCss = buildFontFaceCss(numberPreviews);

    return (
        <StoreKeyContext.Provider value={activeStore || storeSlug || 'soccerpost'}>
        <NumberPreviewsContext.Provider value={numberPreviews}>
        <SkuNotificationsContext.Provider value={skuNotifications}>
        <div>
            {/* Polaris-styled toast for the print-restriction message. Shows
                when user clicks Print Order at All Locations. Auto-dismisses. */}
            {odPrintToast && (
                <div
                    key={odPrintToast.key}
                    className="opshub-toast"
                    role="alert"
                    aria-live="assertive"
                >
                    <Banner tone={odPrintToast.tone || 'warning'} onDismiss={() => setOdPrintToast(null)}>
                        <Text as="p">{odPrintToast.msg}</Text>
                    </Banner>
                </div>
            )}
            {/* @font-face rules for any Team-Portal number-preview fonts the
                order references — lets embellishment thumbs render the
                player number in the product's own typeface. */}
            {fontFaceCss && <style>{fontFaceCss}</style>}
            {/* Hidden print source — rendered off-screen so we can grab innerHTML */}
            {printReady && (
                <div ref={printSourceRef} className="od-print-clone" aria-hidden="true">
                    {printMergedLocs ? (
                        <MergedNjPrint fo={{ order: {}, location_name: order?._location_name || '' }} fullOrder={order} locs={printMergedLocs} printAll={printFull} numberPreviews={printPreviews} storeKey={resolvedStore || activeStore || 'soccerpost'} storeDisplayName={STORE_DISPLAY_NAMES?.[resolvedStore || activeStore] || 'Soccer Post'} printoutFooterHtml={printoutFooterHtml} skuNotifications={skuNotifications} />
                    ) : (
                        <OrderPrintCard fo={{ order: {}, location_name: order?._location_name || '' }} fullOrder={order} printAll={printFull} bins={printBins} overstockBins={printOverstockBins} numberPreviews={printPreviews} locationId={odPrintLocationId} storeKey={resolvedStore || activeStore || 'soccerpost'} storeDisplayName={STORE_DISPLAY_NAMES?.[resolvedStore || activeStore] || 'Soccer Post'} printoutFooterHtml={printoutFooterHtml} skuNotifications={skuNotifications} />
                    )}
                </div>
            )}

            {/* On-hold HARD BLOCK — held orders can't be printed. User must
                release the hold in Shopify first. No "Print anyway" escape. */}
            <Modal
                open={showOnHoldConfirm}
                onClose={() => setShowOnHoldConfirm(false)}
                title="Order is on hold"
                primaryAction={{ content: 'OK', onAction: () => setShowOnHoldConfirm(false) }}
            >
                <Modal.Section>
                    <BlockStack gap="200">
                        <Text variant="bodyMd" tone="critical">
                            This order is <strong>on hold in Shopify</strong> and cannot be printed. Release the hold in Shopify, then come back to print.
                        </Text>
                    </BlockStack>
                </Modal.Section>
            </Modal>

            {/* Already-printed confirmation modal */}
            <Modal
                open={showPrintConfirm}
                onClose={() => setShowPrintConfirm(false)}
                title="Order already printed"
                primaryAction={{ content: 'Print anyway', onAction: handlePrintConfirmed }}
                secondaryActions={[{ content: 'Cancel', onAction: () => setShowPrintConfirm(false) }]}
            >
                <Modal.Section>
                    <BlockStack gap="200">
                        <Text variant="bodyMd" tone="caution">
                            This order has already been printed. Are you sure you want to print it again?
                        </Text>
                        <Text variant="bodyMd" fontWeight="semibold">{order?.replacement_alias || order?.name}</Text>
                        <Checkbox
                            label="Reprint full order, not just unfulfilled items"
                            helpText="Every line at its ordered quantity — logos and all — even those already fulfilled."
                            checked={printFull}
                            onChange={setPrintFull}
                        />
                    </BlockStack>
                </Modal.Section>
            </Modal>

            {/* 2026-09-26 (Eric): Print order — every print of an order with open items at both NJ
                warehouses goes through here (first print or reprint). */}
            <Modal
                open={showPrintOptions}
                onClose={() => setShowPrintOptions(false)}
                title="Print order"
                primaryAction={{ content: 'Print', onAction: () => startPrint(mergeNj) }}
                secondaryActions={[{ content: 'Cancel', onAction: () => setShowPrintOptions(false) }]}
            >
                <Modal.Section>
                    <BlockStack gap="300">
                        {(isPrinted(order?.tags) || getPerLocationPrintTags(order?.tags).length > 0) && (
                            <Text variant="bodyMd" tone="caution">This order has already been printed. Printing it again.</Text>
                        )}
                        <Text variant="bodyMd" fontWeight="semibold">{order?.replacement_alias || order?.name}</Text>
                        <Checkbox
                            label="Merge NJ Warehouses"
                            helpText="One ticket: the Tinton Falls Warehouse items first, a dividing line, then the Eatontown Warehouse items. The order is tagged as printed at both warehouses."
                            checked={mergeNj}
                            onChange={setMergeNj}
                        />
                        {(isPrinted(order?.tags) || getPerLocationPrintTags(order?.tags).length > 0) && (
                            <Checkbox
                                label="Reprint full order, not just unfulfilled items"
                                helpText="Every line at its ordered quantity — logos and all — even those already fulfilled."
                                checked={printFull}
                                onChange={setPrintFull}
                            />
                        )}
                    </BlockStack>
                </Modal.Section>
            </Modal>

            {/* Change Pickup Location modal */}
            <Modal
                open={showPickupLocModal}
                onClose={() => setShowPickupLocModal(false)}
                title="Change Pickup Location"
                primaryAction={{
                    content: pickupLocSaving ? 'Saving…' : 'Save',
                    onAction: handlePickupLocSave,
                    disabled: !pickupLocSelected || pickupLocSaving || pickupLocLoading,
                    loading: pickupLocSaving,
                }}
                secondaryActions={[
                    ...(order.store_pickup_override && pickupLocSelected === order.store_pickup_override ? [{
                        content: 'Clear override / Use original',
                        onAction: handlePickupLocClear,
                        disabled: pickupLocSaving,
                    }] : []),
                    { content: 'Cancel', onAction: () => setShowPickupLocModal(false) },
                ]}
            >
                <Modal.Section>
                    <BlockStack gap="400">
                        {pickupLocError && (
                            <Banner tone="critical" onDismiss={() => setPickupLocError(null)}>
                                <Text as="p" variant="bodyMd">{pickupLocError}</Text>
                            </Banner>
                        )}
                        {pickupLocLoading ? (
                            <Text as="p" variant="bodyMd" tone="subdued">Loading locations…</Text>
                        ) : (
                            pickupLocList.length === 0 ? (
                                <Banner tone="warning">
                                    <Text as="p" variant="bodyMd">None of your locations is set up as a pickup location yet. Ask Soccer Post to add it.</Text>
                                </Banner>
                            ) : <Select
                                label="Pickup location"
                                placeholder="Choose a location…"
                                options={pickupLocList.map(loc => ({ label: loc, value: loc }))}
                                value={pickupLocSelected}
                                onChange={setPickupLocSelected}
                            />
                        )}
                    </BlockStack>
                </Modal.Section>
            </Modal>

            {/* Back — anchor (not a button) so cmd/ctrl+click and middle-click
                open the order list in a new tab the way the browser expects. The
                href encodes the page/filters from the orders list the user came
                from, so the new tab opens to the same page (e.g. ?page=3). */}
            <a
                href={backToHref}
                onClick={(e) => {
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                    e.preventDefault();
                    navigate(backToHref);
                }}
                className="od-back-btn"
            >
                {ordersBackToLabel(activeStore || storeSlug, _emuForBack, _storeNames)}
            </a>

            {/* Cancelled banner */}
            {isCancelled && (
                <div className="od-cancelled-banner">
                    <span className="od-cancelled-banner__icon">🚫</span>
                    <div>
                        <strong className="text-secondary-sm">This order has been cancelled</strong>
                        {order.cancelReason && (
                            <span className="od-cancelled-reason">
                                · Reason: {CANCEL_REASON_LABELS[order.cancelReason] || order.cancelReason.replace(/_/g, ' ').toLowerCase()}
                            </span>
                        )}
                        <span className="od-cancelled-at">{fmt(order.cancelledAt)}</span>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="od-header">
                <div>
                    <div className="od-title-row">
                        {order.replacement_alias ? (
                            /* TP replacement order: this order's OWN raw
                               Shopify name crossed out (clickable → the
                               original parent order), internal alias +
                               "(Order Edited)" in red. */
                            <h1 className="od-title">
                                {/* When TP already renamed the order to the
                                    alias itself (raw name === alias), the
                                    crossed-out copy is redundant noise —
                                    show just "SP-xxxxx:A (Order Edited)". */}
                                {order.name !== order.replacement_alias && (
                                    <Link
                                        to={`/store/${resolvedStore || activeStore || 'soccerpost'}/orders/${order.replacement_parent_order_id}`}
                                        style={{ textDecoration: 'line-through', opacity: 0.55, color: 'inherit', marginRight: 10 }}
                                        title={`Open the original (cancelled) order ${order.replacement_parent_name}`}
                                    >
                                        {order.name}
                                    </Link>
                                )}
                                <span style={{ color: '#c1281c' }}>{order.replacement_alias}</span>
                                <span style={{ color: '#c1281c', fontSize: '0.55em', fontWeight: 600, marginLeft: 10 }}>(Order Edited)</span>
                            </h1>
                        ) : (
                            <h1 className="od-title">{order.name}</h1>
                        )}
                        {viewers.length > 0 && (
                            <div className="od-presence">
                                {viewers.map(v => (
                                    <div
                                        key={v.user_id}
                                        className={`od-presence-circle${v.is_me ? ' od-presence-circle--me' : ''}`}
                                        style={{ background: presenceColor(v.user_id) }}
                                        title={v.display_name}
                                    >
                                        {v.initials}
                                        {/* 2026-09-12: profile avatar over the centred initials, plus a
                                            small initials badge at the bottom-right (Eric's ask). A failed
                                            image load hides both so the centred initials show through. */}
                                        {v.avatar_url && (
                                            <>
                                                <img
                                                    className="od-presence-avatar"
                                                    src={v.avatar_url}
                                                    alt=""
                                                    draggable={false}
                                                    onError={e => { e.currentTarget.parentElement.classList.add('od-presence-circle--noimg'); }}
                                                />
                                                <span className="od-presence-badge" aria-hidden>{v.initials}</span>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="od-meta">
                        <span className="od-meta-date">{fmt(order.processedAt || order.createdAt)}</span>
                        {fulfillBy && (
                            <span className="od-meta-fulfill-by" title="Fulfill by">
                                · Fulfill by {fmt(fulfillBy)}
                            </span>
                        )}
                        {isCancelled ? (
                            <span className="od-badge-cancelled">CANCELLED</span>
                        ) : (
                            <span className={`badge badge--${(effectiveFulfillmentStatus || 'unfulfilled').toLowerCase().replace(/ /g, '-').replace(/_/g, '-')}`}>
                                {effectiveFulfillmentStatus?.replace(/_/g, ' ')}
                            </span>
                        )}
                        <span className="od-badge-financial">
                            {order.displayFinancialStatus?.replace(/_/g, ' ')}
                        </span>
                        {pickPressSummary && pickPressSummary.totalItems > 0 && (
                            <span className={`od-progress-badge od-progress-badge--pick${
                                pickPressSummary.itemsPicked >= pickPressSummary.totalItems
                                    ? '-full'
                                    : pickPressSummary.itemsPicked > 0
                                        ? '-partial'
                                        : '-unpicked'
                            }`}>
                                {pickPressSummary.itemsPicked >= pickPressSummary.totalItems
                                    ? 'FULLY PICKED'
                                    : `${pickPressSummary.itemsPicked} OF ${pickPressSummary.totalItems} ITEMS PICKED`}
                            </span>
                        )}
                        {pickPressSummary && pickPressSummary.bundlesPressed > 0 && (
                            <span className={`od-progress-badge od-progress-badge--press${pickPressSummary.bundlesPressed >= pickPressSummary.totalBundles ? '-full' : '-partial'}`}>
                                {pickPressSummary.bundlesPressed >= pickPressSummary.totalBundles
                                    ? 'FULLY PRESSED'
                                    : `${pickPressSummary.bundlesPressed} OF ${pickPressSummary.totalBundles} BUNDLES PRESSED`}
                            </span>
                        )}
                        {pickPressSummary && pickPressSummary.itemsPacked > 0 && (
                            <span className={`od-progress-badge od-progress-badge--pack${pickPressSummary.itemsPacked >= pickPressSummary.totalPackUnits ? '-full' : '-partial'}`}>
                                {pickPressSummary.itemsPacked >= pickPressSummary.totalPackUnits
                                    ? 'FULLY PACKED'
                                    : `${pickPressSummary.itemsPacked} OF ${pickPressSummary.totalPackUnits} ITEMS PACKED`}
                            </span>
                        )}
                    </div>
                    {(order.tags || []).length > 0 && (
                        <div className="od-tags-wrap">
                            {order.tags.map(t => <Tag key={t} label={t} />)}
                        </div>
                    )}
                </div>
            </div>

            {/* Jumbled bundle alert banner */}
            {hasSplitBundles && (
                <div className="od-jumble-alert">
                    <span className="od-jumble-alert__icon">⚠️</span>
                    <div>
                        <span className="od-jumble-alert__title">JUMBLED BUNDLE ALERT</span>
                        <span className="od-jumble-alert__detail">
                            One or more bundles are split across shipments.  Color-coded badges mark the affected items.
                        </span>
                    </div>
                </div>
            )}

            {/* Missing required embellishments (2026-10-02) — approved by Eric as a warning on this page. */}
            <MissingEmbellishmentsBanner missing={order.missing_required} />

            {/* Pickup location override banner */}
            {order.store_pickup_override && (
                <div style={{ marginBottom: 12 }}>
                    <Banner
                        tone="warning"
                        action={hasRole(user, ['admin', 'site_manager', 'user']) ? { content: 'Change', onAction: openPickupLocModal } : undefined}
                        secondaryAction={hasRole(user, ['admin', 'site_manager', 'user']) ? {
                            content: 'Clear',
                            onAction: () => {
                                if (!window.confirm('Clear the pickup location override? The order will revert to its original pickup location.')) return;
                                handlePickupLocClear();
                            },
                        } : undefined}
                    >
                        <Text as="p" variant="bodyMd">
                            Pickup location overridden — this order will be picked up at <Text as="span" fontWeight="bold">{order.store_pickup_override}</Text>.
                        </Text>
                    </Banner>
                </div>
            )}

            {/* Active broadcast notifications — stacked, no dismiss. Removal
                via the Notifications button on this page (order-scoped) or
                Settings → Notifications. Pushes content down. */}
            <OrderNotificationsBanner notifications={order?.active_notifications} />

            {/* Two-column layout */}
            <div className="od-layout">

                {/* ── LEFT: FO cards ─────────────────────────────────────── */}
                {/* od-col classes: only the Inbox split view (.app-shell--embed) restacks these columns */}
                <div className="od-col od-col--main">
                    {displayCards.map(card => {
                        // For Fulfilled cards from a still-IN_PROGRESS FO, we
                        // only attach a stored label if it ALSO matches this
                        // specific fulfillment (label.fulfillment_id). Otherwise
                        // the pending card claims it. NOTE: the current
                        // shipping_labels schema has no fulfillment_id column,
                        // so we only enforce that gate when the field is
                        // actually populated — otherwise fall back to matching
                        // by fulfillment_order_id alone (the common case).
                        const foLabel = storedLabels.find(l => {
                            if (l.fulfillment_order_id !== card.fo.id) return false;
                            // Pending cards (the unfulfilled remainder of an
                            // IN_PROGRESS FO) never have a label of their own —
                            // the existing label belongs to the already-shipped
                            // batch on the matching 'fulfilled' card. Showing
                            // "View / Reprint Shipping Label" here is misleading
                            // because the customer never bought a label for the
                            // pending units yet.
                            if (card.displayMode === 'pending') return false;
                            if (card.displayMode === 'fulfilled' && l.fulfillment_id) {
                                return l.fulfillment_id === card.fulfillment?.id;
                            }
                            return true;
                        }) || null;
                        return (
                            <FulfillmentOrderCard
                                key={card.key}
                                fo={card.fo}
                                fulfillment={card.displayMode === 'legacy'
                                    ? card.fulfillment
                                    : card.fulfillment}
                                displayMode={card.displayMode}
                                displayLineItems={card.displayLineItems}
                                shippedQtyByLineItem={shippedQtyByLineItem}
                                orderId={orderId}
                                orderName={order?.name}
                                orderLineItems={orderLineItems}
                                bulkShipmentLabels={order?.bulk_shipment_labels}
                                fbmMoved={order?.fbm?.steps?.moved?.status === 'done'}
                                onFulfilled={loadOrder}
                                storedLabel={foLabel}
                                orderCancelled={!!order.cancelledAt}
                                splitBundleMap={splitBundleMap}
                                portalState={portalState}
                                onPortalStateRefresh={() => {
                                    storeFetch(`/api/orders/${orderId}/portal-state`)
                                        .then(r => r.ok ? r.json() : {})
                                        .then(state => setPortalState(state))
                                        .catch(() => {});
                                    // Also reload the order — the header
                                    // pick/press/pack chips read from
                                    // order._cache (items_picked_total,
                                    // items_pressed_total, etc). Without
                                    // this the admin's un-mark updates the
                                    // per-item badges + FO card but the
                                    // top-level chips stay stale until a
                                    // full page reload.
                                    loadOrder();
                                }}
                                storePickupOverride={order?.store_pickup_override || ''}
                                shippedLineItemIds={shippedLineItemIdSet}
                                orderTags={order?.tags || []}
                                siblingFos={(order.fulfillmentOrders?.edges || [])
                                    .map(e => e.node)
                                    .filter(sib => sib && sib.id && sib.id !== card.fo?.id)
                                    .map(sib => ({
                                        id: sib.id,
                                        status: sib.status,
                                        locationName: sib.assignedLocation?.name || '',
                                        locationId: (sib.assignedLocation?.location?.id || '').split('/').pop() || '',
                                    }))}
                                tpOverridesByName={tpOverridesByName}
                                tpReqByLiId={tpReqByLiId}
                                onToast={(t) => setOdPrintToast(t)}
                            />
                        );
                    })}

                    {/* Removed / refunded items — rendered below the FO cards
                      * so the operator can see anything Shopify took off the
                      * order (refund + restock or Order Editing). Returns
                      * null when nothing has been removed. */}
                    <RemovedItemsCard
                        orderLineItems={orderLineItems}
                        splitBundleMap={splitBundleMap}
                        portalState={portalState}
                        orderTags={order?.tags || []}
                    />


                    {/* Totals */}
                    {(() => {
                        const num = (set) => parseFloat(set?.shopMoney?.amount || 0);
                        const fmt = (set) => `$${num(set).toFixed(2)}`;
                        const fmtNum = (n) => `$${(parseFloat(n) || 0).toFixed(2)}`;
                        const discount = num(order.currentTotalDiscountsSet);
                        const tax      = num(order.totalTaxSet);
                        const shipping = num(order.totalShippingPriceSet);
                        const refunded = num(order.totalRefundedSet);
                        const received = num(order.totalReceivedSet);
                        const total    = num(order.totalPriceSet);
                        // Match Shopify admin "Subtotal" — the gross items total
                        // BEFORE discounts (order-level OR line-item-level).
                        // order.subtotalPriceSet reports the after-line-item-discount
                        // total, which is $0 when a discount zeroes each LI (e.g.
                        // PACKAGES DISCOUNT). Sum originalTotalSet across LIs.
                        const subtotalGross = orderLineItems.reduce((s, li) => {
                            const ot = parseFloat(li.originalTotalSet?.shopMoney?.amount);
                            if (isFinite(ot)) return s + ot;
                            const up = parseFloat(li.originalUnitPriceSet?.shopMoney?.amount) || 0;
                            const qty = parseInt(li.quantity || 0, 10) || 0;
                            return s + up * qty;
                        }, 0);
                        const taxLines = order.taxLines || [];
                        const discountApps = (order.discountApplications?.edges || []).map(e => e.node);
                        // "Pickup" line label — when the order's primary delivery method is
                        // pickup, the totalShippingPriceSet is $0 but we still want to show
                        // it labeled as Pickup (with the location) the way Shopify Admin does.
                        const fos = (order.fulfillmentOrders?.edges || []).map(e => e.node);
                        const firstPickupFO = fos.find(f => (f.deliveryMethod?.methodType || '').toUpperCase() === 'PICK_UP');
                        // Tools-menu pickup override (custom.store_pickup_location) wins over
                        // the original delivery method everywhere.
                        const overrideLoc = (order.store_pickup_override || '').trim() || null;
                        const isPickup = !!firstPickupFO || !!overrideLoc;
                        const pickupLocationName = overrideLoc
                            || firstPickupFO?.deliveryMethod?.presentedName
                            || firstPickupFO?.assignedLocation?.name
                            || '';
                        const fs = order.displayFinancialStatus || '';
                        const fsLabel = fs.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
                        const fsTone = ({
                            PAID: { bg: '#e7f5ec', fg: '#0a6a1f', border: '#b6dfc1' },
                            PARTIALLY_REFUNDED: { bg: '#fef3c7', fg: '#92400e', border: '#fde68a' },
                            REFUNDED: { bg: '#fee2e2', fg: '#991b1b', border: '#fecaca' },
                            PARTIALLY_PAID: { bg: '#fef3c7', fg: '#92400e', border: '#fde68a' },
                            PENDING: { bg: '#fef3c7', fg: '#92400e', border: '#fde68a' },
                            VOIDED: { bg: '#f3f4f6', fg: '#374151', border: '#d1d5db' },
                        })[fs] || { bg: '#e7f5ec', fg: '#0a6a1f', border: '#b6dfc1' };
                        return (
                            <Card title={`Order Summary (${totalItems} items)`}>
                                {fs && (
                                    <div style={{ marginBottom: 10 }}>
                                        <span style={{
                                            display: 'inline-block',
                                            padding: '3px 10px',
                                            fontSize: 12,
                                            fontWeight: 600,
                                            color: fsTone.fg,
                                            backgroundColor: fsTone.bg,
                                            border: `1px solid ${fsTone.border}`,
                                            borderRadius: 4,
                                        }}>{fsLabel}</span>
                                    </div>
                                )}
                                <div className="od-totals">
                                    <div className="od-totals-row">
                                        <span className="od-totals-label">Subtotal</span>
                                        <span className="od-totals-mid">{totalItems} {totalItems === 1 ? 'item' : 'items'}</span>
                                        <span>{fmtNum(subtotalGross)}</span>
                                    </div>
                                    {discount > 0 && (
                                        <>
                                        <div className="od-totals-row">
                                            <span className="od-totals-label">Discount</span>
                                            <span className="od-totals-mid" style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
                                                {discountApps.map((d, i) => (
                                                    <Badge key={i}>{d.code || d.title || '—'}</Badge>
                                                ))}
                                            </span>
                                            <span>−{fmt(order.currentTotalDiscountsSet)}</span>
                                        </div>
                                        {/* Per-package discount breakdown (2026-09-01): list each
                                            PACKAGES DISCOUNT by player · team · amount, matching the
                                            Discounts By Club report. */}
                                        {(order.package_discount_details || []).map((pd, i) => (
                                            <div className="od-totals-row" key={`pkgd-${i}`}>
                                                <span className="od-totals-label" style={{ paddingLeft: 16, fontWeight: 400, color: '#6d7175' }}>↳</span>
                                                <span className="od-totals-mid" style={{ fontSize: 12, color: '#6d7175', lineHeight: 1.3 }}>
                                                    {pd.title || 'Package'}
                                                    {(pd.player || pd.team) ? ` — ${[pd.player, pd.team].filter(Boolean).join(' · ')}` : ''}
                                                </span>
                                                <span style={{ fontSize: 12, color: '#6d7175' }}>
                                                    {pd.discount != null ? `−${fmtNum(Number(pd.discount))}` : ''}
                                                    {pd.prorated ? ` (package ${fmtNum(Number(pd.package_discount))})` : ''}
                                                </span>
                                            </div>
                                        ))}
                                        </>
                                    )}
                                    {isPickup ? (
                                        <div className="od-totals-row">
                                            <span className="od-totals-label">Pickup</span>
                                            <span className="od-totals-mid">{pickupLocationName}</span>
                                            <span>{fmt(order.totalShippingPriceSet)}</span>
                                        </div>
                                    ) : (
                                        <div className="od-totals-row">
                                            <span className="od-totals-label">Shipping</span>
                                            <span className="od-totals-mid">{order.shippingLine?.title || ''}</span>
                                            <span>{fmt(order.totalShippingPriceSet)}</span>
                                        </div>
                                    )}
                                    {taxLines.length > 0 ? (
                                        <>
                                            {taxLines.map((tl, i) => (
                                                <div key={i} className="od-totals-row">
                                                    <span className="od-totals-label">{i === 0 ? 'Taxes' : ''}</span>
                                                    <span className="od-totals-mid">{tl.title} {tl.ratePercentage != null ? `${tl.ratePercentage}%` : ''}</span>
                                                    <span>{fmt(tl.priceSet)}</span>
                                                </div>
                                            ))}
                                        </>
                                    ) : (tax > 0 && (
                                        <div className="od-totals-row">
                                            <span className="od-totals-label">Taxes</span>
                                            <span className="od-totals-mid"></span>
                                            <span>{fmt(order.totalTaxSet)}</span>
                                        </div>
                                    ))}
                                    <div className="od-totals-row od-totals-grand">
                                        <span>Total</span>
                                        <span className="od-totals-mid"></span>
                                        <span>{fmt(order.totalPriceSet)}</span>
                                    </div>
                                </div>
                                {(received > 0 || refunded > 0) && (
                                    <div className="od-totals" style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #e5e7eb' }}>
                                        {received > 0 && (
                                            <div className="od-totals-row">
                                                <span className="od-totals-label">Paid</span>
                                                <span className="od-totals-mid"></span>
                                                <span>{fmt(order.totalReceivedSet)}</span>
                                            </div>
                                        )}
                                        {refunded > 0 && (() => {
                                            const refundList = (order.refunds || [])
                                                .map(r => ({ id: r.id, note: (r.note || '').trim(),
                                                             amt: num(r.totalRefundedSet) }))
                                                .filter(r => r.amt > 0);
                                            if (refundList.length === 0) {
                                                return (
                                                    <div className="od-totals-row" style={{ color: '#991b1b' }}>
                                                        <span className="od-totals-label">Refunded</span>
                                                        <span className="od-totals-mid"></span>
                                                        <span>−{fmt(order.totalRefundedSet)}</span>
                                                    </div>
                                                );
                                            }
                                            return refundList.map((r, i) => (
                                                <div key={r.id || i} className="od-totals-row" style={{ color: '#991b1b' }}>
                                                    <span className="od-totals-label">{i === 0 ? 'Refunded' : ''}</span>
                                                    <span className="od-totals-mid" title={r.note}>{r.note}</span>
                                                    <span>−${r.amt.toFixed(2)}</span>
                                                </div>
                                            ));
                                        })()}
                                        {received - refunded !== total && (
                                            <div className="od-totals-row od-totals-grand">
                                                <span>Net</span>
                                                <span className="od-totals-mid"></span>
                                                <span>${(received - refunded).toFixed(2)}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Card>
                        );
                    })()}

                    {(order.shippingLine || order.store_pickup_override) && (
                        <Card title="Shipping Method">
                            <p className="od-shipping-text">
                                {order.store_pickup_override
                                    ? `Pickup in store at ${order.store_pickup_override}`
                                    : order.shippingLine?.title}
                            </p>
                        </Card>
                    )}
                </div>

                {/* ── RIGHT: sidebar ─────────────────────────────────────── */}
                <div className="od-col od-col--side">
                    {/* Action buttons above Players / Members */}
                    <div className="od-actions">
                        {/* View In menu — hidden for store_manager (retail store role
                            doesn't launch into picking/pressing/packing portals). */}
                        {(
                        <div className="od-view-menu" ref={viewMenuRef}>
                            <button
                                className="od-action-btn"
                                onClick={() => setViewMenuOpen(v => !v)}
                                aria-expanded={viewMenuOpen}
                            >
                                <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4zM4 12a2 2 0 100-4 2 2 0 000 4zM16 12a2 2 0 100-4 2 2 0 000 4z"/>
                                </svg>
                                View In
                                <svg className={`od-chevron${viewMenuOpen ? ' od-chevron--open' : ''}`} width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M5 7l5 5 5-5"/>
                                </svg>
                            </button>
                            {viewMenuOpen && (
                                <div className="od-view-dropdown">
                                    {!allRolesIn(user, ['crm', 'customer_service', 'finance', 'store_manager', 'external']) && (
                                    <Link
                                        to={`/store/${resolvedStore || activeStore}/picking-portal/orders/${orderId}`}
                                        state={{ returnTo: `/store/${resolvedStore || activeStore}/orders/${orderId}` }}
                                        className="od-view-item"
                                        onClick={() => setViewMenuOpen(false)}
                                    >
                                        <span className="od-view-item__icon">
                                            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                                <path d="M3 4h2l2.5 8h7l2-5H6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                                                <circle cx="8.5" cy="15" r="1.2" fill="currentColor"/>
                                                <circle cx="14.5" cy="15" r="1.2" fill="currentColor"/>
                                            </svg>
                                        </span>
                                        <span className="od-view-item__body">
                                            <span className="od-view-item__label">Picking Portal</span>
                                            <span className="od-view-item__sub">Pick items for this order</span>
                                        </span>
                                    </Link>
                                    )}
                                    {!allRolesIn(user, ['crm', 'customer_service', 'finance', 'store_manager', 'external']) && (
                                    <Link
                                        to={`/store/${resolvedStore || activeStore}/pressing-portal/orders/${orderId}`}
                                        state={{ returnTo: `/store/${resolvedStore || activeStore}/orders/${orderId}` }}
                                        className="od-view-item"
                                        onClick={() => setViewMenuOpen(false)}
                                    >
                                        <span className="od-view-item__icon">
                                            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                                <path d="M3 6c0-1.1.9-2 2-2h10a2 2 0 012 2v2a4 4 0 010 8H3V6z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M7 12h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                                            </svg>
                                        </span>
                                        <span className="od-view-item__body">
                                            <span className="od-view-item__label">Pressing Portal</span>
                                            <span className="od-view-item__sub">Press this order</span>
                                        </span>
                                    </Link>
                                    )}
                                    {!allRolesIn(user, ['crm', 'customer_service', 'finance', 'store_manager', 'external']) && (
                                    <Link
                                        to={`/store/${resolvedStore || activeStore}/packing-portal/orders/${orderId}`}
                                        state={{ returnTo: `/store/${resolvedStore || activeStore}/orders/${orderId}` }}
                                        className="od-view-item"
                                        onClick={() => setViewMenuOpen(false)}
                                    >
                                        <span className="od-view-item__icon">
                                            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                                <path d="M3 5a2 2 0 012-2h10a2 2 0 012 2v1H3V5z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M3 6h14v10a2 2 0 01-2 2H5a2 2 0 01-2-2V6z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M8 10h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                                            </svg>
                                        </span>
                                        <span className="od-view-item__body">
                                            <span className="od-view-item__label">Packing Portal</span>
                                            <span className="od-view-item__sub">Pack this order</span>
                                        </span>
                                    </Link>
                                    )}
                                    {/* Shopify Admin: shown to all web roles EXCEPT franchise. */}
                                    {hasRole(user, ['admin', 'site_manager', 'user', 'crm', 'customer_service', 'finance', 'store_manager', 'external']) && (
                                        <>
                                            {!allRolesIn(user, ['crm', 'customer_service', 'finance']) && <div className="od-view-divider" />}
                                            <a
                                                href={`https://admin.shopify.com/store/${SHOPIFY_ADMIN_HANDLES[resolvedStore || activeStore] || 'soccerpostshop'}/orders/${orderId}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="od-view-item"
                                                onClick={() => setViewMenuOpen(false)}
                                            >
                                                <span className="od-view-item__icon od-view-item__icon--muted">
                                                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                                        <path d="M11 3h6v6M17 3l-9 9M8 5H4a1 1 0 00-1 1v10a1 1 0 001 1h10a1 1 0 001-1v-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                                                    </svg>
                                                </span>
                                                <span className="od-view-item__body">
                                                    <span className="od-view-item__label">Shopify Admin</span>
                                                    <span className="od-view-item__sub">Open in Shopify</span>
                                                </span>
                                            </a>
                                        </>
                                    )}
                                    {/* Order Edits (Soccer Post Production app) — admin,
                                        franchise, site_manager, user, customer_service
                                        (2026-08-31 user spec). External link, per-store
                                        admin handle. Only on Team Portal-capable stores
                                        AND orders whose cache source includes Team Portal
                                        (team_portal / tp_retail / tp_addon /
                                        tp_retail_addon). */}
                                    {hasRole(user, ['admin', 'franchise', 'site_manager', 'user', 'customer_service'])
                                        && ['soccerpost', 'surf', 'rush', 'citysports'].includes(resolvedStore || activeStore)
                                        && ['team_portal', 'tp_retail', 'tp_addon', 'tp_retail_addon'].includes(order?._cache?.source) && (
                                        <a
                                            href={hasRole(user, ['franchise'])
                                                /* Franchises (Soccer Post only) have no Shopify admin
                                                   access — use the Team Portal manager view instead. */
                                                ? `https://app.soccerpost.com/manager/orders?id=${orderId}`
                                                : `https://admin.shopify.com/store/${SHOPIFY_ADMIN_HANDLES[resolvedStore || activeStore] || 'soccerpostshop'}/apps/soccer-post-production/orders/${orderId}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="od-view-item od-view-item--order-edits"
                                            onClick={() => setViewMenuOpen(false)}
                                        >
                                            <span className="od-view-item__icon od-view-item__icon--muted">
                                                <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                                    <path d="M13.5 3.5l3 3L7 16H4v-3l9.5-9.5z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M12 5l3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                                                </svg>
                                            </span>
                                            <span className="od-view-item__body">
                                                <span className="od-view-item__label">Order Edits</span>
                                                <span className="od-view-item__sub" style={{ whiteSpace: 'nowrap' }}>Edit within Team Portal</span>
                                            </span>
                                        </a>
                                    )}
                                </div>
                            )}
                        </div>
                        )}

                        {/* Tools menu — hidden for CRM/finance (read-only) and
                            store_manager (retail store role — no admin tools). */}
                        {!allRolesIn(user, ['crm', 'finance', 'store_manager', 'external']) && (
                        <div className="od-view-menu" ref={toolsMenuRef}>
                            <button
                                className="od-action-btn"
                                onClick={() => setToolsMenuOpen(v => !v)}
                                aria-expanded={toolsMenuOpen}
                            >
                                <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                                    <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l.3-.3a2 2 0 10-2.8-2.8l-.5.1zM13 8l-9 9-1 3 3-1 9-9-2-2z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                Tools
                                <svg className={`od-chevron${toolsMenuOpen ? ' od-chevron--open' : ''}`} width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M5 7l5 5 5-5"/>
                                </svg>
                            </button>
                            {toolsMenuOpen && (
                                <div className="od-view-dropdown">
                                    {/* franchise added 2026-09-30 (Eric): the list they get is only their own locations. */}
                                    {hasRole(user, ['admin', 'site_manager', 'user', 'customer_service', 'franchise']) && (
                                        <button className="od-view-item" onClick={openPickupLocModal}>
                                            <span className="od-view-item__icon od-view-item__icon--muted">
                                                <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M10 2a6 6 0 016 6c0 4-6 10-6 10S4 12 4 8a6 6 0 016-6z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/><circle cx="10" cy="8" r="2" stroke="currentColor" strokeWidth="1.6"/></svg>
                                            </span>
                                            <span className="od-view-item__body">
                                                <span className="od-view-item__label" style={{ color: '#1e3a8a' }}>Change Pickup Location</span>
                                                <span className="od-view-item__sub">Override the pickup store for this order</span>
                                            </span>
                                        </button>
                                    )}
                                    {/* Operational reset items: hidden for customer_service (CSR role
                                        gets only Change Pickup Location in Tools) AND for store_manager
                                        (retail store role — no warehouse ops reset authority). */}
                                    {!allRolesIn(user, ['customer_service', 'store_manager']) && (<>
                                    <button className="od-view-item od-view-item--danger" onClick={() => handleToolAction('reset_picking')}>
                                        <span className="od-view-item__icon od-view-item__icon--muted">
                                            <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M4 6h12M8 6V4h4v2M7 6l1 10h4l1-10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                        </span>
                                        <span className="od-view-item__body">
                                            <span className="od-view-item__label">Reset Picking History</span>
                                            <span className="od-view-item__sub">Clear all picked quantities</span>
                                        </span>
                                    </button>
                                    <button className="od-view-item od-view-item--danger" onClick={() => handleToolAction('reset_pressing')}>
                                        <span className="od-view-item__icon od-view-item__icon--muted">
                                            <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M4 6h12M8 6V4h4v2M7 6l1 10h4l1-10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                        </span>
                                        <span className="od-view-item__body">
                                            <span className="od-view-item__label">Reset Pressing History</span>
                                            <span className="od-view-item__sub">Clear all pressed quantities</span>
                                        </span>
                                    </button>
                                    <button className="od-view-item od-view-item--danger" onClick={() => handleToolAction('reset_packing')}>
                                        <span className="od-view-item__icon od-view-item__icon--muted">
                                            <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M4 6h12M8 6V4h4v2M7 6l1 10h4l1-10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                        </span>
                                        <span className="od-view-item__body">
                                            <span className="od-view-item__label">Reset Packing History</span>
                                            <span className="od-view-item__sub">Clear all QC pass/fail state</span>
                                        </span>
                                    </button>
                                    </>)}
                                    {hasRole(user, ['admin']) && (
                                        <button className="od-view-item" onClick={handleResync}>
                                            <span className="od-view-item__icon od-view-item__icon--muted">
                                                <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M4 10a6 6 0 0110.5-4M16 10a6 6 0 01-10.5 4M14 3v4h-4M6 17v-4h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                            </span>
                                            <span className="od-view-item__body">
                                                <span className="od-view-item__label">Force Resync from Shopify</span>
                                                <span className="od-view-item__sub">Re-fetch FO/fulfillment state if the view looks stale</span>
                                            </span>
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                        )}

                        <OrderNotificationsControl
                            storeKey={resolvedStore || activeStore}
                            orderId={orderId}
                            orderName={order?.name}
                            activeNotifications={order?.active_notifications}
                            onChange={loadOrder}
                        />

                        <button className="od-action-btn" onClick={() => setShowTimeline(true)}>
                            <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                                <path d="M3 10h14M3 5h14M3 15h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                                <circle cx="16" cy="15" r="2.5" stroke="currentColor" strokeWidth="1.6"/>
                            </svg>
                            Timeline
                        </button>

                        {!allRolesIn(user, ['crm', 'finance', 'customer_service', 'external']) && (
                        <button
                            onClick={() => {
                                if (!odPrintLocationInScope) {
                                    setOdPrintToast({ msg: "You can't print orders when you are viewing \"All Locations\". Choose a single location first.", key: Date.now() });
                                    return;
                                }
                                handlePrint();
                            }}
                            className={`od-action-btn od-action-btn--primary${!odPrintLocationInScope ? ' od-action-btn--disabled' : ''}`}
                            title={!odPrintLocationInScope ? "You can't print orders when you are viewing \"All Locations\". Choose a single location first." : undefined}
                        >
                            <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5 7V3h10v4M5 15H3a1 1 0 01-1-1V9a1 1 0 011-1h14a1 1 0 011 1v5a1 1 0 01-1 1h-2M5 11h10v6H5v-6z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Print Order
                            {isOrderPrinted && <span className="od-print-badge">(printed)</span>}
                        </button>
                        )}
                        {hasRole(user, ['admin', 'site_manager', 'customer_service', 'franchise', 'user']) && (
                        <button
                            onClick={() => setReportIssueOpen(true)}
                            className="od-action-btn od-action-btn--danger"
                            title="Log a customer service or office escalation"
                        >
                            <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10 2l8 14H2L10 2z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
                                <path d="M10 8v4M10 14.5v.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                            </svg>
                            Report Issue
                        </button>
                        )}
                        {hasRole(user, ['admin', 'franchise', 'site_manager', 'user', 'customer_service', 'store_manager']) && (
                        <button
                            onClick={() => setRefundModalOpen(true)}
                            className="od-action-btn"
                            title="Issue a refund for one or more items on this order"
                        >
                            <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9 4L3 10l6 6M3 10h13a3 3 0 010 6h-3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Refund
                        </button>
                        )}
                    </div>

                    {/* Order Notes (2026-10-02, Eric): top of the right column, above Players /
                        Members, so the note is seen first. Shown only when the order has a note. */}
                    {String(order.note || '').trim() && (
                        <Card title="Order Notes">
                            <p className="od-note-text">{order.note}</p>
                        </Card>
                    )}

                    <PlayersPanel orderAttrs={order.customAttributes} tpOverrides={order.tp_number_overrides || []} tpTeamOverrides={order.tp_team_overrides || []} tpRequirementsPlayers={order.tp_requirements?.players || []} />

                    <div className="od-card od-card--customer">
                    <Card title="Customer">
                        {order.customer ? (
                            <div className="od-customer-info">
                                <div className="od-customer-name">{order.customer.firstName} {order.customer.lastName}</div>
                                {order.customer.email && (
                                    <div className="od-customer-detail od-copyable">
                                        <span className="od-copyable__text">{order.customer.email}</span>
                                        <button
                                            type="button"
                                            className="od-copy-btn"
                                            aria-label="Copy email to clipboard"
                                            title="Copy email"
                                            onClick={async () => {
                                                const txt = order.customer.email;
                                                try {
                                                    if (navigator.clipboard?.writeText) {
                                                        await navigator.clipboard.writeText(txt);
                                                    } else {
                                                        const ta = document.createElement('textarea');
                                                        ta.value = txt;
                                                        ta.style.position = 'fixed'; ta.style.opacity = '0';
                                                        document.body.appendChild(ta);
                                                        ta.select();
                                                        document.execCommand('copy');
                                                        document.body.removeChild(ta);
                                                    }
                                                    const stamp = Date.now();
                                                    setEmailCopiedAt(stamp);
                                                    setTimeout(() => {
                                                        setEmailCopiedAt(prev => prev === stamp ? 0 : prev);
                                                    }, 1800);
                                                } catch (_) { /* clipboard blocked — silently noop */ }
                                            }}
                                        >
                                            <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                                <rect x="6" y="6" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="1.6"/>
                                                <path d="M3 13V5a2 2 0 012-2h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                                            </svg>
                                        </button>
                                        {emailCopiedAt > 0 && (
                                            <span key={emailCopiedAt} className="od-copied-badge">Copied</span>
                                        )}
                                    </div>
                                )}
                                {order.customer.phone && <div className="od-customer-detail">{order.customer.phone}</div>}
                            </div>
                        ) : <div className="od-no-info">No customer info</div>}
                    </Card>
                    </div>

                    {(() => {
                        // 2026-07-29: CRM / Expected Fulfillment Location moved
                        // BELOW Shipping Address and wrapped in a titled Card
                        // (same shell as neighboring boxes) so padding, corner
                        // radius, and shadow match. Prior 'od-info-strip'
                        // rendering was a borderless flat block that stuck out.
                        return null;
                    })()}

                    <div className="od-card od-card--shipping">
                    <Card title="Shipping Address">
                        {isPickupOrder ? (
                            <div className="od-pickup-addr">
                                <Badge tone="critical">🏪 Pickup Order</Badge>
                                {pickupLocations.map(loc => (
                                    <span key={loc}>📍 <StoreBadge name={loc} /></span>
                                ))}
                            </div>
                        ) : order.shippingAddress ? (
                            <address className="od-shipping-addr">
                                {order.shippingAddress.validationResultSummary && order.shippingAddress.validationResultSummary !== 'NO_ISSUES' && (
                                    <button type="button" className="od-addr-issues" onClick={openAddrReview}>
                                        ⚠ Review address issues
                                    </button>
                                )}
                                <div className="od-addr-name">{[order.shippingAddress.firstName, order.shippingAddress.lastName].filter(Boolean).join(' ') || order.shippingAddress.company}</div>
                                {order.shippingAddress.company && (order.shippingAddress.firstName || order.shippingAddress.lastName) && <div>{order.shippingAddress.company}</div>}
                                <div>{order.shippingAddress.address1}</div>
                                {order.shippingAddress.address2 && <div>{order.shippingAddress.address2}</div>}
                                <div>{order.shippingAddress.city}, {order.shippingAddress.provinceCode} {order.shippingAddress.zip}</div>
                                <div>{order.shippingAddress.countryCode}</div>
                                {order.shippingAddress.phone && <div className="od-addr-phone">{order.shippingAddress.phone}</div>}
                            </address>
                        ) : <div className="od-no-info">No shipping address</div>}
                    </Card>
                    </div>

                    {addrReviewOpen && (() => {
                        const d = addrReview; const cur = d?.current; const sug = d?.suggestion; const fi = d?.field_issues || {};
                        const flag = (k) => fi[k] ? 'od-addr-hl od-addr-hl--warn' : '';
                        const chg = (k) => (sug && cur && (sug[k] || '') !== (cur[k] || '')) ? 'od-addr-hl od-addr-hl--sug' : '';
                        const Addr = ({ a, cls }) => (
                            <div className="od-addr-block">
                                <div><span className={cls('address1')}>{a.address1}</span>{a.address2 ? <>, <span className={cls('address2')}>{a.address2}</span></> : null}</div>
                                <div><span className={cls('city')}>{a.city}</span> <span className={cls('provinceCode')}>{a.provinceCode}</span> <span className={cls('zip')}>{a.zip}</span></div>
                                <div>{a.countryCode === 'US' ? 'United States' : a.countryCode}</div>
                            </div>
                        );
                        const F = ({ k, label, w }) => (
                            <label className="od-addr-field" style={w ? { flex: w } : undefined}>
                                <span>{label}</span>
                                <input value={addrForm?.[k] || ''} onChange={e => setAddrForm(f => ({ ...f, [k]: e.target.value }))} />
                            </label>
                        );
                        return (
                            <div className="od-addr-modal-bg" onClick={() => !addrBusy && setAddrReviewOpen(false)}>
                                <div className="od-addr-modal" onClick={e => e.stopPropagation()}>
                                    <div className="od-addr-modal__head">
                                        <div className="od-addr-modal__title">Shipping address</div>
                                        <button type="button" className="od-addr-icon" title={addrMode === 'edit' ? 'Back to suggestions' : 'Edit address'}
                                                onClick={() => setAddrMode(m => m === 'edit' ? 'review' : 'edit')}>✎</button>
                                    </div>
                                    {!d && !addrErr && <div className="od-addr-muted">Checking address…</div>}
                                    {addrErr && <div className="od-addr-error">{addrErr}</div>}
                                    {d && addrMode === 'review' && (
                                        <>
                                            <label className={`od-addr-option${addrChoice === 'current' ? ' is-selected' : ''}`}>
                                                <input type="radio" name="addr-choice" checked={addrChoice === 'current'} onChange={() => setAddrChoice('current')} />
                                                <div>
                                                    <div className="od-addr-option__label">Current</div>
                                                    <Addr a={cur} cls={flag} />
                                                    {(d.concerns || []).map((c, i) => (
                                                        <div className="od-addr-concern" key={i}><span className="od-addr-concern__icon">⚠</span>{c.message}</div>
                                                    ))}
                                                    {!(d.concerns || []).length && <div className="od-addr-muted">Shopify reports no issues with this address.</div>}
                                                </div>
                                            </label>
                                            {sug && (
                                                <label className={`od-addr-option${addrChoice === 'suggestion' ? ' is-selected' : ''}`}>
                                                    <input type="radio" name="addr-choice" checked={addrChoice === 'suggestion'} onChange={() => setAddrChoice('suggestion')} />
                                                    <div>
                                                        <div className="od-addr-option__label">Suggestion</div>
                                                        <Addr a={sug} cls={chg} />
                                                    </div>
                                                </label>
                                            )}
                                            {d.customer_default_matches && (
                                                <label className="od-addr-check">
                                                    <input type="checkbox" checked={addrSaveCust} onChange={e => setAddrSaveCust(e.target.checked)} />
                                                    Also update the customer's saved address
                                                </label>
                                            )}
                                            <div className="od-addr-modal__foot">
                                                {d.customer_email && (
                                                    <a className="od-addr-btn" href={`mailto:${d.customer_email}?subject=${encodeURIComponent(`Please confirm your shipping address for order ${d.order_name || ''}`)}`}>Email customer</a>
                                                )}
                                                {addrChoice === 'suggestion' && sug
                                                    ? <button type="button" className="od-addr-btn od-addr-btn--primary" disabled={addrBusy} onClick={() => applyAddress(sug, 'suggestion')}>{addrBusy ? 'Saving…' : 'Use suggestion'}</button>
                                                    : <button type="button" className="od-addr-btn od-addr-btn--primary" disabled={addrBusy} onClick={() => setAddrReviewOpen(false)}>Keep current</button>}
                                            </div>
                                        </>
                                    )}
                                    {d && addrMode === 'edit' && addrForm && (
                                        <>
                                            <div className="od-addr-row"><F k="firstName" label="First name" /><F k="lastName" label="Last name" /></div>
                                            <div className="od-addr-row"><F k="company" label="Company" /><F k="phone" label="Phone" /></div>
                                            <div className="od-addr-row"><F k="address1" label="Address" w={2} /></div>
                                            <div className="od-addr-row"><F k="address2" label="Apartment, suite, etc." w={2} /></div>
                                            <div className="od-addr-row"><F k="city" label="City" w={2} /><F k="provinceCode" label="State" /><F k="zip" label="ZIP" /></div>
                                            <div className="od-addr-row"><F k="countryCode" label="Country code" /></div>
                                            {d.customer_default_matches && (
                                                <label className="od-addr-check">
                                                    <input type="checkbox" checked={addrSaveCust} onChange={e => setAddrSaveCust(e.target.checked)} />
                                                    Also update the customer's saved address
                                                </label>
                                            )}
                                            <div className="od-addr-modal__foot">
                                                <button type="button" className="od-addr-btn" onClick={() => setAddrMode('review')}>Cancel</button>
                                                <button type="button" className="od-addr-btn od-addr-btn--primary" disabled={addrBusy} onClick={() => applyAddress(addrForm, 'manual')}>{addrBusy ? 'Saving…' : 'Save address'}</button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })()}

                    {(() => {
                        const expectedLoc = (order.customAttributes || [])
                            .find(a => a.key === '_soccer_fulfillment_location')?.value || '';
                        // Fulfillment window from the order's own _soccer_club
                        // snapshot ({"fulfillment_property": {"value": "from 15
                        // to 21 business days"}}) — set at TP club-open and
                        // virtually never changes, so the checkout snapshot is
                        // authoritative. No backend/TP-DB lookup needed.
                        let fulfillmentTime = '';
                        try {
                            const clubRaw = (order.customAttributes || [])
                                .find(a => a.key === '_soccer_club')?.value;
                            const snap = clubRaw ? (JSON.parse(clubRaw)?.fulfillment_property?.value || '') : '';
                            const nums = String(snap).match(/\d+/g) || [];
                            if (nums.length >= 2) fulfillmentTime = `${nums[0]}-${nums[1]} Business Days`;
                            else if (nums.length === 1) fulfillmentTime = `${nums[0]} Business Days`;
                        } catch (_) { /* malformed attr → no row */ }
                        if (!order.teamportal_crm && !expectedLoc && !fulfillmentTime) return null;
                        return (
                            <Card title="Order Info">
                                <BlockStack gap="200">
                                    {order.teamportal_crm && (
                                        <div>
                                            <Text as="span" tone="subdued">CRM: </Text>
                                            <Text as="span" fontWeight="semibold">{order.teamportal_crm}</Text>
                                        </div>
                                    )}
                                    {expectedLoc && (
                                        <div>
                                            <Text as="span" tone="subdued">Expected Fulfillment Location: </Text>
                                            <Text as="span" fontWeight="semibold">{expectedLoc}</Text>
                                        </div>
                                    )}
                                    {fulfillmentTime && (
                                        <div>
                                            <Text as="span" tone="subdued">Fulfillment Time: </Text>
                                            <Text as="span" fontWeight="semibold">{fulfillmentTime}</Text>
                                        </div>
                                    )}
                                </BlockStack>
                            </Card>
                        );
                    })()}

                    <EventLog orderId={orderId} cancelReason={order.cancelReason} />
                    {order?.fbm && <FbmCard fbm={order.fbm} />}
                </div>
            </div>

            {/* Prev/Next footer — mirrors the Orders LIST pagination layout
                exactly (info on the left, prev/next buttons on the right).
                Only renders when the operator arrived via the Orders list
                (sessionStorage cursor present) AND the current order is in
                that cursor. Bypass paths (deep links / refresh without prior
                list nav) get nothing rather than misleading always-disabled
                chevrons. */}
            {prevNextCursor.idx >= 0 && prevNextCursor.ids.length > 1 && (
                <div className="od-prevnext">
                    <div className="ots-pagination-info">
                        <strong>Order {prevNextCursor.idx + 1}</strong>
                        <span> of {prevNextCursor.ids.length.toLocaleString()}</span>
                    </div>
                    <div className="ots-pagination-btns">
                        <button
                            type="button"
                            className="ots-pagination-btn"
                            disabled={!prevOrderId}
                            onClick={() => goAdjacent(prevOrderId)}
                            title={prevOrderId ? `Go to order ${prevOrderId}` : 'No previous order in this list'}
                        >
                            ← Previous
                        </button>
                        <button
                            type="button"
                            className="ots-pagination-btn"
                            disabled={!nextOrderId}
                            onClick={() => goAdjacent(nextOrderId)}
                            title={nextOrderId ? `Go to order ${nextOrderId}` : 'No next order in this list'}
                        >
                            Next →
                        </button>
                    </div>
                </div>
            )}
        {refundModalOpen && (
            <RefundModal
                order={order}
                storeKey={resolvedStore || activeStore}
                onClose={() => setRefundModalOpen(false)}
                onRefundIssued={() => { setRefundModalOpen(false); loadOrder(); }}
            />
        )}
        {reportIssueOpen && (
            <ReportIssueModal
                orderName={order?.name}
                onSubmit={async (reason, note) => {
                    const numericId = String(orderId).split('/').pop();
                    const r = await storeFetch(`/api/orders/${numericId}/report-issue`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            store_key: resolvedStore || activeStore,
                            reason,
                            note,
                        }),
                    });
                    if (!r.ok) {
                        const d = await r.json().catch(() => ({}));
                        throw new Error(d.detail || `HTTP ${r.status}`);
                    }
                    setReportIssueOpen(false);
                    loadOrder();
                }}
                onCancel={() => setReportIssueOpen(false)}
            />
        )}
        </div>
        </SkuNotificationsContext.Provider>
        </NumberPreviewsContext.Provider>
        </StoreKeyContext.Provider>
    );
}

// ── Shared with the V2 order page (2026-09-15, Eric): the per-fulfillment
// action menu, the small item badges/links and a few helpers. V2 imports
// these instead of re-implementing them so the two pages stay in step.
export { FulfillmentMenu, ShippingLabelModal, JumbleBadge, SkuNotificationCell, SkuNotificationsContext,
         StoreKeyContext, ProductLink, presenceColor, CANCEL_REASON_LABELS, isPickupMethod , TpReqBadge , EventLog,
         renderShopifyMessage, verbLabel };
